import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  runTransaction,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";

import { auth, db } from "../../firebase";

const text = (value) => String(value ?? "").trim();

const number = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const toDateSafe = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value?.toDate === "function") {
    const date = value.toDate();

    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value === "number") {
    const date = new Date(value);

    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value === "string") {
    const date = new Date(value);

    return Number.isFinite(date.getTime()) ? date : null;
  }

  return null;
};

const pad2 = (value) => String(value).padStart(2, "0");

const dateKey = (date) => {
  const d = toDateSafe(date) || new Date();

  return [d.getFullYear(), pad2(d.getMonth() + 1), pad2(d.getDate())].join("-");
};

const dateFromKey = (value) => {
  const [year, month, day] = text(value).split("-").map(Number);

  return year && month && day ? new Date(year, month - 1, day) : new Date();
};

const dateKeysBetween = (startKey, endKey, maxDays = 366) => {
  const start = dateFromKey(startKey);
  const end = dateFromKey(endKey);
  const first = start.getTime() <= end.getTime() ? start : end;
  const last = start.getTime() <= end.getTime() ? end : start;
  const keys = [];
  const cursor = new Date(first);

  while (cursor.getTime() <= last.getTime() && keys.length < maxDays) {
    keys.push(dateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return keys;
};

const REPORT_PAGE_SIZE = 40;
const TRACKING_HISTORY_API_URL = "https://us-central1-chat3test-59045.cloudfunctions.net/api/tracking/history";

const INCIDENT_PHOTO_LIMIT = 5;

function createReportMediaUuid() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === "function") cryptoApi.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function releaseIncidentPhotoPreviews(photos) {
  if (typeof URL === "undefined" || typeof URL.revokeObjectURL !== "function") return;
  (Array.isArray(photos) ? photos : []).forEach((photo) => {
    if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
  });
}

async function createIncidentPhotoPreviewBlob(file) {
  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("incident_photo_preview_failed"));
      element.src = sourceUrl;
    });
    const width = Number(image.naturalWidth || image.width);
    const height = Number(image.naturalHeight || image.height);
    if (!width || !height) throw new Error("incident_photo_preview_failed");
    const scale = Math.min(1, 640 / width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("incident_photo_preview_failed");
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error("incident_photo_preview_failed")),
        "image/jpeg",
        0.3,
      );
    });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

async function uploadIncidentReportPhoto(file, orgId, uploadedByUid) {
  const assetId = createReportMediaUuid();
  const originalPath = `assets/${assetId}/original`;
  const previewPath = `assets/${assetId}/preview`;
  const contentType = text(file?.type) || "image/jpeg";
  const previewBlob = await createIncidentPhotoPreviewBlob(file);
  const storage = getStorage();
  try {
    await uploadBytes(storageRef(storage, originalPath), file, { contentType });
    await uploadBytes(storageRef(storage, previewPath), previewBlob, { contentType: "image/jpeg" });
    const asset = {
      assetId,
      ownerOrgId: text(orgId),
      source: "report_center",
      category: "incident_report_photo",
      mediaType: "image",
      stage: "report",
      originalName: text(file?.name) || "異常照片",
      contentType,
      sizeBytes: Number(file?.size) || null,
      originalPath,
      previewPath,
      uploadedByUid: text(uploadedByUid),
      uploadedAt: serverTimestamp(),
      processingStatus: "completed",
    };
    await setDoc(doc(db, "mediaAssets", assetId), asset);
    return {
      assetId,
      paths: [originalPath, previewPath],
      reference: {
        assetId,
        name: asset.originalName,
        originalName: asset.originalName,
        kind: "image",
        contentType,
        sizeBytes: asset.sizeBytes,
      },
    };
  } catch (error) {
    await Promise.allSettled([
      deleteObject(storageRef(storage, originalPath)),
      deleteObject(storageRef(storage, previewPath)),
      deleteDoc(doc(db, "mediaAssets", assetId)),
    ]);
    throw error;
  }
}

async function removeIncidentReportPhotoAsset(uploaded) {
  const assetId = text(uploaded?.assetId);
  const paths = Array.isArray(uploaded?.paths) && uploaded.paths.length
    ? uploaded.paths
    : assetId ? [`assets/${assetId}/original`, `assets/${assetId}/preview`] : [];
  const storageResults = await Promise.allSettled(paths.map(async (path) => {
    try {
      await deleteObject(storageRef(getStorage(), path));
    } catch (error) {
      if (error?.code !== "storage/object-not-found") throw error;
    }
  }));
  const storageFailures = storageResults.filter((result) => result.status === "rejected");
  if (storageFailures.length) {
    if (assetId) {
      await updateDoc(
        doc(db, "mediaAssets", assetId),
        { cleanupStatus: "pending", cleanupRequestedAt: serverTimestamp() },
      ).catch(() => undefined);
    }
    throw storageFailures[0].reason || new Error("incident_photo_cleanup_failed");
  }
  if (assetId) {
    try {
      await deleteDoc(doc(db, "mediaAssets", assetId));
    } catch (error) {
      await updateDoc(
        doc(db, "mediaAssets", assetId),
        { cleanupStatus: "pending", cleanupRequestedAt: serverTimestamp() },
      ).catch(() => undefined);
      throw error;
    }
  }
}


function taipeiDateKeyFromMillis(value) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
  );
  return `${values.year}-${values.month}-${values.day}`;
}

function formatTaipeiDateTime(value) {
  const date = toDateSafe(value);
  if (!date) return "—";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]),
  );
  return `${values.year}/${values.month}/${values.day} ${values.hour}:${values.minute}`;
}

function nextTaipeiMidnightMillis(value) {
  const [year, month, day] = taipeiDateKeyFromMillis(value).split("-").map(Number);
  return Date.UTC(year, month - 1, day + 1) - 8 * 60 * 60 * 1000;
}

function trackingRangesByTaipeiDay(startAt, endAt) {
  const ranges = [];
  let cursor = startAt;
  while (cursor < endAt) {
    const nextMidnight = nextTaipeiMidnightMillis(cursor);
    const rangeEnd = Math.min(endAt, nextMidnight - 1);
    if (rangeEnd > cursor) {
      ranges.push({ dateKey: taipeiDateKeyFromMillis(cursor), startAt: cursor, endAt: rangeEnd });
    }
    cursor = rangeEnd + 1;
  }
  if (!ranges.length && endAt > startAt) {
    ranges.push({ dateKey: taipeiDateKeyFromMillis(startAt), startAt, endAt: startAt + 1 });
  }
  const endDateKey = taipeiDateKeyFromMillis(endAt);
  if (endAt > startAt && !ranges.some((range) => range.dateKey === endDateKey)) {
    ranges.push({ dateKey: endDateKey, startAt: Math.max(startAt, endAt - 1), endAt });
  }
  return ranges;
}

const reportDayRef = (orgId, day) => doc(db, "orgs", orgId, "reportDays", day);

const reportSummaryItemsRef = (orgId, day) =>
  collection(db, "orgs", orgId, "reportDays", day, "items");

const reportTypeItemsRef = (orgId, day, type) =>
  collection(db, "orgs", orgId, "reportDays", day, "types", type, "items");

const reportTypeMetaRef = (orgId, day, type) =>
  doc(db, "orgs", orgId, "reportDays", day, "types", type);

const reportTypeChunksRef = (orgId, day, type) =>
  collection(db, "orgs", orgId, "reportDays", day, "types", type, "chunks");

const reportMetaVersion = (meta) => {
  const revision = meta?.revision;
  if (revision !== undefined && revision !== null) return String(revision);
  const updatedAt = meta?.updatedAt;
  if (typeof updatedAt?.toMillis === "function") return String(updatedAt.toMillis());
  if (updatedAt?.seconds !== undefined) return `${updatedAt.seconds}:${updatedAt.nanoseconds || 0}`;
  return text(updatedAt);
};

const recordsFromChunks = (snapshots, day, reportType) =>
  snapshots.flatMap((snapshot) =>
    Object.entries(snapshot.data()?.items || {}).map(([id, item]) => ({
      id,
      ...(item || {}),
      reportType,
      dateKey: item?.workDateKey || item?.dateKey || day,
      occurredAt: item?.createdAt || item?.updatedAt || null,
      reportChunkId: snapshot.id,
    })),
  );

const loadChunkRecords = async (orgId, days, reportType) => {
  const results = await Promise.all(days.map(async (day) => {
    const metaSnapshot = await getDoc(reportTypeMetaRef(orgId, day, reportType));
    const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
    const chunkIds = Array.isArray(meta.chunkIds) ? meta.chunkIds.map(text).filter(Boolean) : [];
    const snapshots = await Promise.all(chunkIds.map((chunkId) =>
      getDoc(doc(reportTypeChunksRef(orgId, day, reportType), chunkId)),
    ));
    return { day, meta, snapshots: snapshots.filter((snapshot) => snapshot.exists()) };
  }));
  return {
    records: results.flatMap(({ day, snapshots }) => recordsFromChunks(snapshots, day, reportType)),
    metaByDay: Object.fromEntries(results.map(({ day, meta }) => [day, meta])),
  };
};

const loadCargoRecords = async (orgId, days) => {
  const result = await loadChunkRecords(orgId, days, "cargo");
  return result.records;
};

const loadAttendanceRecords = async (orgId, day) =>
  loadChunkRecords(orgId, [day], "attendance");

const loadTransportRecords = async (orgId, day) =>
  loadChunkRecords(orgId, [day], "transport");

const reportTypeSummaryRef = (orgId, day) =>
  collection(db, "orgs", orgId, "reportDays", day, "types");

const fmtDate = (value) => {
  const date = toDateSafe(value);

  if (!date) return "—";

  return `${date.getFullYear()}/${pad2(
    date.getMonth() + 1,
  )}/${pad2(date.getDate())}`;
};

const fmtDateTime = (value) => {
  const date = toDateSafe(value);

  if (!date) return "—";

  return `${fmtDate(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const money = (value) =>
  `NT$ ${Math.round(number(value)).toLocaleString("zh-TW")}`;

const normalizeType = (report) => {
  const raw = text(
    report?.type || report?.reportType || report?.categoryType,
  ).toLowerCase();

  if (
    ["pre_trip", "pretrip", "inspection", "vehicle_inspection"].includes(raw)
  ) {
    return "pre_trip";
  }

  if (["expense", "fuel", "cost", "payment"].includes(raw)) {
    return "expense";
  }

  if (["incident", "emergency", "accident", "abnormal"].includes(raw)) {
    return "incident";
  }

  if (["cargo", "cargo_report", "cargo_index"].includes(raw)) return "cargo";
  return raw || "other";
};

const TYPE_META = {
  all: {
    title: "全部紀錄",
    short: "全部",
    icon: "reports",
    tone: "neutral",
  },

  pre_trip: {
    title: "出車前檢查",
    short: "出車檢查",
    icon: "clipboard",
    tone: "green",
  },

  expense: {
    title: "費用登記",
    short: "費用",
    icon: "wallet",
    tone: "blue",
  },

  incident: {
    title: "緊急異常",
    short: "異常",
    icon: "alert",
    tone: "red",
  },
  cargo: { title: "貨物報表", short: "貨物", icon: "box", tone: "orange" },
};

const REPORT_CATALOG = {
  all: {
    title: "全部報表",
    short: "全部",
    icon: "reports",
    tone: "neutral",
    description: "查看所有車隊紀錄",
    columns: [
      ["date", "日期"],
      ["type", "報表類型"],
      ["driver", "成員"],
      ["vehicle", "車牌"],
      ["content", "內容"],
      ["amount", "金額"],
      ["status", "狀態"],
    ],
  },
  safety: {
    title: "出車前安全檢查",
    short: "安全檢查",
    icon: "clipboard",
    tone: "green",
    description: "每月出車前車輛安全檢查紀錄",
    columns: [
      ["date", "日期"],
      ["safetyFuel", "燃料油、機油、水"],
      ["safetyLights", "各燈光、雨刷、照後鏡、喇叭"],
      ["safetyBattery", "電瓶、胎壓、胎紋、風扇、皮帶、冷氣"],
      ["safetyBrake", "轉向、煞車系統"],
      ["safetyDashboard", "儀表板警示燈、行車記錄器"],
      ["safetyFire", "滅火器壓力及有效期限"],
      ["safetyRepair", "待修理或需更換部分"],
      ["safetyCargo", "貨物是否綑綁穩妥、覆蓋嚴密"],
      ["safetyDriver", "駕駛人確認"],
    ],
  },
  attendance: {
    title: "成員出勤表",
    short: "成員出勤",
    icon: "user",
    tone: "blue",
    description: "工作日期、成員、工作與執行狀態",
    columns: [
      ["date", "工作日期"],
      ["attendanceDriver", "成員"],
      ["attendanceJob", "工作"],
      ["jobMode", "形式"],
      ["jobType", "類型"],
      ["attendanceStatus", "出勤狀態"],
      ["workStatus", "工作狀態"],
    ],
  },
  maintenance: {
    title: "車輛保養表",
    short: "車輛保養",
    icon: "truck",
    tone: "blue",
    description: "車輛保養項目、里程與費用",
    columns: [
      ["vehicle", "車牌號碼"],
      ["date", "保養日期"],
      ["maintenanceOdometer", "里程讀數"],
      ["maintenanceItem", "保養項目"],
      ["amount", "保養金額"],
      ["note", "備註"],
    ],
  },
  incident: {
    title: "異常事故紀錄",
    short: "異常事故",
    icon: "alert",
    tone: "red",
    description: "異常、事故與後續處理紀錄",
    columns: [
      ["date", "日期"],
      ["incidentTime", "時間"],
      ["incidentReporter", "通報者"],
      ["vehicle", "車牌號碼"],
      ["incidentDescription", "異常說明"],
      ["incidentFollowUp", "後續處理"],
      ["note", "備註"],
    ],
  },
  transport: {
    title: "運輸任務總表",
    short: "運輸任務",
    icon: "truck",
    tone: "blue",
    description: "成員實際執行過的站點運輸紀錄",
    columns: [
      ["date", "工作日期"],
      ["transportDriver", "成員"],
      ["transportJob", "工作"],
      ["transportStop", "站點"],
      ["transportStartedAt", "開始時間"],
      ["transportCompletedAt", "完成時間"],
      ["transportStatus", "執行狀態"],
    ],
  },
  breath: {
    title: "出車前酒測紀錄",
    short: "出車酒測",
    icon: "shield",
    tone: "green",
    description: "出車前酒測數值與照片紀錄",
    columns: [
      ["date", "日期"],
      ["breathTime", "時間"],
      ["breathValue", "酒測器讀數"],
      ["breathPhoto", "酒測器照片"],
      ["note", "備註"],
    ],
  },
  expense: {
    title: "一般開銷支出",
    short: "一般開銷",
    icon: "wallet",
    tone: "orange",
    description: "一般營運開銷與支出明細",
    columns: [
      ["date", "日期"],
      ["expenseClaimant", "申報者"],
      ["expensePurpose", "使用事項"],
      ["amount", "金額"],
      ["note", "備註"],
    ],
  },
  cargo: {
    title: "貨物報表", short: "貨物", icon: "box", tone: "orange",
    description: "工作日期內建立的貨物基礎索引",
    columns: [
      ["date", "工作日期"],
      ["recipientName", "聯絡人"],
      ["recipientRole", "角色"],
      ["recipientPhone", "電話"],
      ["cargoName", "貨物名稱"],
      ["quantity", "數量"],
      ["unit", "單位"],
    ],
  },
};

const REPORT_KEYS = [
  "all",
  "safety",
  "attendance",
  "maintenance",
  "incident",
  "transport",
  "breath",
  "expense",
  "cargo",
];

const REPORT_TAB_LABELS = {
  all: "全部",
  safety: "出車前檢查",
  attendance: "成員出勤",
  maintenance: "車輛保養",
  incident: "異常",
  transport: "運輸總表",
  breath: "酒測",
  expense: "費用紀錄",
  cargo: "貨物報表",
};

const EMPTY_REPORT_FORM = {
  reportType: "safety",
  driverId: "",
  vehicleId: "",
  occurredAt: "",
  status: "pending",
  severity: "normal",
  odometer: "",
  abnormalCount: "0",
  canDepart: "true",
  summary: "",
  attendanceMonth: "",
  expectedDays: "",
  actualDays: "",
  leaveDays: "",
  category: "fuel",
  amount: "",
  liters: "",
  vendor: "",
  maintenanceItem: "",
  incidentCategory: "other",
  reporter: "",
  incidentTime: "",
  incidentDescription: "",
  incidentPhotos: [],
  followUp: "",
  taskNo: "",
  loading: "",
  unloading: "",
  distanceKm: "",
  startTime: "",
  endTime: "",
  breathTime: "",
  breathValue: "",
  note: "",
};

function reportPayload(report) {
  return report?.data || report?.payload || report;
}

function nestedValue(value, path) {
  return path.reduce((current, key) => current?.[key], value);
}

function firstReportValue(report, paths) {
  const sources = [reportPayload(report), report];
  for (const source of sources) {
    for (const path of paths) {
      const value = nestedValue(source, path);
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }
  return "";
}

function inspectionCell(report, index, keys = []) {
  const inspection =
    report?.inspection || reportPayload(report)?.inspection || {};
  const items = Array.isArray(inspection.items)
    ? inspection.items
    : Array.isArray(report?.items)
      ? report.items
      : [];
  const item =
    items.find((candidate) =>
      keys.some((key) =>
        text(candidate?.key || candidate?.name || candidate?.label)
          .toLowerCase()
          .includes(key),
      ),
    ) || items[index];
  if (!item) return "";
  if (
    item.normal === false ||
    ["abnormal", "fail", "failed", "ng", "bad"].includes(
      text(item.status).toLowerCase(),
    )
  )
    return "異常";
  if (
    item.normal === true ||
    ["normal", "pass", "passed", "ok"].includes(text(item.status).toLowerCase())
  )
    return "正常";
  return text(item.note || item.value || item.result || item.status);
}

function reportKey(report) {
  const raw = text(
    report?.reportType ||
      report?.type ||
      report?.categoryType ||
      report?.category,
  ).toLowerCase();
  const payload = reportPayload(report);
  if (
    [
      "pre_trip",
      "pretrip",
      "inspection",
      "vehicle_inspection",
      "safety",
    ].includes(raw) ||
    report?.inspection ||
    payload?.inspection
  )
    return "safety";
  if (
    ["attendance", "driver_attendance", "attendance_record"].includes(raw) ||
    report?.attendance
  )
    return "attendance";
  if (["fuel", "refuel", "fueling"].includes(raw)) return "expense";
  if (
    ["maintenance", "vehicle_maintenance", "repair"].includes(raw) ||
    report?.maintenance
  )
    return "maintenance";
  if (
    ["incident", "emergency", "accident", "abnormal"].includes(raw) ||
    report?.incident
  )
    return "incident";
  if (["cargo", "cargo_report", "cargo_index"].includes(raw) || report?.contentId) return "cargo";
  if (
    ["transport", "transport_task", "task", "application"].includes(raw) ||
    report?.applicationId ||
    report?.scheduleSnapshot
  )
    return "transport";
  if (
    ["breath", "breathalyzer", "alcohol", "alcohol_test"].includes(raw) ||
    report?.breathalyzer ||
    report?.alcoholTest
  )
    return "breath";
  if (["expense", "general_expense", "cost", "payment"].includes(raw))
    return "expense";
  return "expense";
}

function formatReportCell(value) {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (value instanceof Date || typeof value?.toDate === "function")
    return fmtDateTime(value);
  if (Array.isArray(value)) return value.join("、");
  return String(value);
}

function normalizeCsvHeader(value) {
  return text(value)
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/[\s_\-()/：:]/g, "");
}

function parseCsvRows(csvText) {
  const source = String(csvText || "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    const next = source[index + 1];

    if (character === '"') {
      if (quoted && next === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === "," && !quoted) {
      row.push(cell);
      cell = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && next === "\n") index += 1;
      row.push(cell);
      if (row.some((value) => text(value))) rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += character;
    }
  }

  if (cell || row.length) {
    row.push(cell);
    if (row.some((value) => text(value))) rows.push(row);
  }

  if (rows.length < 2) return [];
  const headers = rows.shift().map((value, index) => text(value) || `欄位${index + 1}`);
  return rows.map((values) =>
    Object.fromEntries(headers.map((header, index) => [header, text(values[index])])),
  );
}

function csvField(row, names) {
  const wanted = names.map(normalizeCsvHeader);
  const entry = Object.entries(row || {}).find(([header]) =>
    wanted.includes(normalizeCsvHeader(header)),
  );
  return entry ? text(entry[1]) : "";
}

const REPORT_INSPECTION_FIELDS = {
  safetyFuel: [0, ["fuel", "oil", "water"]],
  safetyLights: [1, ["light", "wiper", "mirror", "horn"]],
  safetyBattery: [2, ["battery", "tire", "tyre", "fan", "belt", "air"]],
  safetyBrake: [3, ["steer", "brake"]],
  safetyDashboard: [4, ["dashboard", "recorder", "warning"]],
  safetyFire: [5, ["fire", "extinguisher"]],
  safetyCargo: [7, ["cargo", "tie", "cover"]],
};

const REPORT_COLUMN_PATHS = {
  note: [["note"], ["remark"], ["remarks"]],
  safetyRepair: [
    ["inspection", "repair"],
    ["repair"],
    ["inspection", "needsRepair"],
  ],
  safetyDriver: [
    ["inspection", "driverConfirmed"],
    ["inspection", "confirmed"],
    ["driverConfirmed"],
  ],
  attendanceExpected: [
    ["attendance", "expectedDays"],
    ["expectedDays"],
    ["requiredDays"],
  ],
  attendanceActual: [
    ["attendance", "actualDays"],
    ["actualDays"],
    ["workedDays"],
  ],
  attendanceLeave: [
    ["attendance", "leaveDays"],
    ["leaveDays"],
    ["absenceDays"],
  ],
  fuelItem: [
    ["expense", "item"],
    ["expense", "category"],
    ["item"],
    ["product"],
  ],
  fuelLiters: [["expense", "liters"], ["liters"], ["fuelLiters"], ["quantity"]],
  fuelOdometer: [["expense", "odometer"], ["odometer"], ["fuelOdometer"]],
  maintenanceOdometer: [["maintenance", "odometer"], ["odometer"]],
  maintenanceItem: [
    ["maintenance", "item"],
    ["maintenance", "description"],
    ["maintenanceItem"],
    ["item"],
  ],
  incidentTime: [["incident", "time"], ["time"]],
  incidentReporter: [["incident", "reporter"], ["reporter"], ["reportedBy"]],
  incidentDescription: [["incident", "description"], ["description"], ["note"]],
  incidentFollowUp: [
    ["incident", "followUp"],
    ["incident", "followup"],
    ["followUp"],
    ["handling"],
  ],
  transportLoading: [
    ["stops", 0, "text"],
    ["stops", 0, "address"],
    ["routeSnapshot", "pickup", "text"],
    ["pickupText"],
  ],
  transportUnloading: [
    ["stops", 1, "text"],
    ["stops", 1, "address"],
    ["routeSnapshot", "dropoff", "text"],
    ["dropoffText"],
  ],
  transportDistance: [
    ["scheduleSnapshot", "distanceKm"],
    ["routeMetrics", "distanceKm"],
    ["distanceKm"],
  ],
  transportComplete: [
    ["scheduleSnapshot", "endTimeISO"],
    ["endTimeISO"],
    ["execution", "completedAt"],
  ],
  breathTime: [["breathalyzer", "time"], ["alcoholTest", "time"], ["time"]],
  breathValue: [
    ["breathalyzer", "value"],
    ["alcoholTest", "value"],
    ["breathValue"],
    ["reading"],
  ],
  expensePurpose: [
    ["expense", "purpose"],
    ["expense", "note"],
    ["purpose"],
    ["description"],
  ],
};

function reportColumnValue(report, key) {
  const date = reportDate(report);
  const amount = expenseAmount(report);
  if (reportKey(report) === "attendance") {
    const jobTypeText = {
      NONE: "一般",
      CONTAINER: "貨櫃",
      HAZARDOUS_MATERIAL: "危險品",
      WASTE: "廢棄物",
      SOIL_AGGREGATE: "土石方",
      HEAVY_EQUIPMENT: "大型設備",
      COLD_CHAIN: "冷鏈",
      LIVE_ANIMAL: "活體動物",
      VEHICLE_MACHINERY: "車輛機械",
      INSPECTION: "出車前檢查",
    };
    const statusText = (value) => ({
      assigned: "已指派",
      present: "已出勤",
      working: "工作中",
      on_duty: "已出勤",
      break: "休息中",
      completed: "已完成",
      not_started: "未開始",
      in_progress: "進行中",
      cancelled: "已取消",
    }[text(value).toLowerCase()] || text(value) || "—");
    if (key === "date") return report.workDateKey || report.dateKey || "—";
    if (key === "attendanceDriver") return report.driverName || report.driverPhone || "—";
    if (key === "attendanceJob") return report.jobName || report.applicationId || "—";
    if (key === "jobMode") {
      const mode = text(report.jobMode).toLowerCase();
      return mode === "single" ? "單點" : mode === "multi" ? "多點" : text(report.jobMode) || "—";
    }
    if (key === "jobType") {
      const type = text(report.jobType);
      return jobTypeText[type.toUpperCase()] || type || "一般";
    }
    if (key === "attendanceStatus") return statusText(report.attendanceStatus);
    if (key === "workStatus") return statusText(report.workStatus);
  }
  if (reportKey(report) === "cargo") {
    if (key === "date") return report.dateKey || fmtDate(date);
    if (key === "recipientRole") {
      const role = text(report.recipientRole).toLowerCase();
      if (["sender", "pickup", "寄件人", "取貨人"].includes(role)) return "寄件人";
      if (["receiver", "收貨人", "delivery", "dropoff"].includes(role)) return "收貨人";
      return text(report.recipientRole) || "—";
    }
    if (["applicationId", "stopId", "recipientId", "contentId"].includes(key)) {
      return report[key] ?? "";
    }
    if (["recipientName", "recipientPhone", "cargoName", "quantity", "unit"].includes(key)) {
      return report[key] ?? "";
    }
  }
  if (reportKey(report) === "transport") {
    const transportStatusText = {
      in_progress: "執行中",
      arrived: "已抵達",
      exception: "異常",
      completed: "已完成",
    };
    if (key === "date") return report.workDateKey || report.dateKey || "—";
    if (key === "transportDriver") return report.driverName || report.driverPhone || "—";
    if (key === "transportJob") return report.jobName || report.applicationId || "—";
    if (key === "transportStop") {
      const stopName = report.stopName || report.stopId || "—";
      const stopOrder = Number(report.stopOrder);
      return stopOrder > 0 ? `${stopOrder} (${stopName})` : stopName;
    }
    if (key === "transportStartedAt") return fmtDateTime(report.startedAt);
    if (key === "transportCompletedAt") return fmtDateTime(report.completedAt);
    if (key === "transportStatus") return transportStatusText[text(report.transportStatus).toLowerCase()] || text(report.transportStatus) || "—";
  }
  const common = {
    date: fmtDate(date),
    type: REPORT_CATALOG[reportKey(report)]?.title || "其他報表",
    driver: reportDriverName(report),
    vehicle: reportVehiclePlate(report),
    content: getReportTitle(report),
    amount: amount ? money(amount) : "—",
    status: getStatusMeta(report).text,
  };

  if (Object.prototype.hasOwnProperty.call(common, key)) return common[key];

  const inspection = REPORT_INSPECTION_FIELDS[key];
  if (inspection) return inspectionCell(report, inspection[0], inspection[1]);

  if (key === "attendanceRate") {
    const rate = firstReportValue(report, [
      ["attendance", "attendanceRate"],
      ["attendanceRate"],
      ["attendance", "rate"],
      ["rate"],
    ]);
    return rate === ""
      ? "—"
      : `${Number(rate) <= 1 ? Number(rate) * 100 : Number(rate)}%`;
  }

  if (key === "transportTask")
    return (
      reportApplicationId(report) ||
      firstReportValue(report, [["taskNo"], ["taskNumber"], ["jobNo"]])
    );
  if (key === "transportStart")
    return (
      firstReportValue(report, [
        ["scheduleSnapshot", "startTimeISO"],
        ["startTimeISO"],
        ["execution", "startedAt"],
      ]) || fmtDateTime(date)
    );
  if (key === "breathPhoto")
    return getPhotos(report).length ? "已上傳" : "未上傳";
  if (key === "expenseClaimant")
    return (
      firstReportValue(report, [
        ["expense", "claimant"],
        ["claimant"],
        ["reportedBy"],
      ]) || reportDriverName(report)
    );

  return firstReportValue(report, REPORT_COLUMN_PATHS[key] || []);
}

const STATUS_META = {
  pending: {
    text: "待處理",
    tone: "orange",
  },

  new: {
    text: "待處理",
    tone: "orange",
  },

  abnormal: {
    text: "發現異常",
    tone: "red",
  },

  needs_review: {
    text: "需要確認",
    tone: "red",
  },

  reviewing: {
    text: "處理中",
    tone: "blue",
  },

  processing: {
    text: "處理中",
    tone: "blue",
  },

  resolved: {
    text: "已結案",
    tone: "green",
  },

  completed: {
    text: "已完成",
    tone: "green",
  },

  approved: {
    text: "已核准",
    tone: "green",
  },

  normal: {
    text: "正常",
    tone: "green",
  },

  rejected: {
    text: "已退回",
    tone: "gray",
  },
};

const EXPENSE_LABELS = {
  fuel: "加油",
  parking: "停車費",
  toll: "過路費",
  repair: "維修費",
  wash: "洗車",
  meal: "餐費",
  accommodation: "住宿",
  tire: "輪胎",
  maintenance: "保養",
  other: "其他費用",
};

const INCIDENT_LABELS = {
  accident: "交通事故",
  collision: "碰撞事故",
  breakdown: "車輛故障",
  cargo_damage: "貨物損壞",
  cargo_loss: "貨物遺失",
  injury: "人員受傷",
  traffic: "交通異常",
  mechanical: "機械異常",
  other: "其他緊急狀況",
};

const CARGO_EXECUTION_TIMELINE = ["尚未開始", "進行中", "已簽收", "已完成"];

function getStatus(report) {
  const raw = text(report?.status).toLowerCase();

  return ["completed", "resolved", "approved", "normal"].includes(raw)
    ? "completed"
    : "pending";
}

function getStatusMeta(report) {
  const status = getStatus(report);

  return (
    STATUS_META[status] || {
      text: status || "待處理",
      tone: "gray",
    }
  );
}

function reportDate(report) {
  return (
    toDateSafe(report?.occurredAt) ||
    toDateSafe(report?.submittedAt) ||
    toDateSafe(report?.createdAt) ||
    toDateSafe(report?.updatedAt)
  );
}

function reportDriverName(report) {
  return text(report?.driverName) || text(report?.driver?.name) || "未指定成員";
}

function reportDriverId(report) {
  return text(report?.driverId) || text(report?.driver?.id);
}

function reportVehiclePlate(report) {
  return (
    text(report?.vehicle?.plateNo) ||
    text(report?.vehiclePlate) ||
    text(report?.plateNo) ||
    "—"
  );
}

function reportVehicleText(report) {
  const plate = reportVehiclePlate(report);

  const vehicleType = text(report?.vehicle?.type);

  return [plate, vehicleType].filter((v) => v && v !== "—").join(" · ") || "—";
}

function reportJobTitle(report) {
  return (
    text(report?.jobTitle) ||
    text(report?.applicationName) ||
    text(report?.application?.title) ||
    ""
  );
}

function reportApplicationId(report) {
  return text(report?.applicationId) || text(report?.application?.id);
}

function cargoDetailMissingReason(report) {
  if (!text(report?.workDateKey || report?.dateKey)) return "工作日期";
  if (!reportApplicationId(report)) return "applicationId";
  if (!text(report?.stopId)) return "站點 ID";
  if (!text(report?.recipientId)) return "聯絡人 ID";
  if (!text(report?.contentId)) return "貨物 ID";
  return "";
}

function cargoQrPayload(report) {
  const workDateKey = text(report?.workDateKey || report?.dateKey);
  const applicationId = reportApplicationId(report);
  const stopId = text(report?.stopId).toUpperCase();
  const recipientId = text(report?.recipientId);
  const contentId = text(report?.contentId);
  if (!workDateKey || !applicationId || !stopId || !recipientId || !contentId) return "";
  const expectedItemId = [applicationId, stopId, recipientId, contentId]
    .map((value) => value.replaceAll("/", "_"))
    .join("__");
  const storedItemId = text(report?.reportItemId || report?.id);
  if (storedItemId && storedItemId !== expectedItemId) return "";
  const reportItemId = storedItemId || expectedItemId;
  return `${workDateKey},${reportItemId}`;
}

function cargoQrImageUri(payload, size = 320) {
  const value = text(payload);
  return value
    ? `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}`
    : "";
}

function expenseAmount(report) {
  return number(report?.expense?.amount ?? report?.amount);
}

function expenseCategory(report) {
  return text(report?.expense?.category || report?.category).toLowerCase();
}

function incidentCategory(report) {
  return text(report?.incident?.category || report?.category).toLowerCase();
}

function severityOf(report) {
  const raw = text(
    report?.severity || report?.incident?.severity,
  ).toLowerCase();

  if (raw) return raw;

  if (normalizeType(report) === "incident") {
    return "high";
  }

  if (
    normalizeType(report) === "pre_trip" &&
    number(report?.inspection?.abnormalCount) > 0
  ) {
    return "medium";
  }

  return "normal";
}

function severityText(report) {
  const severity = severityOf(report);

  if (["critical", "emergency"].includes(severity)) {
    return "緊急";
  }

  if (["high", "severe"].includes(severity)) {
    return "高";
  }

  if (severity === "medium") {
    return "中";
  }

  if (severity === "low") {
    return "低";
  }

  return "一般";
}

function getReportTitle(report) {
  const type = normalizeType(report);

  if (type === "pre_trip") {
    const abnormal = number(report?.inspection?.abnormalCount);

    return abnormal > 0 ? `出車檢查 · ${abnormal} 項異常` : "出車前檢查正常";
  }

  if (type === "expense") {
    const category = expenseCategory(report);

    return EXPENSE_LABELS[category] || "費用登記";
  }

  if (type === "incident") {
    const category = incidentCategory(report);

    return INCIDENT_LABELS[category] || "緊急異常";
  }

  if (type === "cargo") return `貨物報表 · ${text(report?.cargoNumber) || text(report?.contentId) || "未編號"}`;
  return text(report?.title) || "其他紀錄";
}

function getReportDescription(report) {
  const type = normalizeType(report);

  if (type === "pre_trip") {
    return (
      text(report?.inspection?.summary) ||
      text(report?.note) ||
      (number(report?.inspection?.abnormalCount) > 0
        ? "檢查項目發現異常，請管理端確認。"
        : "出車前檢查均為正常。")
    );
  }

  if (type === "expense") {
    const vendor = text(report?.expense?.vendor);

    const note = text(report?.expense?.note || report?.note);

    return [vendor, note].filter(Boolean).join(" · ") || "成員費用登記";
  }

  if (type === "incident") {
    return (
      text(report?.incident?.description) ||
      text(report?.description) ||
      text(report?.note) ||
      "緊急事件回報"
    );
  }

  return text(report?.description) || text(report?.note) || "";
}

function reportSummary(report, id, day) {
  const type = reportKey(report);
  const amount = expenseAmount(report);

  return {
    reportId: id,
    dateKey: day,
    reportType: type,
    title: getReportTitle(report),
    summary: getReportDescription(report),
    status: getStatus(report),
    severity: severityOf(report),
    occurredAt: reportDate(report) || new Date(),
    driverId: reportDriverId(report),
    driverName: reportDriverName(report),
    vehicleId: text(report?.vehicleId || report?.vehicle?.id),
    vehiclePlate: reportVehiclePlate(report),
    applicationId: reportApplicationId(report),
    jobTitle: reportJobTitle(report),
    amount,
    photoCount:
      type === "incident"
        ? Math.max(getPhotos(report).length, number(report?.photoCount))
        : getPhotos(report).length,
    evidenceCount:
      type === "incident"
        ? Math.max(getEvidence(report).length, number(report?.evidenceCount))
        : getEvidence(report).length,
    evidenceStatus:
      text(report?.expense?.evidenceStatus) ||
      (type === "incident" ? text(report?.evidenceStatus) : "") ||
      (getEvidence(report).some((item) => item.kind === "pdf")
        ? "digital_file_attached"
        : getEvidence(report).length
          ? "original_paper_required"
          : "missing"),
    updatedAt: serverTimestamp(),
  };
}

function getEvidence(report) {
  const arrays = [
    report?.photos,
    report?.images,
    report?.attachments,
    report?.inspection?.photos,
    report?.expense?.receiptPhotos,
    report?.expense?.photos,
    report?.expense?.receiptEvidence,
    report?.expense?.evidence,
    report?.incident?.photos,
  ];
  const result = [];
  const seen = new Set();
  arrays.forEach((array) => {
    if (!Array.isArray(array)) return;
    array.forEach((item) => {
      const assetId = typeof item === "string" ? "" : text(item?.assetId);
      if (!assetId || seen.has(assetId)) return;
      seen.add(assetId);
      const name = text(item?.name || item?.fileName || item?.originalName);
      const mimeType = text(item?.contentType || item?.mimeType).toLowerCase();
      const isPdf = item?.kind === "pdf" || mimeType === "application/pdf" || /\.pdf($|\?)/i.test(name);
      result.push({
        assetId,
        name: name || (isPdf ? "費用證明.pdf" : "費用照片"),
        kind: isPdf ? "pdf" : "image",
        sizeBytes: number(item?.sizeBytes),
      });
    });
  });
  return result;
}

function getPhotos(report) {
  return getEvidence(report).filter((item) => item.kind === "image");
}

function Icon({ name, size = 20 }) {
  const paths = {
    reports: "M4 19V9m6 10V5m6 14v-7m4 7H2",

    clipboard: "M9 5h6M9 3h6v4H9V3ZM6 5H4v16h16V5h-2M8 12l2 2 4-4M8 18h8",

    wallet:
      "M3 7h15a3 3 0 0 1 3 3v8H5a2 2 0 0 1-2-2V7Zm0 0V5a2 2 0 0 1 2-2h12v4M16 12h5v4h-5a2 2 0 0 1 0-4Z",

    alert: "M12 3 2.8 20h18.4L12 3Zm0 6v5m0 3h.01",

    search: "m16 16 5 5M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0",

    filter: "M4 6h16M7 12h10m-7 6h4",

    download: "M12 3v12m0 0 5-5m-5 5-5-5M4 21h16",

    upload: "M12 21V9m0 0 5 5m-5-5-5 5M4 3h16",

    arrow: "M5 12h14m-5-5 5 5-5 5",

    close: "m6 6 12 12M18 6 6 18",

    user: "M20 21a8 8 0 0 0-16 0m8-10a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",

    truck:
      "M3 5h11v11H3V5Zm11 4h4l3 4v3h-7V9ZM8 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z",

    box: "m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm0 9 8-4.5M12 12v9m0-9L4 7.5m4-2.25 8 4.5",

    clock: "M12 7v5l3 2M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0",

    check: "m5 12 4 4L19 6",

    eye: "M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Zm13 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",

    fuel: "M5 3h9v18H5V3Zm2 3h5v5H7V6Zm7 2 3 3v7a2 2 0 0 0 4 0v-7l-2-2",

    pin: "M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Zm-5 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",

    image: "M3 4h18v16H3V4Zm0 12 5-5 4 4 3-3 6 6M9 8h.01",

    document:
      "M6 3h9l3 3v15H6V3Zm9 0v4h4M9 12h6M9 16h6",

    shield:
      "M12 3 4 6v5c0 5 3.2 8.5 8 10 4.8-1.5 8-5 8-10V6l-8-3Zm-3 9 2 2 4-4",

    dots: "M5 12h.01M12 12h.01M19 12h.01",

    qr: "M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h2v2h-2zM18 14h3v7h-7v-3h4z",
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name] || paths.reports} />
    </svg>
  );
}

function StatusBadge({ report }) {
  const meta = getStatusMeta(report);

  return (
    <span className={`dmr-status dmr-status-${meta.tone}`}>
      <i />
      {meta.text}
    </span>
  );
}

function StatCard({ icon, label, value, description, tone }) {
  return (
    <div className="dmr-stat-card">
      <div className={`dmr-stat-icon dmr-tone-${tone}`}>
        <Icon name={icon} size={21} />
      </div>

      <div className="dmr-stat-copy">
        <span>{label}</span>

        <strong>{value}</strong>

        <small>{description}</small>
      </div>
    </div>
  );
}

function EmptyState({ activeType }) {
  const meta = REPORT_CATALOG[activeType] || REPORT_CATALOG.all;

  return (
    <div className="dmr-empty-state-with-table">
      <ReportTable reports={[]} activeType={activeType} onSelect={() => {}} />
      <div className="dmr-empty">
        <div className="dmr-empty-icon">
          <Icon name={meta.icon} size={28} />
        </div>

        <strong>
          目前沒有
          {meta.title}
        </strong>

        <p>成員從 App 送出的紀錄會自動出現在這裡。</p>
      </div>
    </div>
  );
}

function DetailItem({ label, value, full = false }) {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  return (
    <div className={`dmr-detail-item ${full ? "dmr-detail-full" : ""}`}>
      <span>{label}</span>

      <strong>{String(value)}</strong>
    </div>
  );
}

function InspectionDetail({ report }) {
  const inspection = report?.inspection || {};

  const items = Array.isArray(inspection?.items) ? inspection.items : [];

  return (
    <>
      <div className="dmr-detail-section">
        <h3>出車檢查摘要</h3>

        <div className="dmr-detail-grid">
          <DetailItem
            label="里程"
            value={
              inspection.odometer
                ? `${Number(inspection.odometer).toLocaleString()} km`
                : ""
            }
          />

          <DetailItem
            label="異常項目"
            value={`${number(inspection.abnormalCount)} 項`}
          />

          <DetailItem
            label="是否可出車"
            value={
              inspection.canDepart === false
                ? "不可出車"
                : inspection.canDepart === true
                  ? "可以出車"
                  : "未填寫"
            }
          />

          <DetailItem
            label="檢查結果"
            value={number(inspection.abnormalCount) > 0 ? "發現異常" : "正常"}
          />
        </div>
      </div>

      {!!items.length && (
        <div className="dmr-detail-section">
          <h3>檢查項目</h3>

          <div className="dmr-inspection-list">
            {items.map((item, index) => {
              const itemStatus = text(item?.status).toLowerCase();

              const abnormal =
                ["abnormal", "fail", "failed", "ng", "bad"].includes(
                  itemStatus,
                ) || item?.normal === false;

              return (
                <div
                  className={`dmr-inspection-row ${
                    abnormal ? "is-abnormal" : ""
                  }`}
                  key={item?.key || index}
                >
                  <span className="dmr-inspection-check">
                    <Icon name={abnormal ? "alert" : "check"} size={15} />
                  </span>

                  <div>
                    <strong>
                      {text(item?.label || item?.name) ||
                        `檢查項目 ${index + 1}`}
                    </strong>

                    {text(item?.note) && <small>{item.note}</small>}
                  </div>

                  <b>{abnormal ? "異常" : "正常"}</b>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {text(inspection.summary || report?.note) && (
        <div className="dmr-detail-section">
          <h3>成員備註</h3>

          <div className="dmr-note-box">
            {text(inspection.summary || report?.note)}
          </div>
        </div>
      )}
    </>
  );
}

function ExpenseDetail({ report }) {
  const expense = report?.expense || {};
  const evidence = getEvidence(report);
  const evidenceStatus = text(expense.evidenceStatus);
  const evidenceStatusLabel =
    evidenceStatus === "digital_file_attached"
      ? "已附 PDF 數位檔"
      : evidenceStatus === "original_paper_required"
        ? "照片已附；紙本正本待收"
        : evidence.length
          ? "已附證明，待會計核對"
          : "尚未附證明";

  return (
    <>
      <div className="dmr-detail-section">
        <h3>費用內容</h3>

        <div className="dmr-detail-grid">
          <DetailItem
            label="費用類型"
            value={
              EXPENSE_LABELS[expenseCategory(report)] ||
              expense.category ||
              "其他"
            }
          />

          <DetailItem label="金額" value={money(expenseAmount(report))} />

          <DetailItem label="商家 / 加油站" value={expense.vendor} />

          <DetailItem label="付款方式" value={expense.paymentMethod} />

          <DetailItem
            label="油量"
            value={expense.liters ? `${expense.liters} L` : ""}
          />

          <DetailItem
            label="每公升單價"
            value={expense.unitPrice ? money(expense.unitPrice) : ""}
          />

          <DetailItem
            label="車輛里程"
            value={
              expense.odometer
                ? `${Number(expense.odometer).toLocaleString()} km`
                : ""
            }
          />

          <DetailItem label="發票 / 收據編號" value={expense.receiptNo} />

          <DetailItem label="證明狀態" value={evidenceStatusLabel} />

          <DetailItem label="證明份數" value={evidence.length ? `${evidence.length} 份` : "0 份"} />
        </div>
      </div>

      {text(expense.note || report?.note) && (
        <div className="dmr-detail-section">
          <h3>備註</h3>

          <div className="dmr-note-box">
            {text(expense.note || report?.note)}
          </div>
        </div>
      )}
    </>
  );
}

function IncidentDetail({ report }) {
  const incident = report?.incident || {};

  return (
    <>
      <div className="dmr-emergency-banner">
        <div>
          <Icon name="alert" size={22} />
        </div>

        <section>
          <span>緊急事件</span>

          <strong>
            {INCIDENT_LABELS[incidentCategory(report)] || "緊急異常回報"}
          </strong>

          <p>
            嚴重程度：
            {severityText(report)}
          </p>
        </section>
      </div>

      <div className="dmr-detail-section">
        <h3>事故資訊</h3>

        <div className="dmr-detail-grid">
          <DetailItem
            label="事故類型"
            value={
              INCIDENT_LABELS[incidentCategory(report)] ||
              incident.category ||
              "其他"
            }
          />

          <DetailItem label="嚴重程度" value={severityText(report)} />

          <DetailItem
            label="有人受傷"
            value={
              incident.injuries === true
                ? "是"
                : incident.injuries === false
                  ? "否"
                  : ""
            }
          />

          <DetailItem
            label="車輛可行駛"
            value={
              incident.vehicleOperable === true
                ? "可以"
                : incident.vehicleOperable === false
                  ? "不可以"
                  : ""
            }
          />

          <DetailItem
            label="已報警"
            value={
              incident.policeReported === true
                ? "是"
                : incident.policeReported === false
                  ? "否"
                  : ""
            }
          />

          <DetailItem label="報案編號" value={incident.policeReportNo} />

          <DetailItem
            label="第三方涉入"
            value={
              incident.thirdParty === true
                ? "有"
                : incident.thirdParty === false
                  ? "無"
                  : ""
            }
          />

          <DetailItem
            label="聯絡公司"
            value={
              incident.companyContacted === true
                ? "已聯絡"
                : incident.companyContacted === false
                  ? "尚未"
                  : ""
            }
          />
        </div>
      </div>

      <div className="dmr-detail-section">
        <h3>事件說明</h3>

        <div className="dmr-note-box dmr-incident-note">
          {text(incident.description || report?.description || report?.note) ||
            "成員未填寫事件說明。"}
        </div>
      </div>
    </>
  );
}

function CargoDetail({ report }) {
  return (
    <div className="dmr-detail-section">
      <h3>貨物基本資訊</h3>
      <div className="dmr-detail-grid">
        <DetailItem
          label="聯絡人角色"
          value={report?.recipientRole === "sender" ? "寄件人" : "收貨人"}
        />
        <DetailItem label="聯絡人" value={report?.recipientName} />
        <DetailItem label="電話" value={report?.recipientPhone} />
        <DetailItem label="貨物名稱" value={report?.cargoName} />
        <DetailItem label="數量" value={report?.quantity} />
        <DetailItem label="單位" value={report?.unit} />
        <DetailItem label="工作 ID" value={report?.applicationId} />
        <DetailItem label="站點 ID" value={report?.stopId} />
        <DetailItem label="貨物 ID" value={report?.contentId} />
      </div>
    </div>
  );
}

function ReportMediaSection({ report, sectionTitle = "附件照片", sectionClassName = "", showHeading = true }) {
  const evidence = useMemo(() => getEvidence(report), [report]);
  const assetIds = useMemo(
    () => evidence.map((item) => item.assetId).filter(Boolean),
    [evidence],
  );
  const mediaCacheRef = useRef({});
  const mediaRequestsRef = useRef({});
  const originalRequestsRef = useRef({});
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(false);
  const [openingId, setOpeningId] = useState("");
  const [mediaViewer, setMediaViewer] = useState(null);

  useEffect(() => {
    let active = true;
    if (!assetIds.length) {
      setAssets({});
      return undefined;
    }
    setLoading(true);
    const loadAsset = (assetId) => {
      if (mediaCacheRef.current[assetId]) {
        return Promise.resolve(mediaCacheRef.current[assetId]);
      }
      if (mediaRequestsRef.current[assetId]) {
        return mediaRequestsRef.current[assetId];
      }
      const request = getDoc(doc(db, "mediaAssets", assetId))
        .then(async (snapshot) => {
          if (!snapshot.exists()) {
            const missing = { assetId, previewUrl: "", originalUrl: "" };
            mediaCacheRef.current[assetId] = missing;
            return missing;
          }
          const asset = snapshot.data() || {};
          const previewPath = text(asset.previewPath);
          const previewUrl = previewPath
            ? await getDownloadURL(storageRef(getStorage(), previewPath))
            : "";
          const record = { ...asset, assetId, previewUrl, originalUrl: "" };
          mediaCacheRef.current[assetId] = record;
          return record;
        })
        .catch((error) => {
          console.error("[DriverManagerReports] media metadata error:", error);
          const failed = { assetId, previewUrl: "", originalUrl: "" };
          mediaCacheRef.current[assetId] = failed;
          return failed;
        });
      mediaRequestsRef.current[assetId] = request;
      return request;
    };
    Promise.all(assetIds.map(loadAsset)).then((records) => {
      if (active) {
        setAssets((current) => ({
          ...current,
          ...Object.fromEntries(records.map((record) => [record.assetId, record])),
        }));
      }
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [assetIds]);

  const openOriginal = async (item, label) => {
    const assetId = text(item?.assetId);
    const key = assetId;
    setOpeningId(key);
    try {
      let asset = assetId ? mediaCacheRef.current[assetId] || assets[assetId] : null;
      let url = "";
      let contentType = text(item?.contentType || item?.mimeType).toLowerCase();
      if (assetId) {
        if (!asset) throw new Error("media_asset_not_loaded");
        contentType = text(asset.contentType || contentType).toLowerCase();
        if (asset.originalUrl) {
          url = asset.originalUrl;
        } else {
          const originalPath = text(asset.originalPath);
          if (!originalPath) throw new Error("media_original_path_missing");
          if (!originalRequestsRef.current[assetId]) {
            originalRequestsRef.current[assetId] = getDownloadURL(
              storageRef(getStorage(), originalPath),
            );
          }
          url = await originalRequestsRef.current[assetId];
          asset = { ...asset, originalUrl: url };
          mediaCacheRef.current[assetId] = asset;
          setAssets((current) => ({ ...current, [assetId]: asset }));
        }
      }
      if (!url) throw new Error("media_original_url_missing");
      setMediaViewer({
        url,
        contentType: contentType || (item?.kind === "pdf" ? "application/pdf" : "image/jpeg"),
        label: label || text(asset?.originalName || item?.fileName || item?.name) || "附件",
      });
    } catch (error) {
      console.error("[DriverManagerReports] original media error:", error);
      window.alert("原始附件目前無法開啟，請確認 mediaAssets 與 Storage 權限設定。");
    } finally {
      setOpeningId("");
    }
  };

  const imageItems = useMemo(() => evidence.filter((item) => item.kind === "image"), [evidence]);
  const fileItems = useMemo(() => evidence.filter((item) => item.kind === "pdf"), [evidence]);
  if (!evidence.length) return null;

  return (
    <>
      {!!imageItems.length && (
        <div className={`dmr-detail-section ${sectionClassName}`.trim()}>
          {showHeading ? <h3>{sectionTitle} <span>{imageItems.length} 張</span></h3> : null}
          {loading ? <div className="dmr-media-loading">讀取附件預覽中…</div> : null}
          <div className="dmr-photo-grid">
            {imageItems.map((item, index) => {
              const asset = item.assetId ? assets[item.assetId] : null;
              const src = asset?.previewUrl || "";
              const key = item.assetId;
              return (
                <button type="button" key={`${key}-${index}`} onClick={() => openOriginal(item, `${sectionTitle === "附件照片" ? "報表照片" : sectionTitle} ${index + 1}`)} onContextMenu={(event) => event.preventDefault()} disabled={openingId === key}>
                  {src ? <img src={src} alt={item.name || `報表照片 ${index + 1}`} draggable={false} onContextMenu={(event) => event.preventDefault()} /> : <span className="dmr-media-placeholder">預覽載入中</span>}
                  <span><Icon name="eye" size={15} /></span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {!!fileItems.length && (
        <div className="dmr-detail-section">
          <h3>費用證明檔案 <span>{fileItems.length} 份</span></h3>
          <div className="dmr-evidence-file-list">
            {fileItems.map((file, index) => {
              const key = file.assetId;
              return (
                <button type="button" className="dmr-evidence-file-row" key={`${key}-${index}`} onClick={() => openOriginal(file, file.name)} disabled={openingId === key}>
                  <span className="dmr-evidence-file-icon"><Icon name="document" size={17} /></span>
                  <span className="dmr-evidence-file-copy"><strong>{file.name}</strong><small>PDF 數位檔案 · 點擊查看 ORIGINAL</small></span>
                  <Icon name="arrow" size={16} />
                </button>
              );
            })}
          </div>
        </div>
      )}
      {mediaViewer ? (
        <div className="dmr-media-viewer" role="dialog" aria-modal="true" onClick={() => setMediaViewer(null)}>
          <div className="dmr-media-viewer-card" onClick={(event) => event.stopPropagation()}>
            <div className="dmr-media-viewer-head">
              <strong>{mediaViewer.label}</strong>
              <button type="button" className="dmr-icon-button" onClick={() => setMediaViewer(null)} aria-label="關閉">×</button>
            </div>
            <div className="dmr-media-viewer-body">
              {mediaViewer.contentType.startsWith("image/") ? <img src={mediaViewer.url} alt={mediaViewer.label} /> : <iframe title={mediaViewer.label} src={mediaViewer.url} />}
            </div>
            <div className="dmr-media-viewer-foot"><span>ORIGINAL</span>已載入原始版本</div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function cargoRecipientItems(recipient) {
  if (Array.isArray(recipient?.cargoItems)) return recipient.cargoItems;
  if (Array.isArray(recipient?.contents)) return recipient.contents;
  if (Array.isArray(recipient?.cargo)) return recipient.cargo;
  return [];
}

function cargoAddressText(value) {
  if (typeof value === "string") return text(value);
  return text(
    value?.text || value?.label || value?.formattedAddress || value?.addressText,
  );
}

function cargoMediaReport(records) {
  const photos = (Array.isArray(records) ? records : [])
    .map((record) => {
      const assetId = typeof record === "string" ? text(record) : text(record?.assetId);
      return assetId
        ? {
            ...(typeof record === "object" && record ? record : {}),
            assetId,
            kind: "image",
          }
        : null;
    })
    .filter(Boolean);
  return { photos };
}

function cargoExecutionPresentation(status, operationType) {
  const normalized = text(status).toUpperCase();
  if (normalized === "IN_PROGRESS") {
    return {
      label: operationType === "PICKUP" ? "前往取貨中" : operationType === "DELIVERY" ? "配送中" : "進行中",
      tone: "blue",
    };
  }
  if (normalized === "COMPLETED") return { label: "已完成", tone: "green" };
  if (normalized === "EXCEPTION") return { label: "異常", tone: "red" };
  return { label: "未開始", tone: "gray" };
}

function CargoDetailDrawer({ report, orgId, onClose }) {
  const workDateKey = text(report?.workDateKey || report?.dateKey);
  const applicationId = reportApplicationId(report);
  const stopId = text(report?.stopId);
  const recipientId = text(report?.recipientId);
  const contentId = text(report?.contentId);
  const [detail, setDetail] = useState(null);
  const [cargoState, setCargoState] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setDetail(null);
    setCargoState(null);
    setExceptions([]);
    setError("");
    setLoading(true);

    const loadCargoDetail = async () => {
      const missingReason = cargoDetailMissingReason(report);
      if (!orgId || missingReason) {
        if (active) {
          setError(!orgId
            ? "目前帳號尚未取得組織資料，無法載入貨物詳情。"
            : `這筆貨物缺少${missingReason}，為避免顯示錯誤貨物，無法載入詳情。`);
          setLoading(false);
        }
        return;
      }

      try {
        const jobRef = doc(
          db,
          "orgs",
          orgId,
          "applications",
          workDateKey,
          "jobs",
          applicationId,
        );
        const jobSnapshot = await getDoc(jobRef);
        if (!jobSnapshot.exists()) throw new Error("cargo_detail_job_not_found");

        const job = { id: jobSnapshot.id, ...(jobSnapshot.data() || {}) };
        const stops = Array.isArray(job.stops) ? job.stops : [];
        let matchedCargo = null;
        const targetStop = stops.find(
          (candidate) => text(candidate?.id || candidate?.stopId) === stopId,
        );
        const recipients = Array.isArray(targetStop?.recipients) ? targetStop.recipients : [];
        const targetRecipient = recipients.find(
          (candidate) => text(candidate?.recipientId || candidate?.id) === recipientId,
        );
        const targetCargo = cargoRecipientItems(targetRecipient).find(
          (candidate) => text(candidate?.id || candidate?.contentId) === contentId,
        );
        if (targetStop && targetRecipient && targetCargo) {
          matchedCargo = { stop: targetStop, recipient: targetRecipient, cargo: targetCargo };
        }

        let loadedCargoState = null;
        let loadedExceptions = [];
        if (matchedCargo) {
          const stateRef = doc(collection(jobRef, "cargoStates"), contentId);
          const [stateSnapshot, exceptionSnapshot] = await Promise.all([
            getDoc(stateRef),
            getDocs(collection(stateRef, "exceptions")),
          ]);
          loadedCargoState = stateSnapshot.exists() ? stateSnapshot.data() || {} : null;
          loadedExceptions = exceptionSnapshot.docs
            .map((snapshot) => ({ exceptionId: snapshot.id, ...(snapshot.data() || {}) }))
            .sort((left, right) =>
              (toDateSafe(right?.occurredAt)?.getTime() || 0) -
              (toDateSafe(left?.occurredAt)?.getTime() || 0),
            );
        }
        if (active) {
          setDetail({ job, ...(matchedCargo || {}) });
          setCargoState(loadedCargoState);
          setExceptions(loadedExceptions);
        }
      } catch (loadError) {
        console.error("[DriverManagerReports] cargo detail error:", loadError);
        if (active) {
          setError(
            loadError?.code === "permission-denied"
              ? "目前帳號沒有讀取此工作貨物詳情的權限。"
              : loadError?.message === "cargo_detail_job_not_found"
                ? "找不到這筆貨物關聯的工作資料。"
                : "貨物詳情讀取失敗，請稍後再試。",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCargoDetail();
    return () => {
      active = false;
    };
  }, [orgId, workDateKey, applicationId, stopId, recipientId, contentId]);

  const cargo = detail?.cargo || null;
  const recipient = detail?.recipient || null;
  const stop = detail?.stop || null;
  const job = detail?.job || null;
  const operationType = text(
    cargo?.operationType || recipient?.operationType || recipient?.operation || report?.operationType,
  ).toUpperCase() || (text(report?.recipientRole).toLowerCase() === "sender" ? "PICKUP" : "DELIVERY");
  const stateMeta = cargoExecutionPresentation(cargoState?.status, operationType);
  const cargoName = text(cargo?.name || cargo?.cargoName || report?.cargoName) || "未命名貨物";
  const quantity = cargo?.quantity ?? report?.quantity;
  const unit = text(cargo?.unit || report?.unit);
  const contactName = text(recipient?.name || recipient?.recipientName || report?.recipientName) || "未指定聯絡人";
  const companyName = text(recipient?.companyName || recipient?.company || report?.recipientCompany);
  const contactPhone = text(recipient?.phone || recipient?.mobile || report?.recipientPhone);
  const stopAddress = cargoAddressText(
    stop?.addressText || stop?.formattedAddress || stop?.address || stop?.location,
  ) || cargoAddressText(report?.addressText || report?.address || report?.location);
  const jobTitle = text(job?.jobTitle || job?.title || job?.name || reportJobTitle(report) || report?.jobName) || "工作詳情";
  const stopOrder = Number(stop?.order ?? stop?.stopOrder ?? report?.stopOrder) || 0;
  const driverText = text(job?.driverName || job?.assignedDriverName || job?.driver?.name) || reportDriverName(report);
  const vehicleText = reportVehicleText({
    vehicle: job?.vehicle || report?.vehicle,
    vehiclePlate: job?.vehiclePlate || report?.vehiclePlate,
    plateNo: job?.plateNo || report?.plateNo,
  });
  const cargoOriginalPhotos = cargoMediaReport(cargo?.photos);
  const executionPhotos = cargoMediaReport(cargo?.execution?.photos);
  const signature = recipient?.proof?.signature;
  const signatureAssetId = typeof signature === "string" ? text(signature) : text(signature?.assetId);
  const signatureReport = cargoMediaReport(signatureAssetId ? [{ assetId: signatureAssetId, contentType: "image/png" }] : []);
  const normalizedCargoStatus = text(cargoState?.status).toUpperCase();
  const hasSignature = Boolean(
    signatureAssetId || signature?.storagePath || signature?.originalUrl || signature?.url || signature?.signedAt,
  );
  const timelineActiveIndex = normalizedCargoStatus === "COMPLETED"
    ? CARGO_EXECUTION_TIMELINE.length - 1
    : hasSignature
      ? 2
      : normalizedCargoStatus === "IN_PROGRESS" || (normalizedCargoStatus === "EXCEPTION" && cargoState?.startedAt)
        ? 1
        : 0;
  const cargoNumber = text(cargo?.cargoNumber || cargo?.cargoId || report?.cargoNumber || contentId);

  if (!report) return null;

  return (
    <div
      className="dmr-cargo-detail-layer"
      role="dialog"
      aria-modal="true"
      aria-label="貨物詳情"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside className="dmr-cargo-detail-drawer" onMouseDown={(event) => event.stopPropagation()}>
        <header className="dmr-cargo-detail-header">
          <div className="dmr-cargo-detail-heading">
            <div>
              <h2>{cargoName}</h2>
              <p>{quantity ?? "—"}{unit ? ` ${unit}` : ""} · {operationType === "PICKUP" ? "寄件貨物" : "收件貨物"}</p>
            </div>
          </div>
          <button type="button" className="dmr-icon-button" onClick={onClose} aria-label="關閉貨物詳情">
            <Icon name="close" size={19} />
          </button>
        </header>

        <div className="dmr-cargo-detail-body">
          {loading ? (
            <div className="dmr-cargo-detail-loading"><span className="dmr-spinner" /><p>正在讀取貨物與執行證明…</p></div>
          ) : null}
          {error ? <div className="dmr-cargo-detail-error" role="alert"><Icon name="alert" size={17} />{error}</div> : null}
          {!loading && !error ? (
            <>
              <section className="dmr-cargo-state-card">
                <div className="dmr-cargo-state-overview">
                  <div>
                    <strong className={`dmr-cargo-state-text dmr-cargo-state-${stateMeta.tone}`}>{stateMeta.label}</strong>
                  </div>
                  {normalizedCargoStatus === "EXCEPTION" ? <span className="dmr-cargo-exception-state-pill">異常</span> : null}
                </div>
                <div className="dmr-cargo-flow" role="list" aria-label="貨物執行進度">
                  {CARGO_EXECUTION_TIMELINE.map((label, index) => {
                    const skipped = normalizedCargoStatus === "COMPLETED" && !hasSignature && index === 2;
                    const current = index === timelineActiveIndex;
                    const done = !skipped && (
                      index < timelineActiveIndex ||
                      (normalizedCargoStatus === "COMPLETED" && index === timelineActiveIndex)
                    );
                    const stepClassName = [
                      "dmr-cargo-flow-step",
                      index <= timelineActiveIndex ? "is-line-complete" : "",
                      current ? "is-current" : "",
                      done ? "is-done" : "",
                      skipped ? "is-skipped" : "",
                      current && normalizedCargoStatus === "EXCEPTION" ? "is-exception" : "",
                    ].filter(Boolean).join(" ");
                    return (
                      <div className={stepClassName} key={label} role="listitem" aria-current={current ? "step" : undefined}>
                        <span className="dmr-cargo-flow-node">{done ? <Icon name="check" size={13} /> : skipped ? "–" : index + 1}</span>
                        <span className="dmr-cargo-flow-label">{label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="dmr-cargo-state-times">
                  {cargoState?.startedAt ? <span>開始 {fmtDateTime(cargoState.startedAt)}</span> : null}
                  {hasSignature ? <span>已簽收{signature?.signedAt ? ` ${fmtDateTime(signature.signedAt)}` : ""}</span> : null}
                  {cargoState?.completedAt ? <span>完成 {fmtDateTime(cargoState.completedAt)}</span> : null}
                  {stateMeta.label === "異常" && exceptions[0]?.occurredAt ? <span>最近異常 {fmtDateTime(exceptions[0].occurredAt)}</span> : null}
                </div>
              </section>

              {cargoOriginalPhotos.photos.length ? (
                <ReportMediaSection report={cargoOriginalPhotos} sectionTitle="貨物照片" />
              ) : null}

              {executionPhotos.photos.length ? (
                <ReportMediaSection report={executionPhotos} sectionTitle="現場照片" />
              ) : null}

              {hasSignature ? (
                <section className="dmr-cargo-detail-section dmr-cargo-signature-section">
                  <div className="dmr-cargo-section-title"><span>客戶簽名</span><small>聯絡人／站點層級</small></div>
                  {signatureAssetId ? (
                    <ReportMediaSection
                      report={signatureReport}
                      sectionTitle="客戶簽名"
                      sectionClassName="dmr-cargo-signature-media"
                      showHeading={false}
                    />
                  ) : null}
                  {signature?.signedAt ? <small className="dmr-cargo-signature-time">簽收時間：{fmtDateTime(signature.signedAt)}</small> : null}
                </section>
              ) : null}

              {exceptions.length ? (
                <section className="dmr-cargo-detail-section dmr-cargo-exceptions-section">
                  <div className="dmr-cargo-section-title"><span>異常歷程</span><small>{exceptions.length} 筆</small></div>
                  {exceptions.map((exception, index) => {
                  const mediaReport = cargoMediaReport(exception?.mediaAssetIds);
                  return (
                    <article className="dmr-cargo-exception" key={exception.exceptionId || `${contentId}-exception-${index}`}>
                      <div className="dmr-cargo-exception-meta"><strong>{fmtDateTime(exception.occurredAt || exception.createdAt)}</strong><span>異常</span></div>
                      <p>{text(exception.reason) || "未填寫異常說明"}</p>
                      <ReportMediaSection report={mediaReport} sectionTitle="異常照片" />
                    </article>
                  );
                  })}
                </section>
              ) : null}

              {!cargo ? <div className="dmr-cargo-detail-warning">工作內找不到與這筆報表完全相符的站點、聯絡人和貨物資料；為避免顯示錯件，僅保留報表快照。</div> : null}

              <section className="dmr-cargo-detail-section">
                <div className="dmr-cargo-section-title"><span>聯絡人</span><small>{operationType === "PICKUP" ? "寄件人" : "收件人"}</small></div>
                <strong className="dmr-cargo-contact-name">{contactName}</strong>
                {companyName ? <span className="dmr-cargo-contact-company">{companyName}</span> : null}
                {contactPhone ? <a className="dmr-cargo-contact-phone" href={`tel:${contactPhone}`}>{contactPhone}</a> : null}
              </section>

              <section className="dmr-cargo-detail-section">
                <div className="dmr-cargo-section-title"><span>工作與站點</span><small>{workDateKey || "—"}</small></div>
                <strong className="dmr-cargo-job-title">{jobTitle}</strong>
                <div className="dmr-cargo-context-grid">
                  <div><small>成員</small><strong>{driverText}</strong></div>
                  <div><small>車輛</small><strong>{vehicleText || "—"}</strong></div>
                  <div><small>站點</small><strong>{stopOrder ? `第 ${stopOrder} 站` : text(stop?.name || report?.stopName) || "—"}</strong></div>
                  <div className="dmr-cargo-context-address"><small>地址</small><strong>{stopAddress || "—"}</strong></div>
                </div>
                <div className="dmr-cargo-number-row"><span>貨物編號</span><strong>{cargoNumber || "—"}</strong></div>
              </section>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function CargoRouteMapViewport({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    if (positions.length === 1) {
      map.setView(positions[0], 15);
      return;
    }
    const bounds = L.latLngBounds(positions);
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    if (Math.abs(northEast.lat - southWest.lat) < 0.0001 && Math.abs(northEast.lng - southWest.lng) < 0.0001) {
      map.setView(positions[0], 15);
      return;
    }
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
  }, [map, positions]);
  return null;
}

function cargoRouteGradientColor(progress) {
  const safeProgress = Math.max(0, Math.min(1, Number(progress) || 0));
  const start = [37, 99, 235];
  const end = [239, 68, 68];
  return `rgb(${start.map((channel, index) => Math.round(channel + (end[index] - channel) * safeProgress)).join(",")})`;
}

function buildCargoRouteGradientSegments(points) {
  const edgeCount = points.length - 1;
  if (edgeCount < 1) return [];
  const segmentCount = Math.min(64, Math.max(16, edgeCount));
  const positionAtProgress = (progress) => {
    const pointPosition = Math.max(0, Math.min(edgeCount, progress * edgeCount));
    if (pointPosition >= edgeCount) {
      const last = points[points.length - 1];
      return [last.lat, last.lng];
    }
    const leftIndex = Math.floor(pointPosition);
    const rightIndex = Math.min(edgeCount, leftIndex + 1);
    const segmentProgress = pointPosition - leftIndex;
    const left = points[leftIndex];
    const right = points[rightIndex];
    return [
      left.lat + (right.lat - left.lat) * segmentProgress,
      left.lng + (right.lng - left.lng) * segmentProgress,
    ];
  };
  return Array.from({ length: segmentCount }, (_, index) => {
    const startProgress = index / segmentCount;
    const endProgress = (index + 1) / segmentCount;
    const startPosition = startProgress * edgeCount;
    const endPosition = endProgress * edgeCount;
    const segmentPositions = [positionAtProgress(startProgress)];
    for (let pointIndex = Math.floor(startPosition) + 1; pointIndex < endPosition; pointIndex += 1) {
      segmentPositions.push([points[pointIndex].lat, points[pointIndex].lng]);
    }
    segmentPositions.push(positionAtProgress(endProgress));
    return {
      positions: segmentPositions,
      color: cargoRouteGradientColor((index + 0.5) / segmentCount),
    };
  });
}

function cargoRoutePlaybackPoint(points, progress) {
  if (!points.length) return null;
  if (points.length === 1) return points[0];
  const safeProgress = Math.max(0, Math.min(1, Number(progress) || 0));
  const first = points[0];
  const last = points[points.length - 1];
  const targetAt = first.observedAt + (last.observedAt - first.observedAt) * safeProgress;
  if (targetAt <= first.observedAt) return first;
  for (let index = 1; index < points.length; index += 1) {
    const left = points[index - 1];
    const right = points[index];
    if (targetAt > right.observedAt) continue;
    const duration = right.observedAt - left.observedAt;
    const segmentProgress = duration > 0 ? (targetAt - left.observedAt) / duration : 1;
    return {
      ...right,
      observedAt: targetAt,
      lat: left.lat + (right.lat - left.lat) * segmentProgress,
      lng: left.lng + (right.lng - left.lng) * segmentProgress,
    };
  }
  return last;
}

function CargoRouteMap({ points }) {
  const trackPoints = useMemo(
    () => (Array.isArray(points) ? points : [])
      .filter((point) => Number.isFinite(Number(point?.observedAt)) &&
        Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
      .map((point) => ({ ...point, observedAt: Number(point.observedAt), lat: Number(point.lat), lng: Number(point.lng) }))
      .sort((left, right) => left.observedAt - right.observedAt),
    [points],
  );
  const positions = useMemo(() => trackPoints.map((point) => [point.lat, point.lng]), [trackPoints]);
  const gradientSegments = useMemo(() => buildCargoRouteGradientSegments(trackPoints), [trackPoints]);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [isPlaybackPlaying, setIsPlaybackPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const firstAt = trackPoints[0]?.observedAt || 0;
  const lastAt = trackPoints[trackPoints.length - 1]?.observedAt || firstAt;
  const playbackPoint = useMemo(
    () => cargoRoutePlaybackPoint(trackPoints, playbackProgress),
    [playbackProgress, trackPoints],
  );

  useEffect(() => {
    setPlaybackProgress(0);
    setIsPlaybackPlaying(false);
  }, [firstAt, lastAt]);

  useEffect(() => {
    if (!isPlaybackPlaying || trackPoints.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setPlaybackProgress((current) => {
        const next = Math.min(1, current + 0.004 * playbackSpeed);
        if (next >= 1) setIsPlaybackPlaying(false);
        return next;
      });
    }, 50);
    return () => window.clearInterval(timer);
  }, [isPlaybackPlaying, playbackSpeed, trackPoints.length]);

  if (!positions.length) return null;
  const seekPlaybackBySeconds = (seconds) => {
    if (trackPoints.length < 2 || lastAt <= firstAt) return;
    const currentAt = firstAt + (lastAt - firstAt) * playbackProgress;
    const nextAt = Math.max(firstAt, Math.min(lastAt, currentAt + seconds * 1000));
    setIsPlaybackPlaying(false);
    setPlaybackProgress((nextAt - firstAt) / (lastAt - firstAt));
  };

  return (
    <div className="dmr-cargo-route-map-panel">
      <div className="dmr-cargo-route-map">
        <MapContainer center={positions[0]} zoom={13} scrollWheelZoom style={{ width: "100%", height: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <CargoRouteMapViewport positions={positions} />
          {gradientSegments.map((segment, index) => (
            <Polyline
              key={`cargo-route-gradient-${index}`}
              positions={segment.positions}
              pathOptions={{ color: segment.color, weight: 6, opacity: 0.9, lineCap: "round", lineJoin: "round" }}
            />
          ))}
          <CircleMarker center={positions[0]} radius={8} pathOptions={{ color: "#FFFFFF", weight: 3, fillColor: "#2563EB", fillOpacity: 1 }}>
            <Tooltip direction="top" offset={[0, -7]}>開始</Tooltip>
          </CircleMarker>
          {positions.length > 1 ? (
            <CircleMarker center={positions[positions.length - 1]} radius={8} pathOptions={{ color: "#FFFFFF", weight: 3, fillColor: "#EF4444", fillOpacity: 1 }}>
              <Tooltip direction="top" offset={[0, -7]}>抵達</Tooltip>
            </CircleMarker>
          ) : null}
          {playbackPoint ? (
            <CircleMarker center={[playbackPoint.lat, playbackPoint.lng]} radius={9} pathOptions={{ color: "#FFFFFF", weight: 4, fillColor: "#172B4D", fillOpacity: 1 }}>
              <Tooltip direction="top" offset={[0, -9]} permanent={false}>播放位置 · {formatTaipeiDateTime(playbackPoint.observedAt)}</Tooltip>
            </CircleMarker>
          ) : null}
        </MapContainer>
      </div>
      <div className="dmr-cargo-route-playback" aria-label="貨物路線時間軸">
        <div className="dmr-cargo-route-playback-controls">
          <button type="button" className="dmr-cargo-route-seek" onClick={() => seekPlaybackBySeconds(-10)} disabled={trackPoints.length < 2}>↶10 秒</button>
          <button
            type="button"
            className="dmr-cargo-route-play"
            onClick={() => {
              if (trackPoints.length < 2) return;
              if (playbackProgress >= 1) setPlaybackProgress(0);
              setIsPlaybackPlaying((playing) => !playing);
            }}
            disabled={trackPoints.length < 2}
          >
            {isPlaybackPlaying ? "暫停" : "播放"}
          </button>
          <button type="button" className="dmr-cargo-route-seek" onClick={() => seekPlaybackBySeconds(10)} disabled={trackPoints.length < 2}>10 秒↷</button>
          <label className="dmr-cargo-route-speed">速度
            <select value={playbackSpeed} onChange={(event) => setPlaybackSpeed(Number(event.target.value))} aria-label="播放速度">
              <option value="1">1x</option>
              <option value="2">2x</option>
              <option value="4">4x</option>
            </select>
          </label>
        </div>
        <div className="dmr-cargo-route-playback-times">
          <span><i className="is-start" />{firstAt ? formatTaipeiDateTime(firstAt) : "—"}</span>
          <strong>{playbackPoint ? formatTaipeiDateTime(playbackPoint.observedAt) : "—"}</strong>
          <span><i className="is-end" />{lastAt ? formatTaipeiDateTime(lastAt) : "—"}</span>
        </div>
        <input
          className="dmr-cargo-route-range"
          type="range"
          min="0"
          max="1"
          step="any"
          value={playbackProgress}
          onChange={(event) => {
            setIsPlaybackPlaying(false);
            setPlaybackProgress(Number(event.target.value));
          }}
          disabled={trackPoints.length < 2}
          aria-label="貨物路線播放進度"
        />
      </div>
    </div>
  );
}

function CargoRouteDrawer({ report, orgId, onClose }) {
  const workDateKey = text(report?.workDateKey || report?.dateKey);
  const applicationId = reportApplicationId(report);
  const stopId = text(report?.stopId);
  const recipientId = text(report?.recipientId);
  const contentId = text(report?.contentId);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setRoute(null);
    setLoading(true);
    setError("");

    const loadCargoRoute = async () => {
      const missingReason = cargoDetailMissingReason(report);
      if (!orgId || missingReason) {
        if (active) {
          setError(!orgId
            ? "目前帳號尚未取得組織資料，無法載入路線。"
            : `這筆貨物缺少${missingReason}，無法安全定位所屬工作與站點。`);
          setLoading(false);
        }
        return;
      }

      try {
        const jobRef = doc(db, "orgs", orgId, "applications", workDateKey, "jobs", applicationId);
        const jobSnapshot = await getDoc(jobRef);
        if (!jobSnapshot.exists()) throw new Error("找不到這筆貨物關聯的工作資料。");
        const job = { id: jobSnapshot.id, ...(jobSnapshot.data() || {}) };
        const stops = Array.isArray(job.stops) ? job.stops : [];
        const stop = stops.find((candidate) => text(candidate?.id || candidate?.stopId) === stopId);
        const recipients = Array.isArray(stop?.recipients) ? stop.recipients : [];
        const recipient = recipients.find((candidate) => text(candidate?.recipientId || candidate?.id) === recipientId);
        const cargo = cargoRecipientItems(recipient).find((candidate) => text(candidate?.id || candidate?.contentId) === contentId);
        if (!stop || !recipient || !cargo) {
          throw new Error("工作內找不到與這筆報表完全相符的貨物，為避免顯示錯誤路線，已停止查詢。");
        }

        const driverUid = text(
          job?.driver?.uid ||
          job?.driverUid ||
          job?.assignedDriverUid ||
          job?.driver?.id ||
          job?.assignedDriver?.uid ||
          job?.assignedDriver?.id ||
          job?.driverId ||
          job?.assignedDriverId,
        );
        if (!driverUid) throw new Error("這筆工作的派遣資料沒有成員 UID，無法安全讀取成員軌跡。");
        const orderedStops = [...stops].sort((left, right) => Number(left?.order || 0) - Number(right?.order || 0));
        const stopIndex = orderedStops.findIndex((candidate) => text(candidate?.id || candidate?.stopId) === stopId);
        const currentUser = auth.currentUser;
        if (!currentUser?.getIdToken) throw new Error("登入狀態已失效，請重新登入後再查看路線。");
        const token = await currentUser.getIdToken();
        const eventsEndpoint = `${TRACKING_HISTORY_API_URL}/application-events`;
        let eventsResponse;
        try {
          eventsResponse = await fetch(eventsEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ orgId, driverUid, applicationId, workDateKey }),
          });
        } catch (requestError) {
          console.error("[DriverManagerReports] cargo route application-events network error", {
            endpoint: eventsEndpoint,
            applicationId,
            workDateKey,
            message: text(requestError?.message || requestError),
          });
          throw requestError;
        }
        const eventsResponseText = await eventsResponse.text().catch(() => "");
        let eventsPayload = {};
        try {
          eventsPayload = eventsResponseText ? JSON.parse(eventsResponseText) : {};
        } catch (_parseError) {
          eventsPayload = { rawResponse: eventsResponseText };
        }
        if (!eventsResponse.ok || eventsPayload?.ok !== true) {
          const apiError = text(eventsPayload?.error || eventsPayload?.code || eventsPayload?.message);
          console.error("[DriverManagerReports] cargo route application-events API response error", {
            endpoint: eventsEndpoint,
            httpStatus: eventsResponse.status,
            statusText: eventsResponse.statusText,
            apiError: apiError || null,
            responsePreview: apiError ? undefined : text(eventsPayload?.rawResponse).slice(0, 500) || null,
            applicationId,
            workDateKey,
          });
          const knownErrors = {
            history_access_denied: "目前帳號沒有查看此成員軌跡的權限。",
            application_not_found: "找不到這筆貨物關聯的工作資料。",
            application_driver_mismatch: "工作指派成員與軌跡所屬成員不一致，為避免顯示錯誤路線，已停止查詢。",
            missing_bearer_token: "登入狀態已失效，請重新登入後再查看路線。",
          };
          throw new Error(knownErrors[eventsPayload?.error] || "無法讀取此工作的站點事件，請稍後再試。");
        }
        const dedupedEvents = new Map();
        (Array.isArray(eventsPayload.events) ? eventsPayload.events : []).forEach((raw) => {
          const observedAt = toDateSafe(raw?.observedAt)?.getTime() || Number(raw?.observedAt) || 0;
          const eventType = text(raw?.eventType);
          if (
            text(raw?.applicationId) !== applicationId ||
            text(raw?.workDateKey) !== workDateKey ||
            !eventType ||
            !observedAt
          ) return;
          const event = { ...raw, observedAt };
          const dedupeKey = text(raw?.actionId || raw?.eventId) ||
            [applicationId, text(raw?.stopId), eventType, observedAt].join("|");
          const previous = dedupedEvents.get(dedupeKey);
          if (!previous) {
            dedupedEvents.set(dedupeKey, event);
            return;
          }
          const preferred = event.source === "application" && previous.source !== "application" ? event : previous;
          const supplementary = preferred === previous ? event : previous;
          dedupedEvents.set(dedupeKey, {
            ...supplementary,
            ...preferred,
            lat: preferred.lat ?? supplementary.lat,
            lng: preferred.lng ?? supplementary.lng,
          });
        });
        const jobEvents = [...dedupedEvents.values()].sort((left, right) => left.observedAt - right.observedAt);
        const targetEvents = jobEvents.filter((event) => text(event.stopId) === stopId);
        const targetCompletedEvents = targetEvents.filter((event) => event.eventType === "station_completed");
        const targetCompletedEvent = targetCompletedEvents.at(-1) || null;
        const targetStartedEvents = targetEvents.filter((event) => event.eventType === "station_started");
        const targetStartedBeforeCompletion = targetCompletedEvent
          ? targetStartedEvents.filter((event) => event.observedAt <= targetCompletedEvent.observedAt)
          : targetStartedEvents;
        const targetStartedEvent = targetStartedBeforeCompletion.at(-1) || null;
        const progressStartedAt = toDateSafe(stop.progress?.startedAt)?.getTime() || 0;
        const progressCompletedAt = toDateSafe(stop.progress?.completedAt)?.getTime() || 0;
        const routeStartAt = targetStartedEvent?.observedAt || progressStartedAt;
        const routeEndAt = targetCompletedEvent?.observedAt || progressCompletedAt;
        const eventStationNumber = [
          targetStartedEvent?.stationNumber,
          targetCompletedEvent?.stationNumber,
        ].map(Number).find((value) => Number.isInteger(value) && value > 0);
        const storedStopOrder = Number(stop.order ?? stop.stopOrder);
        const fallbackStationNumber = Number.isInteger(storedStopOrder) && storedStopOrder > 0
          ? storedStopOrder
          : stopIndex + 1;
        const targetStationNumber = eventStationNumber || fallbackStationNumber;
        if (!routeStartAt || !routeEndAt || routeEndAt <= routeStartAt) {
          console.error("[DriverManagerReports] cargo route invalid same-stop time window", {
            applicationId,
            stopId,
            stationNumber: targetStationNumber,
            stationStartedEventAt: targetStartedEvent?.observedAt || null,
            stationCompletedEventAt: targetCompletedEvent?.observedAt || null,
            progressStartedAt: progressStartedAt || null,
            progressCompletedAt: progressCompletedAt || null,
            routeStartAt: routeStartAt || null,
            routeEndAt: routeEndAt || null,
          });
          throw new Error("找不到同一站點有效的開始與完成時間，無法查詢本站 GPS 軌跡。");
        }
        const requests = trackingRangesByTaipeiDay(routeStartAt, routeEndAt).map(async (range) => {
          const response = await fetch(`${TRACKING_HISTORY_API_URL}/range`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ orgId, driverUid, dateKey: range.dateKey, startAt: range.startAt, endAt: range.endAt }),
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload?.ok !== true) {
            const knownErrors = {
              history_access_denied: "目前帳號沒有查看此成員軌跡的權限。",
              driver_org_mismatch: "成員與貨物工作不屬於同一組織，無法載入路線。",
              missing_bearer_token: "登入狀態已失效，請重新登入後再查看路線。",
            };
            throw new Error(knownErrors[payload?.error] || "成員軌跡讀取失敗，請稍後再試。");
          }
          return Array.isArray(payload.points) ? payload.points : [];
        });
        const rawPoints = (await Promise.all(requests)).flat().filter((point) =>
          !text(point?.applicationId) || text(point.applicationId) === applicationId,
        );
        const keyedPoints = rawPoints.map((point) => {
          const observedAt = Number(point?.observedAt);
          const lat = Number(point?.lat);
          const lng = Number(point?.lng);
          if (!Number.isFinite(observedAt) || !Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
          return [`${observedAt}|${lat}|${lng}`, { observedAt, lat, lng, speed: Number.isFinite(Number(point?.speed)) ? Number(point.speed) : null }];
        }).filter(Boolean);
        const points = [...new Map(keyedPoints).values()]
          .sort((left, right) => left.observedAt - right.observedAt);

        const stopOrder = Number(stop.order || stop.stopOrder) || orderedStops.findIndex((candidate) => text(candidate?.id || candidate?.stopId) === stopId) + 1;
        const routeInfo = {
          job,
          stop,
          recipient,
          cargo,
          driverUid,
          driverName: text(job?.driverName || job?.assignedDriverName || job?.driver?.name) || reportDriverName(report),
          startedAt: routeStartAt,
          completedAt: routeEndAt,
          points,
          stopOrder,
        };
        if (active) setRoute(routeInfo);
      } catch (loadError) {
        console.error("[DriverManagerReports] cargo route error:", loadError);
        if (active) setError(loadError?.message || "成員軌跡讀取失敗，請稍後再試。");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadCargoRoute();
    return () => { active = false; };
  }, [orgId, workDateKey, applicationId, stopId, recipientId, contentId]);

  if (!report) return null;
  const cargoName = text(route?.cargo?.name || route?.cargo?.cargoName || report?.cargoName) || "貨物路線";
  const jobTitle = text(route?.job?.jobTitle || route?.job?.title || route?.job?.name || reportJobTitle(report) || report?.jobName) || "工作詳情";
  const stopName = text(route?.stop?.text || route?.stop?.name || route?.stop?.address || report?.stopName) || "所屬站點";
  const durationMinutes = route ? Math.max(0, Math.round((route.completedAt - route.startedAt) / 60000)) : 0;
  const durationText = durationMinutes >= 60
    ? `${Math.floor(durationMinutes / 60)} 小時 ${durationMinutes % 60} 分鐘`
    : `${durationMinutes} 分鐘`;
  const exportRoutePdf = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      window.alert("匯出視窗遭瀏覽器封鎖，請允許此網站開啟彈出視窗後再試。" );
      return;
    }

    const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    })[character]);
    const mapElement = document.querySelector(".dmr-cargo-route-map .leaflet-container");
    let mapMarkup = '<div class="map-placeholder">目前地圖尚未載入，請回到路線頁等待地圖完成後再匯出。</div>';
    if (mapElement) {
      const mapRect = mapElement.getBoundingClientRect();
      const mapWidth = Math.max(1, Math.round(mapRect.width));
      const mapHeight = Math.max(1, Math.round(mapRect.height));
      const mapClone = mapElement.cloneNode(true);
      const sourceNodes = [mapElement, ...mapElement.querySelectorAll("*")];
      const cloneNodes = [mapClone, ...mapClone.querySelectorAll("*")];
      sourceNodes.forEach((sourceNode, index) => {
        const computedStyle = window.getComputedStyle(sourceNode);
        for (let styleIndex = 0; styleIndex < computedStyle.length; styleIndex += 1) {
          const property = computedStyle.item(styleIndex);
          cloneNodes[index].style.setProperty(property, computedStyle.getPropertyValue(property));
        }
      });
      mapClone.querySelector(".leaflet-control-container")?.remove();
      mapClone.style.position = "relative";
      mapClone.style.width = `${mapWidth}px`;
      mapClone.style.height = `${mapHeight}px`;
      mapClone.style.overflow = "hidden";
      mapMarkup = `<div class="map-frame"><div class="map-snapshot" data-source-width="${mapWidth}" data-source-height="${mapHeight}" style="width:${mapWidth}px;height:${mapHeight}px;transform-origin:top left">${mapClone.outerHTML}</div></div>`;
    }

    const operationText = text(report?.operationType).toUpperCase() === "PICKUP" ? "寄件貨物" : "收件貨物";
    const pointRows = route.points.map((point, index) => {
      const speedValue = point.speed === null || point.speed === undefined ? null : Number(point.speed);
      const speedText = Number.isFinite(speedValue)
        ? `${Math.round(Math.max(0, speedValue) * 3.6)} km/h`
        : "—";
      return `<tr><td>${index + 1}</td><td>${escapeHtml(formatTaipeiDateTime(point.observedAt))}</td><td>${Number(point.lat).toFixed(6)}</td><td>${Number(point.lng).toFixed(6)}</td><td>${speedText}</td></tr>`;
    }).join("");
    const reportHtml = `<!doctype html>
      <html lang="zh-Hant"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
      <title>${escapeHtml(cargoName)}｜貨物 GPS 路線報告</title>
      <style>
        @page{size:A4 landscape;margin:0}
        *{box-sizing:border-box}
        body{margin:0;padding:24px;background:#f1f4f7;color:#263541;font-family:Arial,"Microsoft JhengHei","PingFang TC",sans-serif;font-size:12px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        .report{width:297mm;max-width:none;margin:0 auto;padding:0;background:#fff}
        .report-head{display:flex;flex:0 0 auto;align-items:flex-start;justify-content:space-between;gap:20px;padding-bottom:7px;border-bottom:2px solid #263541}
        h1{margin:0 0 3px;font-size:20px}.subtitle{color:#72808c;font-size:10px}
        .report-brand{color:#89939d;font-size:10px;font-weight:700;text-align:right}
        .summary-grid{display:grid;flex:0 0 auto;grid-template-columns:repeat(3,minmax(0,1fr));gap:7px;margin:8px 0}
        .summary-item{padding:7px 9px;border:1px solid #e4e9ee;border-radius:7px;background:#fbfcfd}
        .summary-item small{display:block;margin-bottom:5px;color:#87939d;font-size:10px}.summary-item strong{color:#344955;font-size:12px;overflow-wrap:anywhere}
        .time-grid{display:grid;flex:0 0 auto;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin:0 0 8px}
        .time-item{padding:7px 9px;border-left:3px solid #3973ce;background:#f6f8fb}.time-item:nth-child(2){border-color:#ef4444}.time-item:nth-child(3){border-color:#e77b27}
        .time-item small{display:block;margin-bottom:5px;color:#87939d;font-size:10px}.time-item strong{font-size:12px}
        .map-page{display:flex;flex-direction:column;gap:0;width:297mm;height:210mm;padding:7mm;overflow:hidden;break-after:page;page-break-after:always;background:#fff}
        .map-region{position:relative;flex:1 1 auto;min-height:0;overflow:hidden;border:1px solid #dfe5ea;border-radius:7px;background:#edf1f4}
        .map-frame{position:absolute;inset:0;width:100%;height:100%;overflow:hidden;border:0;border-radius:0;background:#edf1f4}
        .map-snapshot{position:absolute;left:0;top:0}.map-region .map-placeholder{position:absolute;inset:0;display:grid;place-items:center;border:0;background:#f8fafb;color:#74818d}
        .map-region-title{position:absolute;top:8px;left:8px;z-index:2;padding:4px 7px;border-radius:5px;background:rgba(255,255,255,.9);color:#425466;font-size:10px;font-weight:700}
        .map-attribution{position:absolute;right:6px;bottom:5px;z-index:2;padding:3px 6px;border-radius:4px;background:rgba(255,255,255,.9);color:#77838d;font-size:9px;text-align:right}
        .report-details{padding:12mm;background:#fff}
        h2{margin:18px 0 9px;font-size:15px}
        table{width:100%;border-collapse:collapse;font-size:10px}thead{display:table-header-group}th,td{padding:6px 8px;border:1px solid #e4e9ee;text-align:left;font-variant-numeric:tabular-nums}th{background:#edf2f6;color:#425466;font-weight:800}tbody tr:nth-child(even){background:#fafbfc}tr{break-inside:avoid;page-break-inside:avoid}
        .toolbar{position:sticky;top:0;z-index:20;display:flex;justify-content:flex-end;margin:-12px -12px 16px;padding:8px;background:rgba(255,255,255,.96)}
        .toolbar button{padding:9px 15px;border:0;border-radius:8px;background:#e77b27;color:#fff;font-size:12px;font-weight:800;cursor:pointer}
        @media print{body{margin:0;padding:0;background:#fff}.report{width:297mm;max-width:none;margin:0;padding:0}.toolbar{display:none}.map-page{width:297mm;height:210mm}h2{break-after:avoid}}
      </style></head><body><main class="report">
        <div class="toolbar"><button type="button" onclick="window.print()">列印／另存為 PDF</button></div>
        <section class="map-page">
        <header class="report-head"><div><h1>${escapeHtml(cargoName)}｜GPS 路線報告</h1><div class="subtitle">${escapeHtml(workDateKey)} · ${escapeHtml(jobTitle)}</div></div><div class="report-brand">Tallarge<br>貨物運輸軌跡</div></header>
        <section class="summary-grid">
          <div class="summary-item"><small>貨物</small><strong>${escapeHtml(cargoName)} · ${escapeHtml(text(report?.quantity) || "—")}${escapeHtml(text(report?.unit) ? ` ${text(report.unit)}` : "")} · ${escapeHtml(operationText)}</strong></div>
          <div class="summary-item"><small>成員</small><strong>${escapeHtml(route.driverName || "—")}</strong></div>
          <div class="summary-item"><small>站點</small><strong>第 ${escapeHtml(route.stopOrder)} 站 · ${escapeHtml(stopName)}</strong></div>
        </section>
        <section class="time-grid">
          <div class="time-item"><small>開始時間</small><strong>${escapeHtml(formatTaipeiDateTime(route.startedAt))}</strong></div>
          <div class="time-item"><small>結束時間</small><strong>${escapeHtml(formatTaipeiDateTime(route.completedAt))}</strong></div>
          <div class="time-item"><small>行程時間</small><strong>${escapeHtml(durationText)}</strong></div>
        </section>
        <div class="map-region">
          <div class="map-region-title">GPS 路線地圖</div>
          ${mapMarkup}
          <div class="map-attribution">© OpenStreetMap contributors</div>
        </div>
        </section>
        <section class="report-details">
        <h2>定位點明細</h2>
        <table><thead><tr><th>序號</th><th>時間（台北）</th><th>緯度</th><th>經度</th><th>速度</th></tr></thead><tbody>${pointRows}</tbody></table>
        </section>
      </main><script>
        window.addEventListener("load",function(){
          const images=Array.from(document.images);
          const pending=images.map(function(image){return new Promise(function(resolve){
            if(image.complete){resolve();return;}
            const timer=window.setTimeout(resolve,5000);
            image.addEventListener("load",function(){window.clearTimeout(timer);resolve();},{once:true});
            image.addEventListener("error",function(){window.clearTimeout(timer);resolve();},{once:true});
          });});
          Promise.all(pending).then(function(){
            const mapFrame=document.querySelector(".map-frame");
            const mapSnapshot=document.querySelector(".map-snapshot");
            if(mapFrame&&mapSnapshot){
              const sourceWidth=Number(mapSnapshot.dataset.sourceWidth);
              const sourceHeight=Number(mapSnapshot.dataset.sourceHeight);
              const frameRect=mapFrame.getBoundingClientRect();
              if(sourceWidth>0&&sourceHeight>0&&frameRect.width>0&&frameRect.height>0){
                const scale=Math.min(frameRect.width/sourceWidth,frameRect.height/sourceHeight);
                mapSnapshot.style.left=((frameRect.width-sourceWidth*scale)/2)+"px";
                mapSnapshot.style.top=((frameRect.height-sourceHeight*scale)/2)+"px";
                mapSnapshot.style.transform="scale("+scale+")";
              }
            }
            window.setTimeout(function(){window.print();},250);
          });
        });
      </script></body></html>`;

    printWindow.document.open();
    printWindow.document.write(reportHtml);
    printWindow.document.close();
  };

  return (
    <div
      className="dmr-cargo-detail-layer dmr-cargo-route-layer"
      role="dialog"
      aria-modal="true"
      aria-label="貨物所屬站點路線"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <aside className="dmr-cargo-detail-drawer dmr-cargo-route-drawer" onMouseDown={(event) => event.stopPropagation()}>
        <header className="dmr-cargo-detail-header">
          <div className="dmr-cargo-detail-heading">
            <div>
              <h2>{cargoName}</h2>
            </div>
          </div>
          <button type="button" className="dmr-icon-button" onClick={onClose} aria-label="關閉路線詳情">
            <Icon name="close" size={19} />
          </button>
        </header>

        <div className="dmr-cargo-detail-body dmr-cargo-route-body">
          {loading ? (
            <div className="dmr-cargo-detail-loading"><span className="dmr-spinner" /><p>正在載入成員軌跡…</p></div>
          ) : null}
          {error ? <div className="dmr-cargo-detail-error" role="alert"><Icon name="alert" size={17} />{error}</div> : null}
          {!loading && route ? (
            <>
              <section className="dmr-cargo-route-summary">
                <div className="dmr-cargo-route-summary-head">
                  <strong>{route.driverName}</strong>
                  <button type="button" className="dmr-cargo-route-export" onClick={exportRoutePdf}>匯出 PDF</button>
                </div>
                <p>第 {route.stopOrder} 站 · {stopName}</p>
                <div className="dmr-cargo-route-times">
                  <span><small>開始時間</small><strong>{formatTaipeiDateTime(route.startedAt)}</strong></span>
                  <span><small>結束時間</small><strong>{formatTaipeiDateTime(route.completedAt)}</strong></span>
                  <span><small>行程時間</small><strong>{durationText}</strong></span>
                </div>
              </section>
              {route.points.length ? (
                <>
                  <CargoRouteMap points={route.points} />
                  <p className="dmr-cargo-route-note">時間窗由同一站點的開始與完成事件配對；定位點依此時間範圍讀取並去除重複。路線為 GPS 定位點連線，非道路導航貼合路徑。</p>
                </>
              ) : (
                <div className="dmr-cargo-route-empty"><Icon name="pin" size={20} /><strong>這段時間沒有記錄到 GPS 定位點</strong><span>工作與站點時間已找到，但目前沒有可繪製的成員軌跡。</span></div>
              )}
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function ReportDetailModal({
  report,
  onClose,
  onToggleStatus,
  canManageStatus,
  updating,
}) {
  if (!report) {
    return null;
  }

  const type = normalizeType(report);

  const meta = TYPE_META[type] || {
    title: "其他紀錄",
    icon: "reports",
    tone: "neutral",
  };

  const locationText = text(report?.location?.text || report?.address);

  return (
    <div
      className="dmr-modal-layer"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="dmr-modal">
        <header className="dmr-modal-header">
          <div className="dmr-modal-heading">
            <span className={`dmr-modal-icon dmr-tone-${meta.tone}`}>
              <Icon name={meta.icon} size={20} />
            </span>

            <div>
              <small>{meta.title}</small>

              <h2>{getReportTitle(report)}</h2>
            </div>
          </div>

          <button
            className="dmr-icon-button"
            type="button"
            onClick={onClose}
            aria-label="關閉"
          >
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="dmr-modal-body">
          <div className="dmr-report-meta-card">
            <div>
              <span>成員</span>
              <strong>{reportDriverName(report)}</strong>
            </div>

            <div>
              <span>車輛</span>
              <strong>{reportVehicleText(report)}</strong>
            </div>

            <div>
              <span>回報時間</span>
              <strong>{fmtDateTime(reportDate(report))}</strong>
            </div>

            <div>
              <span>狀態</span>
              <StatusBadge report={report} />
            </div>
          </div>

          {(reportJobTitle(report) ||
            reportApplicationId(report) ||
            locationText) && (
            <div className="dmr-detail-section">
              <h3>關聯工作</h3>

              <div className="dmr-detail-grid">
                <DetailItem label="工作" value={reportJobTitle(report)} />

                <DetailItem
                  label="Application ID"
                  value={reportApplicationId(report)}
                />

                <DetailItem label="地點" value={locationText} full />
              </div>
            </div>
          )}

          {type === "pre_trip" && <InspectionDetail report={report} />}

          {type === "expense" && <ExpenseDetail report={report} />}

          {type === "incident" && <IncidentDetail report={report} />}

          {type === "cargo" && <CargoDetail report={report} />}

          <ReportMediaSection report={report} />
        </div>

        <footer className="dmr-modal-footer">
          <div>
            <small>報表編號</small>

            <strong>{report.id}</strong>
          </div>

          <div className="dmr-modal-actions">
            <button
              type="button"
              className="dmr-primary-action"
              disabled={!canManageStatus || updating}
              onClick={() => onToggleStatus?.(report)}
            >
              <Icon name="check" size={16} />
              {updating
                ? "更新中…"
                : getStatus(report) === "completed"
                  ? "改回待處理"
                  : "標記已完成"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function CargoQrModal({ report, onClose }) {
  const [imageError, setImageError] = useState(false);
  const contentId = text(report?.contentId);
  const workDateKey = text(report?.workDateKey || report?.dateKey);
  const cargoName = text(report?.cargoName) || "未命名貨物";
  const payload = cargoQrPayload(report);
  const imageUri = cargoQrImageUri(payload);

  useEffect(() => {
    setImageError(false);
  }, [payload]);

  if (!report) return null;

  return (
    <div
      className="dmr-modal-layer dmr-action-modal-layer dmr-cargo-qr-layer"
      role="dialog"
      aria-modal="true"
      aria-label="貨物 QR Code"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="dmr-modal dmr-cargo-qr-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="dmr-modal-header">
          <div className="dmr-modal-heading">
            <div>
              <h2>貨物 QR Code</h2>
            </div>
          </div>
          <button
            className="dmr-icon-button"
            type="button"
            onClick={onClose}
            aria-label="關閉 QR Code"
          >
            <Icon name="close" size={19} />
          </button>
        </header>

        <div className="dmr-modal-body dmr-cargo-qr-body">
          <div className="dmr-cargo-qr-heading">
            <strong>{cargoName}</strong>
            <span>工作日期 · {workDateKey || "未提供"}</span>
            <span>{text(report?.recipientName) || "未指定聯絡人"}</span>
          </div>

          <div className="dmr-cargo-qr-preview">
            {imageUri && !imageError ? (
              <img
                src={imageUri}
                alt={`${cargoName} QR Code`}
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="dmr-cargo-qr-error" role="status">
                {!contentId
                  ? "這筆貨物沒有 contentId，無法產生可掃描的 QR Code。"
                  : !workDateKey
                    ? "這筆貨物沒有工作日期，無法產生可掃描的 QR Code。"
                    : "QR Code 暫時無法載入，請稍後重試。"}
              </div>
            )}
          </div>

          <div className="dmr-cargo-qr-code">
            <span>貨物編號</span>
            <code>{contentId || "未提供"}</code>
          </div>
        </div>

        <footer className="dmr-modal-footer dmr-cargo-qr-footer">
          <div>
            <button type="button" className="dmr-primary-action" onClick={onClose}>
              完成
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ReportCatalogCards({ activeType, typeCounts, onSelect }) {
  return (
    <section className="dmr-report-catalog" aria-label="報表列表">
      <div className="dmr-report-catalog-grid">
        {REPORT_KEYS.map((key) => {
          const meta = REPORT_CATALOG[key];
          const active = activeType === key;
          return (
            <button
              key={key}
              type="button"
              className={`dmr-report-catalog-card${active ? " is-active" : ""}`}
              aria-pressed={active}
              onClick={() => onSelect(key)}
            >
              <span className={`dmr-report-catalog-icon dmr-tone-${meta.tone}`}>
                <Icon name={meta.icon} size={16} />
              </span>
              <span className="dmr-report-tab-label">
                {REPORT_TAB_LABELS[key] || meta.title}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ReportTable({
  reports,
  activeType,
  onSelect,
  onShowCargoQr,
  onShowCargoDetail,
  onShowCargoRoute,
  canManageStatus,
  onToggleStatus,
  updatingId,
  canManage,
  onEdit,
  onDelete,
  deletingReportId,
  draft,
  drivers,
  vehicles,
  submitting,
  onDraftChange,
  onDraftSubmit,
  onDraftCancel,
  onIncidentPhotoSelect,
  onIncidentPhotoRemove,
}) {
  const config = REPORT_CATALOG[activeType] || REPORT_CATALOG.all;
  const isCargo = activeType === "cargo";
  const isTransport = activeType === "transport";
  const isWorkLink = isCargo || isTransport || activeType === "attendance";
  const columns = isCargo || isTransport || activeType === "attendance"
    ? config.columns
    : config.columns.some(([key]) => key === "status")
    ? config.columns
    : [...config.columns, ["status", "狀態"]];
  const tableConfig = { ...config, columns };

  return (
    <div className="dmr-first-table-wrap">
      <table className="dmr-first-table">
        <thead>
          <tr>
            {columns.map(([key, label]) => (
              <th key={key} title={label}>
                <span className="dmr-cell-ellipsis">{label}</span>
              </th>
            ))}
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {draft ? (
            <InlineReportRow
              config={tableConfig}
              activeType={activeType}
              draft={draft}
              drivers={drivers}
              vehicles={vehicles}
              submitting={submitting}
              onChange={onDraftChange}
              onSubmit={onDraftSubmit}
              onCancel={onDraftCancel}
              onIncidentPhotoSelect={onIncidentPhotoSelect}
              onIncidentPhotoRemove={onIncidentPhotoRemove}
            />
          ) : null}
          {reports.map((report) => (
            <tr key={report.id}>
              {columns.map(([key]) => {
                const value = reportColumnValue(report, key);
                return (
                  <td key={key} title={formatReportCell(value)}>
                    {key === "status" ? (
                      <button
                        type="button"
                        className="dmr-status-button"
                        onClick={() => onToggleStatus?.(report)}
                        disabled={!canManageStatus || updatingId === report.id}
                        title={
                          canManageStatus
                            ? "點擊切換待處理／已完成"
                            : "只有管理員可以變更狀態"
                        }
                      >
                        <StatusBadge report={report} />
                      </button>
                    ) : (
                      <span className="dmr-cell-ellipsis">
                        {formatReportCell(value)}
                      </span>
                    )}
                  </td>
                );
              })}
              <td>
                <div className={`dmr-row-actions${isCargo ? " is-cargo" : ""}`}>
                  <button
                    type="button"
                    className="dmr-open-button"
                    onClick={() => onSelect(report)}
                  >
                    {isWorkLink ? "查看工作" : "查看"} <Icon name="arrow" size={15} />
                  </button>
                  {reportKey(report) === "cargo" ? (
                    <button
                      type="button"
                      className="dmr-cargo-detail-button"
                      onClick={() => onShowCargoDetail?.(report)}
                      disabled={!!cargoDetailMissingReason(report)}
                      title={cargoDetailMissingReason(report) ? `此筆資料缺少${cargoDetailMissingReason(report)}，無法安全定位貨物` : "查看這件貨物的執行狀態與證明"}
                    >
                      <Icon name="eye" size={14} />
                      查看貨物
                    </button>
                  ) : null}
                  {isCargo ? (
                    <button
                      type="button"
                      className="dmr-route-action-button"
                      onClick={() => onShowCargoRoute?.(report)}
                      disabled={!!cargoDetailMissingReason(report)}
                      title={cargoDetailMissingReason(report) ? `此筆資料缺少${cargoDetailMissingReason(report)}，無法定位路線` : "查看成員從開始本站至抵達本站的 GPS 軌跡"}
                    >
                      <Icon name="pin" size={14} />
                      查看路線
                    </button>
                  ) : null}
                  {isCargo ? (
                    <button
                      type="button"
                      className="dmr-qr-button"
                      onClick={() => onShowCargoQr?.(report)}
                      disabled={!cargoQrPayload(report)}
                      title={
                        !text(report?.workDateKey || report?.dateKey)
                          ? "此筆貨物缺少工作日期，無法產生可掃描 QR Code"
                          : !reportApplicationId(report) || !text(report?.stopId) || !text(report?.recipientId) || !text(report?.contentId)
                            ? "此筆貨物缺少工作、站點、聯絡人或貨物識別資料，無法產生唯一 QR Code"
                            : "顯示包含完整貨物報表識別資料的 QR Code"
                      }
                    >
                      <Icon name="qr" size={14} />
                      QR Code
                    </button>
                  ) : null}
                  {!isCargo && !isTransport && canManage ? (
                    <>
                      <button
                        type="button"
                        className="dmr-row-edit-button"
                        onClick={() => onEdit(report)}
                      >
                        編輯
                      </button>
                      <button
                        type="button"
                        className="dmr-row-delete-button"
                        onClick={() => onDelete(report)}
                        disabled={deletingReportId === report.id}
                      >
                        {deletingReportId === report.id ? "刪除中" : "刪除"}
                      </button>
                    </>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const INLINE_FIELD_MAP = {
  content: "note",
  safetyFuel: "safetyFuel",
  safetyLights: "safetyLights",
  safetyBattery: "safetyBattery",
  safetyBrake: "safetyBrake",
  safetyDashboard: "safetyDashboard",
  safetyFire: "safetyFire",
  safetyRepair: "safetyRepair",
  safetyCargo: "safetyCargo",
  safetyDriver: "safetyDriver",
  attendanceExpected: "expectedDays",
  attendanceActual: "actualDays",
  attendanceLeave: "leaveDays",
  attendanceRate: "attendanceRate",
  fuelItem: "category",
  fuelLiters: "liters",
  fuelOdometer: "odometer",
  maintenanceOdometer: "odometer",
  maintenanceItem: "maintenanceItem",
  incidentTime: "incidentTime",
  incidentReporter: "reporter",
  incidentDescription: "incidentDescription",
  incidentFollowUp: "followUp",
  transportTask: "taskNo",
  transportLoading: "loading",
  transportUnloading: "unloading",
  transportDistance: "distanceKm",
  transportStart: "startTime",
  transportComplete: "endTime",
  breathTime: "breathTime",
  breathValue: "breathValue",
  expenseClaimant: "reporter",
  expensePurpose: "note",
};

function inlineFieldName(key) {
  return INLINE_FIELD_MAP[key] || key;
}

function InlineReportCell({
  fieldKey,
  draft,
  activeType,
  drivers,
  vehicles,
  onChange,
}) {
  if (fieldKey === "type") {
    if (activeType !== "all") {
      return (
        <span className="dmr-inline-fixed">
          {REPORT_CATALOG[activeType]?.short}
        </span>
      );
    }
    return (
      <select
        name="reportType"
        value={draft.reportType || ""}
        onChange={onChange}
        required
      >
        <option value="">選擇類型</option>
        {REPORT_KEYS.filter((key) => key !== "all").map((key) => (
          <option key={key} value={key}>
            {REPORT_CATALOG[key].short}
          </option>
        ))}
      </select>
    );
  }

  if (fieldKey === "driver") {
    return (
      <select name="driverId" value={draft.driverId || ""} onChange={onChange}>
        <option value="">未指定</option>
        {drivers.map((driver) => (
          <option key={driver.uid} value={driver.uid}>
            {text(driver.displayName) || driver.uid}
          </option>
        ))}
      </select>
    );
  }

  if (fieldKey === "vehicle") {
    return (
      <select
        name="vehicleId"
        value={draft.vehicleId || ""}
        onChange={onChange}
      >
        <option value="">未指定</option>
        {vehicles.map((vehicle) => (
          <option key={vehicle.id} value={vehicle.id}>
            {text(vehicle.plateNo || vehicle.licensePlate || vehicle.plate) ||
              vehicle.id}
          </option>
        ))}
      </select>
    );
  }

  if (fieldKey === "date") {
    return (
      <input
        name="occurredAt"
        type="datetime-local"
        value={draft.occurredAt || ""}
        onChange={onChange}
        required
      />
    );
  }

  if (fieldKey === "status") {
    return (
      <select
        name="status"
        value={draft.status || "pending"}
        onChange={onChange}
      >
        <option value="pending">待處理</option>
        <option value="completed">已完成</option>
      </select>
    );
  }

  const name = inlineFieldName(fieldKey);
  const numeric = [
    "amount",
    "attendanceExpected",
    "attendanceActual",
    "attendanceLeave",
    "attendanceRate",
    "fuelLiters",
    "fuelOdometer",
    "maintenanceOdometer",
    "transportDistance",
    "breathValue",
  ].includes(fieldKey);
  const dateTime = [
    "incidentTime",
    "transportStart",
    "transportComplete",
    "breathTime",
  ].includes(fieldKey);

  return (
    <input
      name={name}
      type={dateTime ? "datetime-local" : numeric ? "number" : "text"}
      value={draft[name] || ""}
      onChange={onChange}
      placeholder="填寫"
    />
  );
}

function IncidentPhotoPicker({ photos = [], disabled = false, onSelect, onRemove }) {
  return (
    <div className="dmr-incident-photo-picker">
      <div className="dmr-incident-photo-copy">
        <strong>異常照片</strong>
        <span>最多 {INCIDENT_PHOTO_LIMIT} 張；送出後保存預覽與原圖</span>
      </div>
      <label className="dmr-incident-photo-add" aria-disabled={disabled || photos.length >= INCIDENT_PHOTO_LIMIT}>
        <Icon name="image" size={15} />
        <span>新增照片</span>
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={disabled || photos.length >= INCIDENT_PHOTO_LIMIT}
          onChange={onSelect}
        />
      </label>
      <div className="dmr-incident-photo-previews">
        {photos.map((photo, index) => (
          <div className="dmr-incident-photo-preview" key={photo.localId}>
            <img src={photo.previewUrl} alt={`異常照片 ${index + 1}`} draggable={false} />
            <button type="button" onClick={() => onRemove(photo.localId)} disabled={disabled} aria-label={`移除第 ${index + 1} 張照片`}>×</button>
          </div>
        ))}
      </div>
      <span className="dmr-incident-photo-count">{photos.length}/{INCIDENT_PHOTO_LIMIT}</span>
    </div>
  );
}

function InlineReportRow({
  config,
  activeType,
  draft,
  drivers,
  vehicles,
  submitting,
  onChange,
  onSubmit,
  onCancel,
  onIncidentPhotoSelect,
  onIncidentPhotoRemove,
}) {
  return (
    <>
      <tr className="dmr-inline-row">
        {config.columns.map(([fieldKey]) => (
          <td key={fieldKey}>
            <InlineReportCell
              fieldKey={fieldKey}
              activeType={activeType}
              draft={draft}
              drivers={drivers}
              vehicles={vehicles}
              onChange={onChange}
            />
          </td>
        ))}
        <td>
          <div className="dmr-inline-actions">
            <button type="button" className="dmr-inline-cancel" onClick={onCancel} disabled={submitting}>
              取消
            </button>
            <button type="button" className="dmr-inline-submit" onClick={onSubmit} disabled={submitting}>
              {submitting ? "建立中…" : "建立"}
            </button>
          </div>
        </td>
      </tr>
      {draft.reportType === "incident" ? (
        <tr className="dmr-inline-photo-row">
          <td colSpan={config.columns.length + 1}>
            <IncidentPhotoPicker
              photos={Array.isArray(draft.incidentPhotos) ? draft.incidentPhotos : []}
              disabled={submitting}
              onSelect={onIncidentPhotoSelect}
              onRemove={onIncidentPhotoRemove}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

export default function DriverManagerReports({ user, account, onOpenCargoReport }) {
  const accountUid = text(account?.uid || user?.uid || auth.currentUser?.uid);
  const [accountProfile, setAccountProfile] = useState(account || {});
  const [profileLoading, setProfileLoading] = useState(Boolean(accountUid));

  useEffect(() => {
    setAccountProfile(account || {});

    if (!accountUid) {
      setProfileLoading(false);
      return undefined;
    }

    setProfileLoading(true);
    const profileRef = doc(db, "drivers", accountUid);

    return onSnapshot(
      profileRef,
      (snapshot) => {
        setAccountProfile({
          ...(account || {}),
          ...(snapshot.exists() ? snapshot.data() : {}),
        });
        setProfileLoading(false);
      },
      (error) => {
        console.error("[DriverManagerReports] account profile error:", error);
        setAccountProfile(account || {});
        setProfileLoading(false);
      },
    );
  }, [accountUid]);

  const orgId = text(accountProfile?.orgId || account?.orgId || user?.orgId);
  const memberRole = (
    text(
      accountProfile?.memberRole ||
        accountProfile?.orgRole ||
        accountProfile?.role,
    ) || "driver"
  ).toLowerCase();
  const driverType = text(
    accountProfile?.driverType ||
      accountProfile?.accountType ||
      (orgId ? "org" : ""),
  ).toLowerCase();
  const primaryRoleValues = [
    accountProfile?.memberRole,
    accountProfile?.orgRole,
    accountProfile?.role,
    accountProfile?.adminRole,
  ].map((value) => text(value).toLowerCase());
  const isPrimaryAdmin =
    primaryRoleValues.some((role) =>
      ["owner", "org_owner", "primary", "primary_owner"].includes(role),
    ) || ["primary", "owner"].includes(text(accountProfile?.adminLevel).toLowerCase());
  const canManageStatus =
    isPrimaryAdmin ||
    ["deputy", "admin", "orgadmin", "org_admin"].includes(memberRole);

  const [reports, setReports] = useState([]);

  const [loading, setLoading] = useState(true);

  const [loadError, setLoadError] = useState("");

  const [activeType, setActiveType] = useState("all");

  const [statusFilter, setStatusFilter] = useState("all");

  const [driverFilter, setDriverFilter] = useState("all");

  const [vehicleFilter, setVehicleFilter] = useState("all");

  const [search, setSearch] = useState("");

  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedCargoQr, setSelectedCargoQr] = useState(null);
  const [selectedCargoDetail, setSelectedCargoDetail] = useState(null);
  const [selectedCargoRoute, setSelectedCargoRoute] = useState(null);

  const [updatingId, setUpdatingId] = useState("");
  const [createReportOpen, setCreateReportOpen] = useState(false);
  const [createReportSubmitting, setCreateReportSubmitting] = useState(false);
  const [createReportError, setCreateReportError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [createReportForm, setCreateReportForm] = useState(EMPTY_REPORT_FORM);
  const incidentPhotoPreviewRef = useRef([]);
  useEffect(() => {
    incidentPhotoPreviewRef.current = Array.isArray(createReportForm.incidentPhotos)
      ? createReportForm.incidentPhotos
      : [];
  }, [createReportForm.incidentPhotos]);
  useEffect(
    () => () => releaseIncidentPhotoPreviews(incidentPhotoPreviewRef.current),
    [],
  );
  const [exportReportOpen, setExportReportOpen] = useState(false);
  const [exportReportType, setExportReportType] = useState("safety");
  const [exportReportStartDate, setExportReportStartDate] = useState(
    dateKey(new Date()),
  );
  const [exportReportEndDate, setExportReportEndDate] = useState(
    dateKey(new Date()),
  );
  const [exportReportSubmitting, setExportReportSubmitting] = useState(false);
  const [exportReportError, setExportReportError] = useState("");
  const [importReportOpen, setImportReportOpen] = useState(false);
  const [importReportType, setImportReportType] = useState("safety");
  const [importReportDate, setImportReportDate] = useState(dateKey(new Date()));
  const [importReportFile, setImportReportFile] = useState(null);
  const [importReportSubmitting, setImportReportSubmitting] = useState(false);
  const [importReportError, setImportReportError] = useState("");
  const [editingReport, setEditingReport] = useState(null);
  const [editReportForm, setEditReportForm] = useState({
    driverName: "",
    vehiclePlate: "",
    status: "pending",
    note: "",
  });
  const [editReportSubmitting, setEditReportSubmitting] = useState(false);
  const [editReportError, setEditReportError] = useState("");
  const [deletingReportId, setDeletingReportId] = useState("");
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [attendanceMetaVersion, setAttendanceMetaVersion] = useState("");
  const [attendanceLoadedVersion, setAttendanceLoadedVersion] = useState("");
  const [attendanceHasUpdate, setAttendanceHasUpdate] = useState(false);
  const [attendanceReloadToken, setAttendanceReloadToken] = useState(0);
  const [transportMetaVersion, setTransportMetaVersion] = useState("");
  const [transportLoadedVersion, setTransportLoadedVersion] = useState("");
  const [transportHasUpdate, setTransportHasUpdate] = useState(false);
  const [transportReloadToken, setTransportReloadToken] = useState(0);

  useEffect(() => {
    if (profileLoading || !orgId) {
      setAvailableDrivers([]);
      setAvailableVehicles([]);
      return undefined;
    }

    const driverQuery = query(
      collection(db, "drivers"),
      where("orgId", "==", orgId),
      limit(200),
    );
    const vehicleQuery = query(
      collection(db, "vehicles"),
      where("ownerOrgId", "==", orgId),
      limit(200),
    );

    const unsubscribeDrivers = onSnapshot(
      driverQuery,
      (snapshot) => {
        setAvailableDrivers(
          snapshot.docs.map((item) => ({ uid: item.id, ...item.data() })),
        );
      },
      (error) => {
        console.error("[DriverManagerReports] drivers error:", error);
        setAvailableDrivers([]);
      },
    );
    const unsubscribeVehicles = onSnapshot(
      vehicleQuery,
      (snapshot) => {
        setAvailableVehicles(
          snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
        );
      },
      (error) => {
        console.error("[DriverManagerReports] vehicles error:", error);
        setAvailableVehicles([]);
      },
    );

    return () => {
      unsubscribeDrivers();
      unsubscribeVehicles();
    };
  }, [orgId, profileLoading]);

  const [selectedDateKey, setSelectedDateKey] = useState(dateKey(new Date()));
  const [selectedDate, setSelectedDate] = useState(() =>
    dateFromKey(dateKey(new Date())),
  );
  const [daySummary, setDaySummary] = useState(null);
  const [typeSummaryCounts, setTypeSummaryCounts] = useState({});
  const [listCursor, setListCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  useEffect(() => {
    setSelectedDateKey(dateKey(new Date()));
    setSelectedDate(dateFromKey(dateKey(new Date())));
  }, [orgId]);

  useEffect(() => {
    if (profileLoading || !orgId || !selectedDateKey) {
      setDaySummary(null);
      return undefined;
    }

    return onSnapshot(
      reportDayRef(orgId, selectedDateKey),
      (snapshot) => {
        setDaySummary(snapshot.exists() ? snapshot.data() || {} : null);
      },
      (error) => {
        console.error("[DriverManagerReports] report day error:", error);
        setDaySummary(null);
      },
    );
  }, [orgId, profileLoading, selectedDateKey]);

  useEffect(() => {
    if (profileLoading || !orgId || !selectedDateKey) {
      setTypeSummaryCounts({});
      return undefined;
    }

    return onSnapshot(
      reportTypeSummaryRef(orgId, selectedDateKey),
      (snapshot) => {
        setTypeSummaryCounts(
          Object.fromEntries(
            snapshot.docs.map((item) => [
              item.id,
              number(item.data()?.count),
            ]),
          ),
        );
      },
      (error) => {
        console.error("[DriverManagerReports] report type summary error:", error);
        setTypeSummaryCounts({});
      },
    );
  }, [orgId, profileLoading, selectedDateKey]);

  useEffect(() => {
    if (profileLoading || !orgId || !selectedDateKey || activeType !== "attendance") {
      setAttendanceMetaVersion("");
      setAttendanceLoadedVersion("");
      setAttendanceHasUpdate(false);
      return undefined;
    }
    return onSnapshot(
      reportTypeMetaRef(orgId, selectedDateKey, "attendance"),
      (snapshot) => setAttendanceMetaVersion(
        snapshot.exists() ? reportMetaVersion(snapshot.data() || {}) : "",
      ),
      (error) => console.error("[DriverManagerReports] attendance meta error:", error),
    );
  }, [activeType, orgId, profileLoading, selectedDateKey]);

  useEffect(() => {
    if (activeType !== "attendance" || !attendanceMetaVersion || !attendanceLoadedVersion) return;
    setAttendanceHasUpdate(attendanceMetaVersion !== attendanceLoadedVersion);
  }, [activeType, attendanceLoadedVersion, attendanceMetaVersion]);

  useEffect(() => {
    if (profileLoading || !orgId || !selectedDateKey || activeType !== "transport") {
      setTransportMetaVersion("");
      setTransportLoadedVersion("");
      setTransportHasUpdate(false);
      return undefined;
    }
    return onSnapshot(
      reportTypeMetaRef(orgId, selectedDateKey, "transport"),
      (snapshot) => setTransportMetaVersion(
        snapshot.exists() ? reportMetaVersion(snapshot.data() || {}) : "",
      ),
      (error) => console.error("[DriverManagerReports] transport meta error:", error),
    );
  }, [activeType, orgId, profileLoading, selectedDateKey]);

  useEffect(() => {
    if (activeType !== "transport" || !transportMetaVersion || !transportLoadedVersion) return;
    setTransportHasUpdate(transportMetaVersion !== transportLoadedVersion);
  }, [activeType, transportLoadedVersion, transportMetaVersion]);

  useEffect(() => {
    if (profileLoading || !orgId || !selectedDateKey) {
      setReports([]);
      setLoading(false);
      setListCursor(null);
      setHasMore(false);
      return undefined;
    }

    setReports([]);
    setLoading(true);
    setPageLoading(true);
    setListCursor(null);
    setHasMore(false);
    setLoadError("");

    if (["cargo", "attendance", "transport"].includes(activeType)) {
      let cancelled = false;
      const loader = activeType === "attendance"
        ? loadAttendanceRecords(orgId, selectedDateKey)
        : activeType === "transport"
          ? loadTransportRecords(orgId, selectedDateKey)
          : loadChunkRecords(orgId, [selectedDateKey], "cargo");
      loader.then((result) => {
        if (cancelled) return;
        const records = result.records;
        records.sort((a, b) => {
          if (activeType === "transport") {
            const jobOrder = text(a.applicationId).localeCompare(text(b.applicationId));
            if (jobOrder !== 0) return jobOrder;
            return Number(a.stopOrder || 0) - Number(b.stopOrder || 0);
          }
          return (reportDate(b)?.getTime() || 0) - (reportDate(a)?.getTime() || 0);
        });
        setReports(records); setListCursor(null); setHasMore(false); setLoading(false); setPageLoading(false); setLoadError("");
        if (activeType === "attendance") {
          setAttendanceLoadedVersion(reportMetaVersion(result.metaByDay?.[selectedDateKey] || {}));
          setAttendanceHasUpdate(false);
        }
        if (activeType === "transport") {
          setTransportLoadedVersion(reportMetaVersion(result.metaByDay?.[selectedDateKey] || {}));
          setTransportHasUpdate(false);
        }
      }).catch(() => { if (!cancelled) { setReports([]); setLoading(false); setPageLoading(false); setListCursor(null); setHasMore(false); setLoadError(`${activeType === "attendance" ? "成員出勤" : activeType === "transport" ? "運輸總表" : "貨物"}報表讀取失敗，請確認網路與 Firestore 權限。`); } });
      return () => { cancelled = true; };
    }

    const itemsRef =
      activeType === "all"
        ? reportSummaryItemsRef(orgId, selectedDateKey)
        : reportTypeItemsRef(orgId, selectedDateKey, activeType);
    const firstQuery = query(
      itemsRef,
      orderBy("occurredAt", "desc"),
      limit(REPORT_PAGE_SIZE),
    );

    return onSnapshot(
      firstQuery,
      (snap) => {
        setReports(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })),
        );
        setListCursor(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.docs.length === REPORT_PAGE_SIZE);
        setLoading(false);
        setPageLoading(false);
        setLoadError("");
      },
      (error) => {
        console.error("[DriverManagerReports] reports error:", error);
        setReports([]);
        setLoading(false);
        setPageLoading(false);
        setListCursor(null);
        setHasMore(false);
        setLoadError(
          error?.code === "permission-denied"
            ? "無法讀取報表資料，請確認 Firestore 權限。"
            : "報表資料讀取失敗，請確認網路後重新整理。",
        );
      },
    );
  }, [
    activeType,
    driverType,
    memberRole,
    orgId,
    profileLoading,
    selectedDateKey,
    attendanceReloadToken,
    transportReloadToken,
  ]);

  useEffect(() => {
    if (!selectedReport || selectedReport._detailLoaded) return;

    const latest = reports.find((report) => report.id === selectedReport.id);

    if (latest) setSelectedReport((current) => ({ ...current, ...latest }));
  }, [reports, selectedReport?._detailLoaded, selectedReport?.id]);

  const stats = useMemo(() => {
    const statusCounts = daySummary?.statusCounts || {};
    const pendingStatuses = [
      "pending",
      "new",
      "abnormal",
      "needs_review",
      "reviewing",
      "processing",
    ];
    return {
      today: number(daySummary?.reportCount ?? reports.length),
      pending: pendingStatuses.reduce(
        (sum, key) => sum + number(statusCounts[key]),
        0,
      ),
      inspectionAbnormal: number(daySummary?.abnormalCount),
      dayExpenses: number(daySummary?.expenseTotal),
      incidents: number(daySummary?.openIncidentCount),
    };
  }, [daySummary, reports.length]);

  const typeCounts = useMemo(() => {
    const summaryCounts =
      Object.keys(typeSummaryCounts).length > 0
        ? typeSummaryCounts
        : daySummary?.typeCounts || {};
    const loadedCounts = reports.reduce((result, report) => {
      const type = reportKey(report);
      result[type] = (result[type] || 0) + 1;
      return result;
    }, {});
    const summaryTotal = Object.values(summaryCounts).reduce(
      (total, value) => total + number(value),
      0,
    );
    const counts =
      summaryTotal > 0
        ? summaryCounts
        : Object.keys(loadedCounts).length
          ? loadedCounts
          : summaryCounts;
    return Object.fromEntries(
      REPORT_KEYS.map((key) => [
        key,
        key === "all"
          ? number(daySummary?.reportCount ?? reports.length)
          : number(counts[key] ?? loadedCounts[key]),
      ]),
    );
  }, [daySummary, reports, typeSummaryCounts]);

  const reportFilterOptions = useMemo(() => {
    const drivers = new Map();
    const vehicles = new Set();

    reports.forEach((report) => {
      const driverId = reportDriverId(report);
      const driverName = reportDriverName(report);
      if (driverId || driverName)
        drivers.set(driverId || driverName, driverName);
      const vehicle = reportVehiclePlate(report);
      if (vehicle && vehicle !== "—") vehicles.add(vehicle);
    });

    return {
      drivers: [...drivers.entries()].sort((a, b) =>
        a[1].localeCompare(b[1], "zh-Hant"),
      ),
      vehicles: [...vehicles].sort(),
    };
  }, [reports]);

  const filteredReports = useMemo(() => {
    const keyword = text(search).toLowerCase();

    return reports
      .filter((report) => {
        const type = reportKey(report);

        if (activeType !== "all" && type !== activeType) {
          return false;
        }

        if (driverFilter !== "all") {
          const driverKey = reportDriverId(report) || reportDriverName(report);
          if (driverKey !== driverFilter) return false;
        }

        if (
          vehicleFilter !== "all" &&
          reportVehiclePlate(report) !== vehicleFilter
        ) {
          return false;
        }

        const status = getStatus(report);

        if (
          statusFilter === "pending" &&
          !["pending", "new", "abnormal", "needs_review"].includes(status)
        ) {
          return false;
        }

        if (
          statusFilter === "reviewing" &&
          !["reviewing", "processing"].includes(status)
        ) {
          return false;
        }

        if (
          statusFilter === "resolved" &&
          !["resolved", "completed", "approved", "normal"].includes(status)
        ) {
          return false;
        }

        if (!keyword) {
          return true;
        }

        const haystack = [
          report.id,
          reportDriverName(report),
          reportDriverId(report),
          reportVehiclePlate(report),
          reportJobTitle(report),
          reportApplicationId(report),
          getReportTitle(report),
          getReportDescription(report),
          report?.recipientRole === "sender" ? "寄件人" : "收貨人",
          report?.recipientName,
          report?.recipientPhone,
          report?.cargoName,
          report?.quantity,
          report?.unit,
          text(report?.location?.text),
        ]
          .join(" ")
          .toLowerCase();

        return haystack.includes(keyword);
      })
      .sort(
        (a, b) =>
          (reportDate(b)?.getTime() || 0) - (reportDate(a)?.getTime() || 0),
      );
  }, [reports, activeType, statusFilter, search, driverFilter, vehicleFilter]);

  const expenseBreakdown = useMemo(() => {
    const map = {};

    filteredReports
      .filter((report) => reportKey(report) === "expense")
      .forEach((report) => {
        const category = expenseCategory(report) || "other";

        map[category] = (map[category] || 0) + expenseAmount(report);
      });

    return Object.entries(map)
      .map(([category, amount]) => ({
        category,
        label: EXPENSE_LABELS[category] || category,
        amount,
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [filteredReports]);

  function updateCreateReportField(event) {
    const { name, value } = event.target;
    setCreateReportForm((current) => ({ ...current, [name]: value }));
    setCreateReportError("");
  }

  function handleIncidentPhotoSelection(event) {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selectedFiles.length) return;
    const imageFiles = selectedFiles.filter((file) => text(file?.type).toLowerCase().startsWith("image/"));
    const existingPhotos = Array.isArray(createReportForm.incidentPhotos) ? createReportForm.incidentPhotos : [];
    const remaining = Math.max(0, INCIDENT_PHOTO_LIMIT - existingPhotos.length);
    const acceptedFiles = imageFiles.slice(0, remaining);
    const addedPhotos = acceptedFiles.map((file) => ({
      localId: createReportMediaUuid(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    if (addedPhotos.length) {
      setCreateReportForm((current) => ({
        ...current,
        incidentPhotos: [...(Array.isArray(current.incidentPhotos) ? current.incidentPhotos : []), ...addedPhotos],
      }));
    }
    if (imageFiles.length > remaining) {
      setCreateReportError(`異常照片最多 ${INCIDENT_PHOTO_LIMIT} 張，已加入可選取的照片。`);
    } else if (imageFiles.length !== selectedFiles.length) {
      setCreateReportError("只支援圖片檔案；非圖片附件未加入。");
    } else {
      setCreateReportError("");
    }
  }

  function removeIncidentPhoto(localId) {
    const photos = Array.isArray(createReportForm.incidentPhotos) ? createReportForm.incidentPhotos : [];
    const removed = photos.find((photo) => photo.localId === localId);
    if (removed) releaseIncidentPhotoPreviews([removed]);
    setCreateReportForm((current) => ({
      ...current,
      incidentPhotos: (Array.isArray(current.incidentPhotos) ? current.incidentPhotos : []).filter((photo) => photo.localId !== localId),
    }));
    setCreateReportError("");
  }

  function openCreateReport() {
    setCreateReportForm({
      ...EMPTY_REPORT_FORM,
      reportType: activeType === "all" ? "" : activeType,
      occurredAt: new Date().toISOString().slice(0, 16),
      driverId: accountUid,
    });
    setCreateReportError("");
    setSuccessMessage("");
    setCreateReportOpen(true);
  }

  function closeCreateReport() {
    if (createReportSubmitting) return;
    releaseIncidentPhotoPreviews(createReportForm.incidentPhotos);
    setCreateReportForm((current) => ({ ...current, incidentPhotos: [] }));
    setCreateReportOpen(false);
    setCreateReportError("");
  }

  function openImportReport() {
    if (activeType === "cargo") return;
    setImportReportType(activeType === "all" ? "safety" : activeType);
    setImportReportDate(selectedDateKey);
    setImportReportFile(null);
    setImportReportError("");
    setSuccessMessage("");
    setImportReportOpen(true);
  }

  function closeImportReport() {
    if (importReportSubmitting) return;
    setImportReportOpen(false);
    setImportReportError("");
  }

  function openEditReport(report) {
    if (!isPrimaryAdmin || !report?.id) return;
    setEditingReport(report);
    setEditReportForm({
      driverName: reportDriverName(report) === "—" ? "" : reportDriverName(report),
      vehiclePlate: reportVehiclePlate(report) === "—" ? "" : reportVehiclePlate(report),
      status: getStatus(report),
      note: text(report?.note || report?.summary || getReportDescription(report)),
    });
    setEditReportError("");
  }

  function closeEditReport() {
    if (editReportSubmitting) return;
    setEditingReport(null);
    setEditReportError("");
  }

  function updateEditReportField(event) {
    const { name, value } = event.target;
    setEditReportForm((current) => ({ ...current, [name]: value }));
    setEditReportError("");
  }

  async function saveEditReport(event) {
    event.preventDefault();
    if (!isPrimaryAdmin || !orgId || !editingReport?.id) return;

    setEditReportError("");
    try {
      setEditReportSubmitting(true);
      const type = reportKey(editingReport);
      const day = text(editingReport.dateKey) || selectedDateKey;
      const reportRef = doc(reportTypeItemsRef(orgId, day, type), editingReport.id);
      const summaryRef = doc(reportSummaryItemsRef(orgId, day), editingReport.id);
      const selectedDriver = availableDrivers.find(
        (driver) =>
          text(driver.displayName) === text(editReportForm.driverName) ||
          text(driver.uid) === text(editReportForm.driverName),
      );
      const selectedVehicle = availableVehicles.find(
        (vehicle) =>
          text(vehicle.plateNo || vehicle.licensePlate || vehicle.plate) ===
            text(editReportForm.vehiclePlate) ||
          text(vehicle.id) === text(editReportForm.vehiclePlate),
      );
      const patch = {
        driverId: text(selectedDriver?.uid || editingReport.driverId),
        driverName: text(selectedDriver?.displayName || editReportForm.driverName),
        vehicleId: text(selectedVehicle?.id || editingReport.vehicleId),
        vehiclePlate: text(
          selectedVehicle?.plateNo ||
            selectedVehicle?.licensePlate ||
            selectedVehicle?.plate ||
            editReportForm.vehiclePlate,
        ),
        status: editReportForm.status === "completed" ? "completed" : "pending",
        note: text(editReportForm.note),
        updatedAt: serverTimestamp(),
        updatedByUid: accountUid,
        updatedByName: text(accountProfile?.displayName || user?.displayName),
      };
      const oldStatus = getStatus(editingReport);
      const batch = writeBatch(db);
      batch.set(reportRef, patch, { merge: true });
      batch.set(
        summaryRef,
        reportSummary({ ...editingReport, ...patch, reportType: type }, editingReport.id, day),
        { merge: true },
      );
      if (oldStatus !== patch.status) {
        batch.set(
          reportDayRef(orgId, day),
          {
            [`statusCounts.${oldStatus}`]: increment(-1),
            [`statusCounts.${patch.status}`]: increment(1),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
        batch.set(
          doc(reportDayRef(orgId, day), "types", type),
          {
            [`statusCounts.${oldStatus}`]: increment(-1),
            [`statusCounts.${patch.status}`]: increment(1),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }
      await batch.commit();
      setEditingReport(null);
      setSuccessMessage("報表已更新。");
    } catch (error) {
      console.error("[DriverManagerReports] edit report error:", error);
      setEditReportError(
        error?.code === "permission-denied"
          ? "沒有編輯報表的權限，請確認 Firestore Rules。"
          : "報表更新失敗，請稍後再試。",
      );
    } finally {
      setEditReportSubmitting(false);
    }
  }

  async function deleteReport(report) {
    if (!isPrimaryAdmin || !orgId || !report?.id || deletingReportId) return;
    if (
      typeof window !== "undefined" &&
      !window.confirm("確定要刪除這份報表嗎？刪除後無法復原。")
    ) {
      return;
    }

    const type = reportKey(report);
    const day = text(report.dateKey) || selectedDateKey;
    const reportRef = doc(reportTypeItemsRef(orgId, day, type), report.id);
    const summaryRef = doc(reportSummaryItemsRef(orgId, day), report.id);

    try {
      setDeletingReportId(report.id);

      if (type === "attendance") {
        const metaRef = reportTypeMetaRef(orgId, day, type);
        const chunkIdsFromReport = text(report.reportChunkId);
        await runTransaction(db, async (transaction) => {
          const metaSnapshot = await transaction.get(metaRef);
          const meta = metaSnapshot.exists() ? metaSnapshot.data() || {} : {};
          const manifestChunkIds = Array.isArray(meta.chunkIds)
            ? meta.chunkIds.map(text).filter(Boolean)
            : [];
          const candidateChunkIds = chunkIdsFromReport
            ? [chunkIdsFromReport]
            : manifestChunkIds;
          const chunkSnapshots = await Promise.all(candidateChunkIds.map((chunkId) =>
            transaction.get(doc(reportTypeChunksRef(orgId, day, type), chunkId)),
          ));
          const matchedIndex = chunkSnapshots.findIndex((snapshot) =>
            snapshot.exists() && Object.prototype.hasOwnProperty.call(snapshot.data()?.items || {}, report.id),
          );

          if (matchedIndex < 0) {
            throw new Error("attendance_chunk_item_not_found");
          }

          const chunkSnapshot = chunkSnapshots[matchedIndex];
          const chunkId = candidateChunkIds[matchedIndex];
          const chunkRef = doc(reportTypeChunksRef(orgId, day, type), chunkId);
          const chunkData = chunkSnapshot.data() || {};
          const nextItems = { ...(chunkData.items || {}) };
          delete nextItems[report.id];
          const nextChunkIds = manifestChunkIds.filter((id) =>
            id !== chunkId || Object.keys(nextItems).length > 0,
          );

          if (Object.keys(nextItems).length) {
            transaction.set(chunkRef, {
              ...chunkData,
              itemCount: Object.keys(nextItems).length,
              items: nextItems,
              updatedAt: serverTimestamp(),
            });
          } else {
            transaction.delete(chunkRef);
          }
          transaction.set(metaRef, {
            reportType: "attendance",
            chunkCount: nextChunkIds.length,
            chunkIds: nextChunkIds,
            totalItemCount: Math.max(0, number(meta.totalItemCount) - 1),
            activeChunkId: nextChunkIds[nextChunkIds.length - 1] || null,
            updatedAt: serverTimestamp(),
          }, { merge: true });
          transaction.delete(reportRef);
          transaction.delete(summaryRef);
        });

        if (selectedReport?.id === report.id) setSelectedReport(null);
        setAttendanceReloadToken((value) => value + 1);
        setAttendanceHasUpdate(false);
        setSuccessMessage("成員出勤資料已刪除。");
        return;
      }

      const snapshot = await getDoc(reportRef);
      const source = snapshot.exists()
        ? { id: snapshot.id, ...snapshot.data() }
        : report;
      const incidentPhotoAssets = type === "incident" ? getPhotos(source) : [];
      const status = getStatus(source);
      const batch = writeBatch(db);
      const dayPatch = {
        reportCount: increment(-1),
        [`typeCounts.${type}`]: increment(-1),
        [`statusCounts.${status}`]: increment(-1),
        abnormalCount:
          type === "safety"
            ? increment(-number(source?.inspection?.abnormalCount))
            : increment(0),
        openIncidentCount:
          type === "incident" &&
          !["resolved", "completed", "approved"].includes(status)
            ? increment(-1)
            : increment(0),
        expenseTotal: ["expense", "fuel"].includes(type)
          ? increment(-expenseAmount(source))
          : increment(0),
        updatedAt: serverTimestamp(),
      };
      batch.delete(reportRef);
      batch.delete(summaryRef);
      batch.set(reportDayRef(orgId, day), dayPatch, { merge: true });
      batch.set(
        doc(reportDayRef(orgId, day), "types", type),
        {
          count: increment(-1),
          [`statusCounts.${status}`]: increment(-1),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      await batch.commit();
      const photoCleanupResults = incidentPhotoAssets.length
        ? await Promise.allSettled(incidentPhotoAssets.map(removeIncidentReportPhotoAsset))
        : [];
      const photoCleanupPending = photoCleanupResults.some((result) => result.status === "rejected");
      if (photoCleanupPending) {
        console.warn(
          "[DriverManagerReports] incident photo cleanup pending:",
          incidentPhotoAssets.map((asset) => asset.assetId),
        );
      }
      if (selectedReport?.id === report.id) setSelectedReport(null);
      setSuccessMessage(
        photoCleanupPending
          ? "報表已刪除，但部分異常照片清理未完成；相關媒體資產已保留清理標記。"
          : "報表已刪除。",
      );
    } catch (error) {
      console.error("[DriverManagerReports] delete report error:", error);
      setLoadError(
        error?.code === "permission-denied"
          ? "沒有刪除報表的權限，請確認 Firestore Rules。"
          : error?.message === "attendance_chunk_item_not_found"
            ? "找不到這筆出勤資料；請重新整理後再試。"
            : "報表刪除失敗，請稍後再試。",
      );
    } finally {
      setDeletingReportId("");
    }
  }

  async function importReports() {
    setImportReportError("");

    if (!orgId) {
      setImportReportError("尚未取得 orgId，無法匯入報表。");
      return;
    }

    if (!importReportFile) {
      setImportReportError("請先選擇 CSV 檔案。");
      return;
    }

    if (importReportType === "cargo") { setImportReportError("貨物報表只能匯出，不能匯入。"); return; }

    if (!importReportType || !REPORT_CATALOG[importReportType]) {
      setImportReportError("請先選擇要匯入的報表項目。");
      return;
    }

    const day = importReportDate || selectedDateKey;
    const type = importReportType;
    const rows = parseCsvRows(await importReportFile.text()).slice(0, 200);

    if (!rows.length) {
      setImportReportError("CSV 沒有可匯入的資料列，請確認第一列是欄位名稱。");
      return;
    }

    const safetyFields = [
      ["fuel", "燃料油、機油、水", "safetyFuel"],
      ["lights", "各燈光、雨刷、照後鏡、喇叭", "safetyLights"],
      ["battery", "電瓶、胎壓、胎紋、風扇、皮帶、冷氣", "safetyBattery"],
      ["brake", "轉向、煞車系統", "safetyBrake"],
      ["dashboard", "儀表板警示燈、行車記錄器", "safetyDashboard"],
      ["fire", "滅火器壓力及有效期限", "safetyFire"],
      ["repair", "待修理或需更換部分", "safetyRepair"],
      ["cargo", "貨物是否綑綁穩妥、覆蓋嚴密", "safetyCargo"],
      ["driver", "駕駛人確認", "safetyDriver"],
    ];

    try {
      setImportReportSubmitting(true);
      const batch = writeBatch(db);
      const dayRef = reportDayRef(orgId, day);
      const statusCounts = {};
      let abnormalCount = 0;
      let openIncidentCount = 0;
      let expenseTotal = 0;

      rows.forEach((row) => {
        const status = "pending";
        const occurredAt = dateFromKey(day);
        const driverValue = csvField(row, ["成員", "駕駛人", "driver", "driverName"]);
        const vehicleValue = csvField(row, ["車牌", "車牌號碼", "vehicle", "vehiclePlate"]);
        const selectedDriver = availableDrivers.find(
          (driver) =>
            text(driver.uid) === driverValue ||
            text(driver.displayName) === driverValue,
        );
        const selectedVehicle = availableVehicles.find(
          (vehicle) =>
            text(vehicle.id) === vehicleValue ||
            text(vehicle.plateNo || vehicle.licensePlate || vehicle.plate) ===
              vehicleValue,
        );
        const reportRef = doc(reportTypeItemsRef(orgId, day, type));
        const summaryRef = doc(reportSummaryItemsRef(orgId, day), reportRef.id);
        const base = {
          orgId,
          reportType: type,
          title: REPORT_CATALOG[type]?.title || "匯入報表",
          status,
          severity: "normal",
          occurredAt,
          submittedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdByUid: accountUid,
          createdByRole: memberRole,
          createdByName: text(accountProfile?.displayName || user?.displayName),
          driverId: text(selectedDriver?.uid || driverValue),
          driverName: text(selectedDriver?.displayName || driverValue),
          vehicleId: text(selectedVehicle?.id),
          vehiclePlate: text(
            selectedVehicle?.plateNo ||
              selectedVehicle?.licensePlate ||
              selectedVehicle?.plate ||
              vehicleValue,
          ),
          sourceReportId: csvField(row, ["報表編號", "reportId"]),
          note: csvField(row, ["備註", "內容", "note", "description"]),
          photos: [],
        };

        if (type === "safety") {
          const items = safetyFields
            .map(([key, label, fieldKey]) => ({
              key,
              label,
              value: csvField(row, [label, fieldKey]),
            }))
            .filter((item) => item.value);
          const abnormalItems = items.filter((item) =>
            ["異常", "不正常", "否", "ng", "fail"].includes(
              item.value.toLowerCase(),
            ),
          );
          abnormalCount += abnormalItems.length;
          base.inspection = {
            items: items.map((item) => ({
              ...item,
              normal: !["異常", "不正常", "否", "ng", "fail"].includes(
                item.value.toLowerCase(),
              ),
              status: ["異常", "不正常", "否", "ng", "fail"].includes(
                item.value.toLowerCase(),
              )
                ? "abnormal"
                : "normal",
            })),
            abnormalCount: abnormalItems.length,
            canDepart: !abnormalItems.length,
            summary: base.note,
          };
        }

        if (type === "attendance") {
          const expectedDays = number(
            csvField(row, ["本月應當執勤天數", "應出勤", "expectedDays"]),
          );
          const actualDays = number(
            csvField(row, ["本月實際執勤天數", "實際出勤", "actualDays"]),
          );
          base.attendance = {
            month: csvField(row, ["月份", "month"]),
            expectedDays,
            actualDays,
            leaveDays: number(csvField(row, ["請假天數", "leaveDays"])),
            attendanceRate: expectedDays ? actualDays / expectedDays : 0,
            note: base.note,
          };
        }

        if (["fuel", "expense"].includes(type)) {
          const amount = number(csvField(row, ["金額", "amount"]));
          expenseTotal += amount;
          base.expense = {
            category: csvField(row, ["品項", "使用事項", "category"]) || "other",
            item: csvField(row, ["品項", "使用事項", "category"]) || "other",
            amount,
            liters: number(csvField(row, ["油量", "liters"])),
            odometer: number(csvField(row, ["加油時里程數", "里程讀數", "odometer"])),
            vendor: csvField(row, ["廠商", "vendor"]),
            note: base.note,
          };
        }

        if (type === "maintenance") {
          base.maintenance = {
            item: csvField(row, ["保養項目", "maintenanceItem"]),
            odometer: number(csvField(row, ["里程讀數", "odometer"])),
            amount: number(csvField(row, ["保養金額", "amount"])),
            vendor: csvField(row, ["廠商", "vendor"]),
            note: base.note,
          };
        }

        if (type === "incident") {
          const incidentStatus = status;
          if (!["resolved", "completed", "approved"].includes(incidentStatus)) {
            openIncidentCount += 1;
          }
          base.incident = {
            category: csvField(row, ["異常類型", "category"]) || "other",
            reporter: csvField(row, ["通報者", "reporter"]) || base.driverName,
            time: toDateSafe(csvField(row, ["時間", "incidentTime"])) || occurredAt,
            description: csvField(row, ["異常說明", "description"]),
            followUp: csvField(row, ["後續處理", "followUp"]),
          };
          base.description = base.incident.description;
        }

        if (type === "transport") {
          base.applicationId = csvField(row, ["Application ID", "任務編號", "taskNo"]);
          base.scheduleSnapshot = {
            startTimeISO: csvField(row, ["出車時間", "startTime"]),
            endTimeISO: csvField(row, ["完成時間", "endTime"]),
            distanceKm: number(csvField(row, ["行駛里程", "distanceKm"])),
          };
          base.stops = [
            { sequence: 0, type: "pickup", text: csvField(row, ["裝貨地點", "loading"]) },
            { sequence: 1, type: "dropoff", text: csvField(row, ["卸貨地點", "unloading"]) },
          ];
        }

        if (type === "breath") {
          base.breathalyzer = {
            time: toDateSafe(csvField(row, ["時間", "breathTime"])) || occurredAt,
            value: number(csvField(row, ["酒測器讀數", "breathValue"])),
          };
        }

        statusCounts[status] = (statusCounts[status] || 0) + 1;
        batch.set(
          reportRef,
          { ...base, reportId: reportRef.id, dateKey: day, reportType: type },
          { merge: true },
        );
        batch.set(
          summaryRef,
          reportSummary({ ...base, reportType: type }, reportRef.id, day),
          { merge: true },
        );
      });

      const dayPatch = {
        dateKey: day,
        reportCount: increment(rows.length),
        [`typeCounts.${type}`]: increment(rows.length),
        abnormalCount: increment(abnormalCount),
        openIncidentCount: increment(openIncidentCount),
        expenseTotal: increment(expenseTotal),
        updatedAt: serverTimestamp(),
      };
      Object.entries(statusCounts).forEach(([status, count]) => {
        dayPatch[`statusCounts.${status}`] = increment(count);
      });
      const typePatch = {
        reportType: type,
        count: increment(rows.length),
        updatedAt: serverTimestamp(),
      };
      Object.entries(statusCounts).forEach(([status, count]) => {
        typePatch[`statusCounts.${status}`] = increment(count);
      });
      batch.set(dayRef, dayPatch, { merge: true });
      batch.set(doc(dayRef, "types", type), typePatch, { merge: true });
      await batch.commit();
      setSelectedDateKey(day);
      setSelectedDate(dateFromKey(day));
      setActiveType(type);
      setImportReportOpen(false);
      setSuccessMessage(`已匯入 ${rows.length} 份${REPORT_CATALOG[type].title}。`);
    } catch (error) {
      console.error("[DriverManagerReports] import reports error:", error);
      setImportReportError(
        error?.code === "permission-denied"
          ? "沒有匯入報表的權限，請確認 Firestore Rules。"
          : "報表匯入失敗，請確認 CSV 格式後再試。",
      );
    } finally {
      setImportReportSubmitting(false);
    }
  }

  async function createReport(event) {
    event.preventDefault();
    setCreateReportError("");

    if (!orgId) {
      setCreateReportError("尚未取得 orgId，無法建立報表。");
      return;
    }

    if (!createReportForm.reportType) {
      setCreateReportError("請選擇報表類型。");
      return;
    }

    const selectedDriver = availableDrivers.find(
      (driver) => driver.uid === createReportForm.driverId,
    );
    const selectedVehicle = availableVehicles.find(
      (vehicle) => vehicle.id === createReportForm.vehicleId,
    );
    const driverId = text(createReportForm.driverId || accountUid);
    const driverName =
      text(selectedDriver?.displayName) ||
      (driverId === accountUid ? text(accountProfile?.displayName) : "") ||
      "未指定成員";
    const vehiclePlate =
      text(
        selectedVehicle?.plateNo ||
          selectedVehicle?.licensePlate ||
          selectedVehicle?.plate,
      ) || "";
    const occurredAt = createReportForm.occurredAt
      ? new Date(createReportForm.occurredAt)
      : new Date();
    const base = {
      orgId,
      reportType: createReportForm.reportType,
      title: REPORT_CATALOG[createReportForm.reportType]?.title || "新增報表",
      status: createReportForm.status || "pending",
      severity: createReportForm.severity || "normal",
      occurredAt,
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdByUid: accountUid,
      createdByRole: memberRole,
      createdByName: text(accountProfile?.displayName || user?.displayName),
      driverId,
      driverName,
      vehicleId: text(createReportForm.vehicleId),
      vehiclePlate,
    };

    if (createReportForm.reportType === "safety") {
      const inspectionItems = [
        ["fuel", "燃料油、機油、水", createReportForm.safetyFuel],
        ["lights", "各燈光、雨刷、照後鏡、喇叭", createReportForm.safetyLights],
        [
          "battery",
          "電瓶、胎壓、胎紋、風扇、皮帶、冷氣",
          createReportForm.safetyBattery,
        ],
        ["brake", "轉向、煞車系統", createReportForm.safetyBrake],
        [
          "dashboard",
          "儀表板警示燈、行車記錄器",
          createReportForm.safetyDashboard,
        ],
        ["fire", "滅火器壓力及有效期限", createReportForm.safetyFire],
        ["repair", "待修理或需更換部分", createReportForm.safetyRepair],
        ["cargo", "貨物是否綑綁穩妥、覆蓋嚴密", createReportForm.safetyCargo],
        ["driver", "駕駛人確認", createReportForm.safetyDriver],
      ].filter(([, , value]) => text(value));
      base.inspection = {
        odometer: number(createReportForm.odometer),
        abnormalCount:
          inspectionItems.filter(([, , value]) =>
            ["異常", "不正常", "否", "ng", "fail"].includes(
              text(value).toLowerCase(),
            ),
          ).length || number(createReportForm.abnormalCount),
        canDepart: createReportForm.canDepart === "true",
        items: inspectionItems.map(([key, label, value]) => ({
          key,
          label,
          value: text(value),
          normal: !["異常", "不正常", "否", "ng", "fail"].includes(
            text(value).toLowerCase(),
          ),
          status: ["異常", "不正常", "否", "ng", "fail"].includes(
            text(value).toLowerCase(),
          )
            ? "abnormal"
            : "normal",
        })),
        summary: text(createReportForm.summary),
      };
    }

    if (createReportForm.reportType === "attendance") {
      base.attendance = {
        month: text(createReportForm.attendanceMonth),
        expectedDays: number(createReportForm.expectedDays),
        actualDays: number(createReportForm.actualDays),
        leaveDays: number(createReportForm.leaveDays),
        attendanceRate: number(createReportForm.expectedDays)
          ? number(createReportForm.actualDays) /
            number(createReportForm.expectedDays)
          : 0,
        note: text(createReportForm.note),
      };
    }

    if (["fuel", "expense"].includes(createReportForm.reportType)) {
      base.expense = {
        category: text(createReportForm.category) || "other",
        item: text(createReportForm.category) || "other",
        amount: number(createReportForm.amount),
        liters: number(createReportForm.liters),
        odometer: number(createReportForm.odometer),
        vendor: text(createReportForm.vendor),
        note: text(createReportForm.note),
      };
    }

    if (createReportForm.reportType === "maintenance") {
      base.maintenance = {
        item: text(createReportForm.maintenanceItem),
        odometer: number(createReportForm.odometer),
        amount: number(createReportForm.amount),
        vendor: text(createReportForm.vendor),
        note: text(createReportForm.note),
      };
    }

    if (createReportForm.reportType === "incident") {
      base.incident = {
        category: text(createReportForm.incidentCategory) || "other",
        reporter: text(createReportForm.reporter) || driverName,
        time: createReportForm.incidentTime
          ? new Date(createReportForm.incidentTime)
          : occurredAt,
        description: text(createReportForm.incidentDescription),
        followUp: text(createReportForm.followUp),
      };
      base.description = text(createReportForm.incidentDescription);
    }

    if (createReportForm.reportType === "transport") {
      base.applicationId = text(createReportForm.taskNo);
      base.scheduleSnapshot = {
        startTimeISO: createReportForm.startTime
          ? new Date(createReportForm.startTime).toISOString()
          : "",
        endTimeISO: createReportForm.endTime
          ? new Date(createReportForm.endTime).toISOString()
          : "",
        distanceKm: number(createReportForm.distanceKm),
      };
      base.stops = [
        { sequence: 0, type: "pickup", text: text(createReportForm.loading) },
        {
          sequence: 1,
          type: "dropoff",
          text: text(createReportForm.unloading),
        },
      ];
      base.note = text(createReportForm.note);
    }

    if (createReportForm.reportType === "breath") {
      base.breathalyzer = {
        time: createReportForm.breathTime
          ? new Date(createReportForm.breathTime)
          : occurredAt,
        value: number(createReportForm.breathValue),
      };
      base.note = text(createReportForm.note);
    }

    if (createReportForm.reportType !== "incident") {
      base.note = text(createReportForm.note || base.note);
    }

    let uploadedIncidentAssets = [];
    try {
      setCreateReportSubmitting(true);
      if (createReportForm.reportType === "incident") {
        base.photos = [];
        const selectedPhotos = Array.isArray(createReportForm.incidentPhotos)
          ? createReportForm.incidentPhotos.filter((photo) => photo?.file)
          : [];
        if (selectedPhotos.length > INCIDENT_PHOTO_LIMIT) throw new Error("incident_photo_limit_exceeded");
        if (selectedPhotos.length) {
          const uploadResults = await Promise.allSettled(
            selectedPhotos.map((photo) => uploadIncidentReportPhoto(photo.file, orgId, accountUid)),
          );
          uploadedIncidentAssets = uploadResults
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value);
          const failedUpload = uploadResults.find((result) => result.status === "rejected");
          if (failedUpload) throw failedUpload.reason;
          base.photos = uploadedIncidentAssets.map((item) => item.reference);
        }
      }
      const type = reportKey(base);
      const day = dateKey(occurredAt);
      const dayRef = reportDayRef(orgId, day);
      const reportRef = doc(reportTypeItemsRef(orgId, day, type));
      const summaryRef = doc(reportSummaryItemsRef(orgId, day), reportRef.id);
      const status = getStatus(base);
      const summary = reportSummary(
        { ...base, reportType: type },
        reportRef.id,
        day,
      );

      if (type === "incident") {
        const reportItemPayload = { ...base, reportId: reportRef.id, dateKey: day, reportType: type };
        const daySummaryPayload = {
          dateKey: day,
          reportCount: increment(1),
          [`typeCounts.${type}`]: increment(1),
          [`statusCounts.${status}`]: increment(1),
          abnormalCount: increment(0),
          openIncidentCount:
            !["resolved", "completed", "approved"].includes(status) ? increment(1) : increment(0),
          expenseTotal: increment(0),
          updatedAt: serverTimestamp(),
        };
        const typeSummaryPayload = {
          reportType: type,
          count: increment(1),
          [`statusCounts.${status}`]: increment(1),
          updatedAt: serverTimestamp(),
        };
        const batch = writeBatch(db);
        batch.set(reportRef, reportItemPayload, { merge: true });
        batch.set(summaryRef, summary, { merge: true });
        batch.set(dayRef, daySummaryPayload, { merge: true });
        batch.set(doc(dayRef, "types", type), typeSummaryPayload, { merge: true });
        await batch.commit();
      } else {
        await Promise.all([
          setDoc(
            reportRef,
            { ...base, reportId: reportRef.id, dateKey: day, reportType: type },
            { merge: true },
          ),
          setDoc(summaryRef, summary, { merge: true }),
          setDoc(
            dayRef,
            {
              dateKey: day,
              reportCount: increment(1),
              [`typeCounts.${type}`]: increment(1),
              [`statusCounts.${status}`]: increment(1),
              abnormalCount:
                type === "safety"
                  ? increment(number(base?.inspection?.abnormalCount))
                  : increment(0),
              openIncidentCount:
                type === "incident" &&
                !["resolved", "completed", "approved"].includes(status)
                  ? increment(1)
                  : increment(0),
              expenseTotal: ["expense", "fuel"].includes(type)
                ? increment(expenseAmount(base))
                : increment(0),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          ),
          setDoc(
            doc(dayRef, "types", type),
            {
              reportType: type,
              count: increment(1),
              [`statusCounts.${status}`]: increment(1),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          ),
        ]);
      }
      releaseIncidentPhotoPreviews(createReportForm.incidentPhotos);
      setCreateReportForm((current) => ({ ...current, incidentPhotos: [] }));
      setCreateReportOpen(false);
      setSuccessMessage("報表建立成功。");
    } catch (error) {
      if (uploadedIncidentAssets.length) {
        await Promise.allSettled(uploadedIncidentAssets.map(removeIncidentReportPhotoAsset));
      }
      console.error("[DriverManagerReports] create report error:", error);
      const photoUploadFailed = text(error?.message).startsWith("incident_photo") || text(error?.code).startsWith("storage/");
      setCreateReportError(
        error?.code === "permission-denied"
          ? "沒有新增報表的權限，請確認 Firestore Rules。"
          : photoUploadFailed
            ? "異常照片上傳失敗，尚未建立回報；請檢查照片或網路後重試。"
            : "報表建立失敗，請稍後再試。",
      );
    } finally {
      setCreateReportSubmitting(false);
    }
  }

  async function updateStatus(report, nextStatus) {
    if (!canManageStatus || !orgId || !report?.id || updatingId) {
      return;
    }

    try {
      setUpdatingId(report.id);

      const type = reportKey(report);
      const oldStatus = getStatus(report);
      const normalizedNextStatus = nextStatus === "completed" ? "completed" : "pending";
      const day = text(report?.dateKey) || selectedDateKey;
      const dayRef = reportDayRef(orgId, day);
      const common = {
        status: normalizedNextStatus,
        reviewedBy: text(user?.uid),
        reviewedByName: text(account?.displayName || user?.displayName),
        reviewedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const statusCountPatch =
        oldStatus === normalizedNextStatus
          ? {}
          : {
              [`statusCounts.${oldStatus}`]: increment(-1),
              [`statusCounts.${normalizedNextStatus}`]: increment(1),
            };

      await Promise.all([
        setDoc(doc(dayRef, "types", type, "items", report.id), common, {
          merge: true,
        }),
        setDoc(doc(dayRef, "items", report.id), common, { merge: true }),
        setDoc(
          dayRef,
          {
            ...statusCountPatch,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
        setDoc(
          doc(dayRef, "types", type),
          {
            ...statusCountPatch,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        ),
      ]);
    } catch (error) {
      console.error("[DriverManagerReports] update status error:", error);

      window.alert(
        error?.code === "permission-denied"
          ? "沒有更新這筆報表的權限。"
          : "狀態更新失敗，請稍後再試。",
      );
    } finally {
      setUpdatingId("");
    }
  }

  function toggleReportStatus(report) {
    if (!canManageStatus || !report?.id) return;
    updateStatus(
      report,
      getStatus(report) === "completed" ? "pending" : "completed",
    );
  }

  const shiftDate = (amount) => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + amount);
    setSelectedDate(next);
    setSelectedDateKey(dateKey(next));
  };

  const loadMoreReports = async () => {
    if (!orgId || !selectedDateKey || !hasMore || pageLoading || !listCursor)
      return;

    setPageLoading(true);
    try {
      const itemsRef =
        activeType === "all"
          ? reportSummaryItemsRef(orgId, selectedDateKey)
          : reportTypeItemsRef(orgId, selectedDateKey, activeType);
      const nextQuery = query(
        itemsRef,
        orderBy("occurredAt", "desc"),
        startAfter(listCursor),
        limit(REPORT_PAGE_SIZE),
      );
      const snapshot = await getDocs(nextQuery);
      setReports((current) => [
        ...current,
        ...snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
      ]);
      setListCursor(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === REPORT_PAGE_SIZE);
    } catch (error) {
      console.error("[DriverManagerReports] load more error:", error);
      setLoadError("更多報表讀取失敗，請稍後再試。");
    } finally {
      setPageLoading(false);
    }
  };

  async function openReport(summary) {
    if (!orgId || !summary?.id) return;
    if (["cargo", "attendance", "transport"].includes(reportKey(summary)) && onOpenCargoReport) {
      onOpenCargoReport(summary, orgId);
      return;
    }
    setSelectedReport(summary);
    const type = reportKey(summary);
    try {
      const snapshot = await getDoc(
        doc(
          db,
          "orgs",
          orgId,
          "reportDays",
          selectedDateKey,
          "types",
          type,
          "items",
          summary.id,
        ),
      );
      if (snapshot.exists()) {
        setSelectedReport({
          id: snapshot.id,
          ...snapshot.data(),
          _detailLoaded: true,
        });
      }
    } catch (error) {
      console.error("[DriverManagerReports] report detail error:", error);
      setLoadError("報表詳細內容讀取失敗，請稍後再試。");
    }
  }

  function openExportReport() {
    setExportReportType(activeType === "all" ? "safety" : activeType);
    setExportReportStartDate(selectedDateKey);
    setExportReportEndDate(selectedDateKey);
    setExportReportError("");
    setExportReportOpen(true);
  }

  function closeExportReport() {
    if (exportReportSubmitting) return;
    setExportReportOpen(false);
    setExportReportError("");
  }

  async function exportCSV() {
    setExportReportError("");

    if (!orgId) {
      setExportReportError("尚未取得 orgId，無法匯出報表。");
      return;
    }

    if (!exportReportType || !REPORT_CATALOG[exportReportType]) {
      setExportReportError("請先選擇要匯出的報表項目。");
      return;
    }

    const days = dateKeysBetween(exportReportStartDate, exportReportEndDate);
    if (!days.length || days.length > 365) {
      setExportReportError("日期區間最多只能匯出 365 天。");
      return;
    }

    try {
      setExportReportSubmitting(true);
      const records = (["cargo", "attendance", "transport"].includes(exportReportType)
        ? (await loadChunkRecords(orgId, days, exportReportType)).records
        : (await Promise.all(
            days.map((day) => getDocs(reportTypeItemsRef(orgId, day, exportReportType))),
          )).flatMap((snapshot) =>
            snapshot.docs.map((item) => ({ id: item.id, ...item.data() })),
          )
      ).sort(
        (a, b) =>
          (reportDate(b)?.getTime() || 0) - (reportDate(a)?.getTime() || 0),
      );

      if (!records.length) {
        setExportReportError("選定的日期區間與報表項目沒有資料。");
        return;
      }

      const configuredColumns = REPORT_CATALOG[exportReportType].columns;
      const exportColumns = [
        ["__id", "報表編號"],
        ["__dateTime", "日期時間"],
        ["driver", "成員"],
        ["vehicle", "車牌"],
        ...configuredColumns,
        ["status", "狀態"],
        ["note", "備註"],
      ].filter(
        ([key], index, columns) =>
          columns.findIndex(([candidate]) => candidate === key) === index,
      );
      const rows = [exportColumns.map(([, label]) => label)];
      records.forEach((report) => {
        rows.push(
          exportColumns.map(([key]) => {
            if (key === "__id") return report.id;
            if (key === "__dateTime") return report.dateKey || fmtDateTime(reportDate(report));
            return reportColumnValue(report, key);
          }),
        );
      });
      const csv = rows
        .map((row) =>
          row
            .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");
      const blob = new Blob([`\uFEFF${csv}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Tallarge_${REPORT_CATALOG[exportReportType].short}_${exportReportStartDate}_${exportReportEndDate}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportReportOpen(false);
      setSuccessMessage(
        `已匯出 ${records.length} 筆${REPORT_CATALOG[exportReportType].title}明細。`,
      );
    } catch (error) {
      console.error("[DriverManagerReports] export reports error:", error);
      setExportReportError(
        error?.code === "permission-denied"
          ? "沒有讀取報表明細的權限，請確認 Firestore Rules。"
          : "報表匯出失敗，請稍後再試。",
      );
    } finally {
      setExportReportSubmitting(false);
    }
  }

  function handleReportTypeSelect(type) {
    setActiveType(type);
  }

  return (
    <div className="dmr-root">
      <style>{REPORT_STYLES}</style>
      <style>{`
        .dmr-root{--line:#E8EDF2;--soft:#F8FAFC;--muted:#64748B;--muted2:#94A3B8;--ink:#111827;--text:#334155;background:#FFFFFF!important;color:#334155}
        .dmr-page-header{max-width:none!important;margin-left:0!important;margin-right:0!important}
        .dmr-page-header h1{color:#111827!important;font-size:25px!important;font-weight:900!important;line-height:1.12;letter-spacing:-.4px!important}
        .dmr-date-navigator{border:1px solid rgba(148,163,184,.14);border-radius:16px;background:#FFFFFF;box-shadow:0 5px 16px rgba(15,23,42,.04)}
        .dmr-date-navigator>button{border-color:#E5E7EB;border-radius:10px;background:#F8FAFC;color:#475569}
        .dmr-date-navigator label span{color:#94A3B8}
        .dmr-date-navigator input{color:#111827;font-weight:850}
        .dmr-report-catalog{max-width:none!important;margin-left:0!important;margin-right:0!important;border:0;background:transparent}
        .dmr-report-catalog-heading h2{color:#111827;font-size:15px;font-weight:900}
        .dmr-report-catalog-card{border-color:rgba(148,163,184,.16);border-radius:14px;background:#FFFFFF;color:#334155;box-shadow:0 2px 8px rgba(15,23,42,.035)}
        .dmr-report-catalog-card:hover,.dmr-report-catalog-card.is-active{border-color:#F0C69F;background:#FFF8F1;box-shadow:0 5px 14px rgba(232,120,32,.08);transform:none}
        .dmr-report-tab-label,.dmr-report-catalog-copy strong{color:#1F2937;font-weight:850}
        .dmr-report-catalog-count{background:#F1F5F9;color:#64748B}
        .dmr-report-catalog-card.is-active .dmr-report-catalog-count{background:#FFF0E2;color:#C66C27}
        .dmr-main-card{max-width:none!important;border:1px solid rgba(148,163,184,.14);border-radius:22px;background:#FFFFFF;box-shadow:0 12px 28px rgba(15,23,42,.05)}
        .dmr-toolbar,.dmr-list-heading{background:#FFFFFF}
        .dmr-toolbar{border-bottom:1px solid #EEF2F7;border-left:1px solid #EEF2F7}
        .dmr-search{border-color:#E5E7EB;border-radius:11px;background:#F8FAFC}
        .dmr-filters label{border-color:#E5E7EB;border-radius:11px;background:#FFFFFF}
        .dmr-list-heading{border-color:#EEF2F7}
        .dmr-list-heading strong{color:#111827;font-size:14px;font-weight:850}
        .dmr-list-heading span{border-radius:999px;background:#F1F5F9;color:#64748B;font-size:10px;font-weight:800}
        .dmr-first-table-wrap{border-color:#EEF2F7}
        .dmr-first-table th{background:#F8FAFC;color:#64748B;font-weight:850}
        .dmr-first-table td{border-color:#EEF2F7;color:#334155}
        .dmr-stat-card{border-color:rgba(148,163,184,.14);border-radius:16px;background:#FFFFFF;box-shadow:0 3px 12px rgba(15,23,42,.035)}
        .dmr-expense-summary{border-color:#EEF2F7;border-radius:16px;background:#F8FAFC}
        .dmr-create-button{border-radius:11px;background:#111827;color:#FFFFFF;font-weight:850;box-shadow:0 6px 14px rgba(15,23,42,.08)}
        .dmr-create-button:hover:not(:disabled){background:#26333E}
        .dmr-export-button{border-color:#E5E7EB;border-radius:11px;background:#F8FAFC;color:#475569;font-weight:800}
        .dmr-export-button:hover{border-color:#CBD5E1;background:#F1F5F9}
        .dmr-update-button{border-color:#B9D4F4;background:#F1F7FF;color:#245E9F}
        .dmr-modal{border:1px solid rgba(148,163,184,.16);border-radius:20px;box-shadow:0 28px 72px rgba(15,23,42,.2)}
        .dmr-modal-header,.dmr-modal-footer{border-color:#EEF2F7;background:#FFFFFF}
        @media(min-width:981px){.dmr-root{width:auto!important;box-sizing:border-box!important;padding:12px 22px 22px!important}}
        @media(max-width:680px){
          .dmr-page-header h1{font-size:24px!important}
          .dmr-main-card{border-radius:18px}
          .dmr-date-navigator{border-radius:14px}
        }
      `}</style>

      <header className="dmr-page-header">
        <div>
          <div className="dmr-eyebrow">REPORT CENTER</div>
          <h1>報表中心</h1>
        </div>
      </header>

      {loadError && (
        <div className="dmr-error-banner">
          <Icon name="alert" size={18} />

          <span>{loadError}</span>
        </div>
      )}

      {successMessage && (
        <div className="dmr-success-banner">
          <Icon name="check" size={18} />
          <span>{successMessage}</span>
        </div>
      )}

      <section className="dmr-date-navigator" aria-label="報表日期">
        <button type="button" onClick={() => shiftDate(-1)} aria-label="前一天">
          ‹
        </button>
        <label>
          <span>報表日期</span>
          <input
            type="date"
            value={selectedDateKey}
            onChange={(event) => {
              const next = dateFromKey(event.target.value);
              setSelectedDate(next);
              setSelectedDateKey(event.target.value);
            }}
          />
        </label>
        <button type="button" onClick={() => shiftDate(1)} aria-label="後一天">
          ›
        </button>
        <div className="dmr-header-actions dmr-date-actions">
          {activeType === "attendance" && attendanceHasUpdate ? (
            <button
              type="button"
              className="dmr-export-button dmr-import-button dmr-update-button"
              onClick={() => setAttendanceReloadToken((value) => value + 1)}
              disabled={loading}
              title="報表資料已更新，點擊後重新讀取最新資料"
            >
              有資料更新，點擊更新
            </button>
          ) : null}
          {activeType === "transport" && transportHasUpdate ? (
            <button
              type="button"
              className="dmr-export-button dmr-import-button dmr-update-button"
              onClick={() => setTransportReloadToken((value) => value + 1)}
              disabled={loading}
              title="運輸站點資料已更新，點擊後重新讀取最新資料"
            >
              有資料更新，點擊更新
            </button>
          ) : null}
          {!['cargo', 'attendance', 'transport'].includes(activeType) ? (
            <button
              type="button"
              className="dmr-export-button dmr-import-button"
              onClick={openImportReport}
              disabled={profileLoading || importReportSubmitting}
            >
              <Icon name="upload" size={17} />
              匯入報表
            </button>
          ) : null}
          <button
            type="button"
            className="dmr-export-button"
            onClick={openExportReport}
            disabled={profileLoading || exportReportSubmitting}
          >
            <Icon name="download" size={17} />
            匯出報表
          </button>
          <button
            type="button"
            className="dmr-create-button"
            onClick={openCreateReport}
            disabled={!orgId || profileLoading || createReportOpen}
          >
            ＋ 新增報表
          </button>
        </div>
      </section>

      <ReportCatalogCards
        activeType={activeType}
        typeCounts={typeCounts}
        onSelect={handleReportTypeSelect}
      />

      <section className="dmr-main-card">
        <div className="dmr-toolbar">
          <div className="dmr-search">
            <Icon name="search" size={18} />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜尋成員、車牌、工作或報表內容"
            />

            {search && (
              <button
                type="button"
                aria-label="清除搜尋"
                onClick={() => setSearch("")}
              >
                <Icon name="close" size={15} />
              </button>
            )}
          </div>

          <div className="dmr-filters">
            <label>
              <Icon name="user" size={15} />
              <select
                value={driverFilter}
                onChange={(e) => setDriverFilter(e.target.value)}
              >
                <option value="all">全部成員</option>
                {reportFilterOptions.drivers.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <Icon name="truck" size={15} />
              <select
                value={vehicleFilter}
                onChange={(e) => setVehicleFilter(e.target.value)}
              >
                <option value="all">全部車牌</option>
                {reportFilterOptions.vehicles.map((vehicle) => (
                  <option key={vehicle} value={vehicle}>
                    {vehicle}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <Icon name="filter" size={15} />

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">全部狀態</option>

                <option value="pending">待處理</option>

                <option value="reviewing">處理中</option>

                <option value="resolved">已完成</option>
              </select>
            </label>
          </div>
        </div>

        {activeType === "expense" && !!expenseBreakdown.length && (
          <div className="dmr-expense-summary">
            <div className="dmr-expense-summary-heading">
              <div>
                <span>費用結構</span>

                <strong>
                  {money(
                    expenseBreakdown.reduce(
                      (total, item) => total + item.amount,
                      0,
                    ),
                  )}
                </strong>
              </div>

              <small>目前篩選期間</small>
            </div>

            <div className="dmr-expense-bars">
              {expenseBreakdown.slice(0, 5).map((item, index) => {
                const max = expenseBreakdown[0]?.amount || 1;

                const width = Math.max(
                  6,
                  Math.round((item.amount / max) * 100),
                );

                return (
                  <div className="dmr-expense-bar-row" key={item.category}>
                    <span>{item.label}</span>

                    <div>
                      <i
                        style={{
                          width: `${width}%`,
                        }}
                      />
                    </div>

                    <strong>{money(item.amount)}</strong>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="dmr-list-heading">
          <div>
            <strong>{selectedDateKey} 報表</strong>

            <span>共 {filteredReports.length} 筆</span>
          </div>
        </div>

        {loading ? (
          <div className="dmr-loading">
            <span className="dmr-spinner" />

            <p>正在讀取報表…</p>
          </div>
        ) : !filteredReports.length && !createReportOpen ? (
          <EmptyState activeType={activeType} />
        ) : (
          <>
            <ReportTable
              reports={filteredReports}
              activeType={activeType}
              onSelect={openReport}
              onShowCargoQr={setSelectedCargoQr}
              onShowCargoDetail={setSelectedCargoDetail}
              onShowCargoRoute={setSelectedCargoRoute}
              canManageStatus={canManageStatus}
              onToggleStatus={toggleReportStatus}
              updatingId={updatingId}
              canManage={isPrimaryAdmin}
              onEdit={openEditReport}
              onDelete={deleteReport}
              deletingReportId={deletingReportId}
              draft={createReportOpen ? createReportForm : null}
              drivers={availableDrivers}
              vehicles={availableVehicles}
              submitting={createReportSubmitting}
              onDraftChange={updateCreateReportField}
              onIncidentPhotoSelect={handleIncidentPhotoSelection}
              onIncidentPhotoRemove={removeIncidentPhoto}
              onDraftSubmit={() => createReport({ preventDefault() {} })}
              onDraftCancel={closeCreateReport}
            />
          </>
        )}
      </section>

      {exportReportOpen ? (
        <div
          className="dmr-modal-layer dmr-action-modal-layer"
          role="dialog"
          aria-modal="true"
          aria-label="匯出報表"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeExportReport();
          }}
        >
          <div
            className="dmr-modal dmr-export-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="dmr-modal-header">
              <div className="dmr-modal-heading">
                <span className="dmr-modal-icon dmr-tone-orange">
                  <Icon name="download" size={20} />
                </span>
                <div>
                  <h2>匯出報表</h2>
                </div>
              </div>
              <button
                type="button"
                className="dmr-icon-button"
                onClick={closeExportReport}
                aria-label="關閉匯出視窗"
              >
                <Icon name="close" size={18} />
              </button>
            </header>
            <div className="dmr-modal-body">
              <div className="dmr-import-grid">
                <label className="dmr-create-field dmr-create-field-wide">
                  <span>報表項目</span>
                  <select
                    value={exportReportType}
                    onChange={(event) => setExportReportType(event.target.value)}
                    disabled={exportReportSubmitting}
                  >
                    {REPORT_KEYS.filter((key) => key !== "all").map((key) => (
                      <option key={key} value={key}>
                        {REPORT_TAB_LABELS[key] || REPORT_CATALOG[key].title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dmr-create-field">
                  <span>開始日期</span>
                  <input
                    type="date"
                    value={exportReportStartDate}
                    onChange={(event) => setExportReportStartDate(event.target.value)}
                    disabled={exportReportSubmitting}
                  />
                </label>
                <label className="dmr-create-field">
                  <span>結束日期</span>
                  <input
                    type="date"
                    value={exportReportEndDate}
                    onChange={(event) => setExportReportEndDate(event.target.value)}
                    disabled={exportReportSubmitting}
                  />
                </label>
                {exportReportError ? (
                  <div className="dmr-import-error" role="alert">
                    <Icon name="alert" size={16} />
                    <span>{exportReportError}</span>
                  </div>
                ) : null}
              </div>
            </div>
            <footer className="dmr-modal-footer">
              <div>
                <button
                  type="button"
                  className="dmr-secondary-action"
                  onClick={closeExportReport}
                  disabled={exportReportSubmitting}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="dmr-primary-action"
                  onClick={exportCSV}
                  disabled={exportReportSubmitting}
                >
                  {exportReportSubmitting ? "整理中…" : "開始匯出"}
                </button>
              </div>
            </footer>
          </div>
        </div>
      ) : null}

      {importReportOpen ? (
        <div
          className="dmr-modal-layer dmr-action-modal-layer"
          role="dialog"
          aria-modal="true"
          aria-label="匯入報表"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeImportReport();
          }}
        >
          <div
            className="dmr-modal dmr-import-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="dmr-modal-header">
              <div className="dmr-modal-heading">
                <span className="dmr-modal-icon dmr-tone-blue">
                  <Icon name="upload" size={20} />
                </span>
                <div>
                  <h2>匯入報表</h2>
                </div>
              </div>
              <button
                type="button"
                className="dmr-icon-button"
                onClick={closeImportReport}
                aria-label="關閉匯入視窗"
              >
                <Icon name="close" size={18} />
              </button>
            </header>
            <div className="dmr-modal-body">
              <div className="dmr-import-grid">
                <label className="dmr-create-field">
                  <span>匯入項目</span>
                  <select
                    value={importReportType}
                    onChange={(event) => setImportReportType(event.target.value)}
                    disabled={importReportSubmitting}
                  >
                    {REPORT_KEYS.filter((key) => key !== "all" && key !== "cargo").map((key) => (
                      <option key={key} value={key}>
                        {REPORT_TAB_LABELS[key] || REPORT_CATALOG[key].title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="dmr-create-field">
                  <span>報表日期</span>
                  <input
                    type="date"
                    value={importReportDate}
                    onChange={(event) => setImportReportDate(event.target.value)}
                    disabled={importReportSubmitting}
                  />
                </label>
                <div className="dmr-create-field dmr-create-field-wide">
                  <span>CSV 檔案</span>
                  <label className="dmr-import-file-picker">
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(event) => {
                        setImportReportFile(event.target.files?.[0] || null);
                        setImportReportError("");
                      }}
                      disabled={importReportSubmitting}
                    />
                    <span className="dmr-import-file-trigger">選擇 CSV</span>
                    <span className="dmr-import-file-label">
                      {importReportFile?.name || "尚未選擇檔案"}
                    </span>
                  </label>
                </div>
                {importReportFile ? (
                  <p className="dmr-import-file-name">
                    已選擇：{importReportFile.name}
                  </p>
                ) : null}
                {importReportError ? (
                  <div className="dmr-import-error" role="alert">
                    <Icon name="alert" size={16} />
                    <span>{importReportError}</span>
                  </div>
                ) : null}
              </div>
            </div>
            <footer className="dmr-modal-footer">
              <div>
                <button
                  type="button"
                  className="dmr-secondary-action"
                  onClick={closeImportReport}
                  disabled={importReportSubmitting}
                >
                  取消
                </button>
                <button
                  type="button"
                  className="dmr-primary-action"
                  onClick={importReports}
                  disabled={importReportSubmitting}
                >
                  {importReportSubmitting ? "匯入中…" : "直接匯入"}
                </button>
              </div>
            </footer>
          </div>
        </div>
      ) : null}

      {editingReport ? (
        <div
          className="dmr-modal-layer"
          role="dialog"
          aria-modal="true"
          aria-label="編輯報表"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeEditReport();
          }}
        >
          <form
            className="dmr-modal dmr-edit-modal"
            onSubmit={saveEditReport}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="dmr-modal-header">
              <div className="dmr-modal-heading">
                <span className="dmr-modal-icon dmr-tone-orange">
                  <Icon name="clipboard" size={20} />
                </span>
                <div>
                  <small>OWNER EDIT</small>
                  <h2>編輯報表</h2>
                </div>
              </div>
              <button
                type="button"
                className="dmr-icon-button"
                onClick={closeEditReport}
                aria-label="關閉編輯視窗"
              >
                <Icon name="close" size={18} />
              </button>
            </header>
            <div className="dmr-modal-body">
              <div className="dmr-edit-summary">
                <span>{REPORT_CATALOG[reportKey(editingReport)]?.title}</span>
                <strong>{getReportTitle(editingReport)}</strong>
              </div>
              <div className="dmr-import-grid">
                <label className="dmr-create-field">
                  <span>成員</span>
                  <input
                    name="driverName"
                    value={editReportForm.driverName}
                    onChange={updateEditReportField}
                    disabled={editReportSubmitting}
                    placeholder="輸入成員名稱或 UID"
                  />
                </label>
                <label className="dmr-create-field">
                  <span>車牌</span>
                  <input
                    name="vehiclePlate"
                    value={editReportForm.vehiclePlate}
                    onChange={updateEditReportField}
                    disabled={editReportSubmitting}
                    placeholder="輸入車牌"
                  />
                </label>
                <label className="dmr-create-field">
                  <span>狀態</span>
                  <select
                    name="status"
                    value={editReportForm.status}
                    onChange={updateEditReportField}
                    disabled={editReportSubmitting}
                  >
                    <option value="pending">待處理</option>
                    <option value="completed">已完成</option>
                  </select>
                </label>
                <label className="dmr-create-field dmr-create-field-wide">
                  <span>備註</span>
                  <textarea
                    name="note"
                    rows="5"
                    value={editReportForm.note}
                    onChange={updateEditReportField}
                    disabled={editReportSubmitting}
                    placeholder="輸入備註"
                  />
                </label>
                {editReportError ? (
                  <div className="dmr-import-error" role="alert">
                    <Icon name="alert" size={16} />
                    <span>{editReportError}</span>
                  </div>
                ) : null}
              </div>
            </div>
            <footer className="dmr-modal-footer">
              <span>只有主管理員可以編輯報表內容。</span>
              <div>
                <button
                  type="button"
                  className="dmr-secondary-action"
                  onClick={closeEditReport}
                  disabled={editReportSubmitting}
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="dmr-primary-action"
                  disabled={editReportSubmitting}
                >
                  {editReportSubmitting ? "儲存中…" : "儲存變更"}
                </button>
              </div>
            </footer>
          </form>
        </div>
      ) : null}

      <CargoQrModal
        report={selectedCargoQr}
        onClose={() => setSelectedCargoQr(null)}
      />

      <CargoDetailDrawer
        report={selectedCargoDetail}
        orgId={orgId}
        onClose={() => setSelectedCargoDetail(null)}
      />

      <CargoRouteDrawer
        report={selectedCargoRoute}
        orgId={orgId}
        onClose={() => setSelectedCargoRoute(null)}
      />

      <ReportDetailModal
        report={selectedReport}
        onClose={() => setSelectedReport(null)}
        updating={updatingId === selectedReport?.id}
        canManageStatus={canManageStatus}
        onToggleStatus={toggleReportStatus}
      />

      {createReportError && createReportOpen ? (
        <div className="dmr-create-error" role="alert">
          <Icon name="alert" size={16} />
          <span>{createReportError}</span>
        </div>
      ) : null}
    </div>
  );
}

const REPORT_STYLES = `
.dmr-root{
  --orange:#F68B2C;
  --orangeDeep:#EA781C;
  --orangeSoft:#FFF2E7;
  --blue:#3276E8;
  --blueSoft:#EDF4FF;
  --green:#299B62;
  --greenSoft:#EAF7F0;
  --red:#E84C4C;
  --redSoft:#FFF0F0;
  --ink:#111827;
  --text:#25313F;
  --muted:#7B8794;
  --muted2:#9AA3AE;
  --line:#E9EDF1;
  --soft:#F7F8FA;
  --white:#FFFFFF;

  width:100%;
  min-width:0;
  height:calc(100vh - 110px);
  max-height:calc(100vh - 110px);
  padding:0 0 20px;
  overflow-y:auto;
  overflow-x:hidden;
  scrollbar-width:thin;
  scrollbar-color:#AEB8C2 transparent;
  color:var(--text);
  background:transparent;
  font-family:
    Inter,
    "Noto Sans TC",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
}

.dmr-root *{
  box-sizing:border-box;
}

.dmr-root button,
.dmr-root input,
.dmr-root select{
  font:inherit;
}

.dmr-root button{
  cursor:pointer;
}

.dmr-root button:disabled{
  cursor:not-allowed;
  opacity:.5;
}

.dmr-root h1,
.dmr-root h2,
.dmr-root h3,
.dmr-root p{
  margin:0;
}

.dmr-page-header{
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:24px;
  max-width:1560px;
  min-height:54px;
  margin:0 auto 14px;
}

.dmr-eyebrow{
  display:block;
  margin-bottom:10px;
  color:#A0A7AF;
  font-size:9px;
  font-weight:700;
  letter-spacing:2.1px;
}

.dmr-page-header h1{
  color:#16202A;
  font-size:30px;
  font-weight:760;
  letter-spacing:-1px;
}

.dmr-page-header p{
  margin-top:9px;
  color:var(--muted);
  font-size:13px;
}

.dmr-header-actions{
  display:flex;
  align-items:center;
  gap:10px;
}
.dmr-date-actions{
  flex:0 0 auto;
  margin-left:auto;
}

.dmr-create-button{
  min-height:42px;
  padding:0 15px;
  border:0;
  border-radius:9px;
  background:var(--orange);
  color:#FFFFFF;
  font-size:12px;
  font-weight:750;
}

.dmr-create-button:hover:not(:disabled){
  background:var(--orangeDeep);
}

.dmr-export-button{
  min-height:42px;
  padding:0 15px;
  border:1px solid #E4E8EC;
  border-radius:9px;
  background:#FFFFFF;
  color:#4E5965;
  display:flex;
  align-items:center;
  gap:8px;
  font-size:12px;
  font-weight:650;
}

.dmr-export-button:hover{
  border-color:#D4DADF;
  background:#FAFBFC;
}

.dmr-update-button{
  position:relative;
  border-color:#B9D4F4;
  background:#F1F7FF;
  color:#245E9F;
  box-shadow:0 0 0 0 rgba(75,139,214,.18);
  animation:dmrUpdatePulse 2.4s ease-in-out infinite;
}

.dmr-update-button::before{
  content:"";
  width:7px;
  height:7px;
  flex:0 0 7px;
  border-radius:50%;
  background:#4285C5;
  box-shadow:0 0 0 3px rgba(66,133,197,.12);
}

.dmr-update-button:hover:not(:disabled){
  border-color:#8CB8E9;
  background:#E8F2FF;
  color:#174F8E;
}

@keyframes dmrUpdatePulse{
  0%,100%{box-shadow:0 0 0 0 rgba(75,139,214,.08)}
  50%{box-shadow:0 0 0 5px rgba(75,139,214,.12)}
}

.dmr-error-banner{
  max-width:1560px;
  margin:0 auto 18px;
  padding:13px 15px;
  display:flex;
  align-items:center;
  gap:9px;
  border:1px solid #F1CCCC;
  border-radius:10px;
  background:#FFF1F1;
  color:#B64242;
  font-size:12px;
}

.dmr-success-banner{
  max-width:1560px;
  margin:0 auto 18px;
  padding:13px 15px;
  display:flex;
  align-items:center;
  gap:9px;
  border:1px solid #CBE8D8;
  border-radius:10px;
  background:#F0FBF4;
  color:#237848;
  font-size:12px;
}

.dmr-date-navigator{
  display:flex;
  align-items:center;
  gap:12px;
  max-width:1560px;
  margin:0 auto 18px;
  padding:12px 16px;
  border:1px solid var(--line);
  border-radius:18px;
  background:var(--white);
}
.dmr-date-navigator > button{
  width:34px;
  height:34px;
  border:1px solid var(--line);
  border-radius:11px;
  color:var(--ink);
  background:var(--soft);
  font-size:22px;
  line-height:1;
}
.dmr-date-navigator label{
  display:flex;
  flex-direction:column;
  gap:3px;
  min-width:170px;
}
.dmr-date-navigator label span{
  color:var(--muted);
  font-size:10px;
  font-weight:800;
}
.dmr-date-navigator input{
  border:0;
  outline:0;
  color:var(--ink);
  background:transparent;
  font-size:17px;
  font-weight:900;
}
.dmr-date-navigator .dmr-today-button{
  width:auto;
  padding:0 13px;
  color:var(--orangeDeep);
  background:var(--orangeSoft);
  font-size:12px;
}
.dmr-date-navigator strong{
  margin-left:auto;
  color:var(--muted);
  font-size:12px;
}
.dmr-load-more-button{
  display:block;
  min-height:42px;
  margin:14px auto 4px;
  padding:0 22px;
  border:1px solid #F5B278;
  border-radius:13px;
  color:var(--orangeDeep);
  background:var(--orangeSoft);
  font-weight:800;
}
.dmr-stats{
  max-width:1560px;
  margin:0 auto 18px;
  display:grid;
  grid-template-columns:
    repeat(5,minmax(0,1fr));
  gap:12px;
}

.dmr-stat-card{
  min-width:0;
  min-height:130px;
  padding:20px;
  display:flex;
  align-items:flex-start;
  gap:14px;
  border:1px solid var(--line);
  border-radius:14px;
  background:#FFFFFF;
}

.dmr-stat-icon{
  width:40px;
  height:40px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  border-radius:11px;
}

.dmr-tone-neutral{
  color:#596574;
  background:#F0F2F5;
}

.dmr-tone-orange{
  color:#D97624;
  background:#FFF0E2;
}

.dmr-tone-blue{
  color:#3475D8;
  background:#EAF2FF;
}

.dmr-tone-green{
  color:#31835D;
  background:#E8F6EE;
}

.dmr-tone-red{
  color:#D94D4D;
  background:#FFECEC;
}

.dmr-stat-copy{
  min-width:0;
}

.dmr-stat-copy>span{
  display:block;
  color:#7E8893;
  font-size:11px;
  font-weight:600;
}

.dmr-stat-copy strong{
  display:block;
  margin-top:7px;
  color:#18222D;
  font-size:24px;
  line-height:1.2;
  font-weight:680;
  letter-spacing:-.6px;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.dmr-stat-copy small{
  display:block;
  margin-top:7px;
  color:#9CA4AD;
  font-size:10px;
  line-height:1.5;
}

.dmr-report-catalog{
  max-width:1560px;
  margin:0 auto 12px;
  padding:0 0 10px;
  border-bottom:1px solid var(--line);
  background:transparent;
}

.dmr-report-catalog-heading{
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:16px;
  margin-bottom:10px;
}

.dmr-report-catalog-heading span{
  color:#A0A7AF;
  font-size:9px;
  font-weight:800;
  letter-spacing:1.8px;
}

.dmr-report-catalog-heading h2{
  margin-top:3px;
  color:#1D2833;
  font-size:17px;
  font-weight:800;
}

.dmr-report-catalog-heading small{
  color:#8B959E;
  font-size:11px;
}

.dmr-report-catalog-grid{
  display:grid;
  grid-template-columns:repeat(9,minmax(0,1fr));
  gap:6px;
  width:100%;
  overflow-x:hidden;
  padding:2px 2px 4px;
}

.dmr-report-catalog-card{
  min-width:0;
  min-height:42px;
  padding:7px 8px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:6px;
  border:1px solid #E9EDF1;
  border-radius:22px;
  background:#FFFFFF;
  color:#2D3944;
  text-align:left;
  transition:border-color .16s,box-shadow .16s,transform .16s,background .16s;
}

.dmr-report-catalog-card:hover,
.dmr-report-catalog-card.is-active{
  border-color:#F4B47D;
  background:#FFFCF9;
  box-shadow:0 7px 18px rgba(246,139,44,.09);
  transform:translateY(-1px);
}

.dmr-report-catalog-icon{
  width:24px;
  height:24px;
  flex:0 0 24px;
  display:grid;
  place-items:center;
  border-radius:8px;
}

.dmr-report-catalog-copy{
  display:grid;
  gap:1px;
  min-width:0;
  flex:1;
}

.dmr-report-tab-label{
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#2D3944;
  font-size:12px;
  font-weight:800;
}

.dmr-report-catalog-copy strong{
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#2D3944;
  font-size:12px;
  font-weight:800;
}

.dmr-report-catalog-copy small{
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#9AA3AB;
  font-size:9px;
}

.dmr-report-catalog-count{
  min-width:21px;
  height:21px;
  display:grid;
  place-items:center;
  border-radius:7px;
  background:#F3F5F7;
  color:#68737E;
  font-size:9px;
  font-weight:800;
}

.dmr-report-catalog-arrow{
  color:#B5BDC4;
  font-size:18px;
  line-height:1;
}

.dmr-main-card{
  max-width:1560px;
  min-width:0;
  margin:auto;
  display:grid;
  grid-template-columns:minmax(300px,.85fr) minmax(560px,1.55fr);
  border:1px solid var(--line);
  border-radius:17px;
  background:#FFFFFF;
  overflow:hidden;
}

.dmr-toolbar{
  grid-column:2;
  grid-row:1;
  padding:10px 16px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
  border-bottom:1px solid #EFF2F4;
  border-left:1px solid #EFF2F4;
}

.dmr-search{
  width:auto;
  flex:1 1 320px;
  min-width:220px;
  min-height:42px;
  padding:0 12px;
  display:flex;
  align-items:center;
  gap:9px;
  border:1px solid #E5E9ED;
  border-radius:9px;
  background:#FAFBFC;
  color:#8B959E;
}

.dmr-search:focus-within{
  border-color:#FFB574;
  background:#FFFFFF;
  box-shadow:0 0 0 3px #FFF4EB;
}

.dmr-search input{
  width:100%;
  min-width:0;
  border:0;
  outline:0;
  background:transparent;
  color:#303B46;
  font-size:12px;
}

.dmr-search input::placeholder{
  color:#A4ABB2;
}

.dmr-search button{
  width:27px;
  height:27px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  border:0;
  border-radius:7px;
  background:transparent;
  color:#8F99A2;
}

.dmr-filters{
  display:flex;
  align-items:center;
  flex:0 0 auto;
  flex-wrap:nowrap;
  gap:8px;
}

.dmr-filters label{
  min-width:118px;
  min-height:42px;
  padding-left:10px;
  display:flex;
  align-items:center;
  gap:6px;
  border:1px solid #E6EAEE;
  border-radius:9px;
  background:#FFFFFF;
  color:#8A949E;
}

.dmr-filters select{
  height:40px;
  width:100%;
  border:0;
  outline:0;
  padding:0 28px 0 2px;
  background:transparent;
  color:#525E69;
  font-size:11px;
}

.dmr-expense-summary{
  grid-column:1 / -1;
  margin:18px;
  padding:20px;
  display:grid;
  grid-template-columns:190px minmax(0,1fr);
  gap:28px;
  border-radius:13px;
  background:#F8FAFC;
  border:1px solid #EDF0F3;
}

.dmr-expense-summary-heading span{
  display:block;
  color:#86919C;
  font-size:11px;
}

.dmr-expense-summary-heading strong{
  display:block;
  margin-top:8px;
  color:#18222C;
  font-size:25px;
  font-weight:680;
}

.dmr-expense-summary-heading small{
  display:block;
  margin-top:8px;
  color:#9BA4AC;
  font-size:10px;
}

.dmr-expense-bars{
  display:grid;
  gap:10px;
}

.dmr-expense-bar-row{
  display:grid;
  grid-template-columns:70px minmax(80px,1fr) 92px;
  align-items:center;
  gap:10px;
}

.dmr-expense-bar-row>span{
  color:#66727D;
  font-size:10px;
}

.dmr-expense-bar-row>div{
  height:6px;
  overflow:hidden;
  border-radius:999px;
  background:#E8EDF2;
}

.dmr-expense-bar-row>div>i{
  height:100%;
  display:block;
  border-radius:999px;
  background:#5B8FE8;
}

.dmr-expense-bar-row>strong{
  color:#56616C;
  text-align:right;
  font-size:10px;
  font-weight:650;
}

.dmr-list-heading{
  grid-column:1;
  grid-row:1;
  min-height:57px;
  padding:0 18px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  border-bottom:1px solid var(--line);
}

.dmr-list-heading>div{
  display:flex;
  align-items:center;
  gap:8px;
}

.dmr-list-heading strong{
  color:#28333D;
  font-size:13px;
  font-weight:650;
}

.dmr-list-heading span{
  padding:3px 7px;
  border-radius:10px;
  background:#F1F3F5;
  color:#858E97;
  font-size:9px;
}

.dmr-list-heading small{
  color:#A0A7AE;
  font-size:10px;
}

.dmr-list-heading-actions{
  display:flex;
  align-items:center;
  gap:12px;
}

.dmr-inline-add-button{
  min-height:32px;
  padding:0 11px;
  border:1px solid #F0B47A;
  border-radius:7px;
  background:#FFF8F1;
  color:#C66C27;
  font-size:10px;
  font-weight:750;
  white-space:nowrap;
}

.dmr-inline-add-button:hover:not(:disabled){
  background:#FFF0E2;
}

.dmr-first-table-wrap{
  grid-column:1 / -1;
  width:100%;
  overflow:auto;
  border-top:1px solid #F0F2F4;
}

.dmr-empty-state-with-table{
  grid-column:1 / -1;
}

.dmr-first-table{
  width:100%;
  min-width:980px;
  border-collapse:collapse;
  table-layout:auto;
}

.dmr-first-table th{
  min-width:118px;
  height:48px;
  padding:0 14px;
  background:#FAFBFC;
  border-bottom:1px solid #E9EDF1;
  color:#6F7B86;
  text-align:left;
  font-size:13px;
  font-weight:800;
  white-space:nowrap;
}

.dmr-cell-ellipsis{
  display:block;
  max-width:180px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.dmr-first-table th .dmr-cell-ellipsis{
  max-width:150px;
}

.dmr-first-table th:first-child,
.dmr-first-table td:first-child{
  padding-left:20px;
}

.dmr-first-table th:last-child,
.dmr-first-table td:last-child{
  min-width:84px;
  padding-right:20px;
}

.dmr-first-table td{
  max-width:260px;
  padding:16px 14px;
  border-top:1px solid #F0F2F4;
  color:#3C4752;
  font-size:13px;
  line-height:1.5;
  vertical-align:middle;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}

.dmr-first-table .dmr-inline-row{
  background:#FFF9F3;
}

.dmr-first-table .dmr-inline-row:hover{
  background:#FFF4E8;
}

.dmr-inline-row td{
  max-width:220px;
  padding:8px 10px;
  white-space:normal;
  overflow:visible;
}

.dmr-inline-row input,
.dmr-inline-row select{
  width:100%;
  min-width:90px;
  height:34px;
  padding:0 7px;
  border:1px solid #E6D7C8;
  border-radius:6px;
  outline:none;
  background:#FFFFFF;
  color:#34414D;
  font-size:10px;
}

.dmr-inline-row input:focus,
.dmr-inline-row select:focus{
  border-color:#F0A45C;
  box-shadow:0 0 0 3px rgba(246,139,44,.12);
}

.dmr-inline-fixed{
  display:block;
  min-width:66px;
  color:#B76729;
  font-size:10px;
  font-weight:750;
}

.dmr-inline-actions{
  display:flex;
  align-items:center;
  gap:5px;
}

.dmr-inline-cancel,
.dmr-inline-submit{
  min-height:30px;
  padding:0 8px;
  border-radius:6px;
  font-size:9px;
  font-weight:700;
  white-space:nowrap;
}

.dmr-inline-cancel{
  border:1px solid #E3E6E9;
  background:#FFFFFF;
  color:#7A858F;
}

.dmr-inline-submit{
  border:0;
  background:#E87820;
  color:#FFFFFF;
}

.dmr-inline-submit:hover:not(:disabled){
  background:#D96815;
}

.dmr-inline-photo-row>td{
  padding:10px 12px;
  background:#FFFCF8;
  border-bottom:1px solid #F0E8DF;
}
.dmr-incident-photo-picker{
  display:flex;
  align-items:center;
  flex-wrap:wrap;
  gap:10px;
}
.dmr-incident-photo-copy{
  display:flex;
  flex-direction:column;
  gap:3px;
  min-width:145px;
  margin-right:auto;
}
.dmr-incident-photo-copy strong{color:#34414D;font-size:11px}
.dmr-incident-photo-copy span{color:#8B969F;font-size:9px}
.dmr-incident-photo-add{
  position:relative;
  display:inline-flex;
  align-items:center;
  gap:6px;
  min-height:31px;
  padding:0 10px;
  border:1px solid #E7C5A7;
  border-radius:7px;
  background:#FFF7EF;
  color:#B76729;
  font-size:10px;
  font-weight:750;
  cursor:pointer;
  overflow:hidden;
}
.dmr-incident-photo-add input{position:absolute;inset:0;width:100%;height:100%;opacity:0;cursor:pointer}
.dmr-incident-photo-add[aria-disabled="true"]{opacity:.5;cursor:not-allowed}
.dmr-incident-photo-previews{display:flex;flex-wrap:wrap;gap:6px}
.dmr-incident-photo-preview{position:relative;width:52px;height:52px;overflow:hidden;border:1px solid #E7E9EC;border-radius:7px;background:#EEF1F3}
.dmr-incident-photo-preview img{display:block;width:100%;height:100%;object-fit:cover}
.dmr-incident-photo-preview button{position:absolute;top:2px;right:2px;width:17px;height:17px;padding:0;border:0;border-radius:50%;background:rgba(24,35,45,.78);color:#fff;font-size:14px;line-height:16px;cursor:pointer}
.dmr-incident-photo-count{color:#8B969F;font-size:9px;font-weight:700}

.dmr-first-table tbody tr:hover{
  background:#FCFCFD;
}

.dmr-first-table .dmr-status{
  display:inline-flex;
}

.dmr-status{
  width:max-content;
  padding:5px 8px;
  display:inline-flex;
  align-items:center;
  gap:5px;
  border-radius:999px;
  font-size:11px;
  font-weight:650;
  white-space:nowrap;
}

.dmr-status i{
  width:5px;
  height:5px;
  border-radius:50%;
  background:currentColor;
}

.dmr-status-orange{
  background:#FFF0E2;
  color:#C97028;
}

.dmr-status-red{
  background:#FDECEC;
  color:#CE4747;
}

.dmr-status-blue{
  background:#EAF2FF;
  color:#3973CE;
}

.dmr-status-green{
  background:#E8F5EE;
  color:#35815D;
}

.dmr-status-gray{
  background:#EFF1F3;
  color:#747F89;
}

.dmr-status-button{
  padding:0;
  display:inline-flex;
  border:0;
  border-radius:999px;
  background:transparent;
}

.dmr-status-button:not(:disabled){
  cursor:pointer;
}

.dmr-status-button:not(:disabled):hover .dmr-status{
  box-shadow:0 0 0 3px rgba(246,139,44,.13);
  transform:translateY(-1px);
}

.dmr-status-button:disabled{
  cursor:default;
}

.dmr-open-button{
  min-height:31px;
  padding:0 9px;
  display:inline-flex;
  align-items:center;
  gap:5px;
  border:1px solid #E5E9ED;
  border-radius:7px;
  background:#FFFFFF;
  color:#69747F;
  font-size:9px;
}

.dmr-open-button:hover{
  border-color:#FFC18A;
  color:#C66C27;
  background:#FFF9F4;
}

.dmr-qr-button{
  min-height:31px;
  padding:0 9px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:5px;
  border:1px solid #F2D5BE;
  border-radius:7px;
  background:#FFF9F4;
  color:#C66C27;
  font-size:9px;
  font-weight:750;
  white-space:nowrap;
}

.dmr-qr-button:hover:not(:disabled){
  border-color:#E9AD7F;
  background:#FFF1E6;
}

.dmr-qr-button:disabled{
  cursor:not-allowed;
  opacity:.48;
}

.dmr-cargo-detail-button{
  min-height:31px;
  padding:0 7px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:4px;
  border:1px solid #D8E6F8;
  border-radius:7px;
  background:#F5F9FF;
  color:#3973CE;
  font-size:9px;
  font-weight:750;
  white-space:nowrap;
}

.dmr-cargo-detail-button:hover:not(:disabled){
  border-color:#AFC9EA;
  background:#EAF2FF;
}

.dmr-cargo-detail-button:disabled{
  cursor:not-allowed;
  opacity:.5;
}

.dmr-route-action-button{
  min-height:31px;
  padding:0 7px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:4px;
  border:1px solid #D8EBDD;
  border-radius:7px;
  background:#F4FBF6;
  color:#258456;
  font-size:9px;
  font-weight:750;
  white-space:nowrap;
}
.dmr-route-action-button:hover:not(:disabled){border-color:#A8D5B6;background:#EAF7EE}
.dmr-route-action-button:disabled{cursor:not-allowed;opacity:.5}
.dmr-row-actions.is-cargo{flex-wrap:wrap;white-space:normal}

.dmr-row-actions{
  display:flex;
  align-items:center;
  gap:5px;
  white-space:nowrap;
}

.dmr-row-edit-button,
.dmr-row-delete-button{
  min-height:31px;
  padding:0 8px;
  border-radius:7px;
  background:#FFFFFF;
  font-size:9px;
  white-space:nowrap;
}

.dmr-row-edit-button{
  border:1px solid #D8E5F5;
  color:#3973CE;
}

.dmr-row-edit-button:hover:not(:disabled){
  background:#F2F7FF;
  border-color:#AFC9EA;
}

.dmr-row-delete-button{
  border:1px solid #F0D4D4;
  color:#C95353;
}

.dmr-row-delete-button:hover:not(:disabled){
  background:#FFF3F3;
  border-color:#E8AAAA;
}

.dmr-row-edit-button:disabled,
.dmr-row-delete-button:disabled{
  cursor:not-allowed;
  opacity:.55;
}

.dmr-first-table th:last-child,
.dmr-first-table td:last-child{
  min-width:220px;
}

.dmr-loading,
.dmr-empty{
  min-height:330px;
  padding:40px 20px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  color:#929CA5;
  text-align:center;
}

.dmr-loading p{
  margin-top:12px;
  font-size:11px;
}

.dmr-spinner{
  width:22px;
  height:22px;
  border:2px solid #E7EAED;
  border-top-color:#F18731;
  border-radius:50%;
  animation:dmrSpin .75s linear infinite;
}

@keyframes dmrSpin{
  to{
    transform:rotate(360deg);
  }
}

.dmr-empty-icon{
  width:52px;
  height:52px;
  display:grid;
  place-items:center;
  margin-bottom:14px;
  border-radius:14px;
  background:#F2F4F6;
  color:#7B8791;
}

.dmr-empty strong{
  color:#4C5864;
  font-size:13px;
}

.dmr-empty p{
  max-width:320px;
  margin-top:7px;
  color:#9AA3AB;
  font-size:10px;
  line-height:1.7;
}

.dmr-create-layer{
  position:fixed;
  inset:0;
  z-index:10000;
  padding:28px;
  display:flex;
  justify-content:flex-end;
  background:rgba(17,24,39,.30);
  backdrop-filter:blur(2px);
}

.dmr-create-modal{
  width:min(780px,100%);
  height:100%;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  border-radius:17px;
  background:#FFFFFF;
  box-shadow:0 25px 80px rgba(15,23,42,.22);
  animation:dmrModalIn .18s ease-out;
}

.dmr-create-header{
  min-height:88px;
  padding:20px 22px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:15px;
  border-bottom:1px solid var(--line);
}

.dmr-create-header small{
  color:#929BA5;
  font-size:9px;
  letter-spacing:1.8px;
}

.dmr-create-header h2{
  margin-top:5px;
  color:#1F2A34;
  font-size:19px;
}

.dmr-create-header p{
  margin-top:5px;
  color:var(--muted);
  font-size:11px;
}

.dmr-create-body{
  flex:1;
  overflow:auto;
  padding:22px;
}

.dmr-create-grid{
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:14px;
}

.dmr-create-field{
  min-width:0;
  display:grid;
  gap:6px;
}

.dmr-create-field-wide{
  grid-column:1 / -1;
}

.dmr-create-field span{
  color:#5E6974;
  font-size:11px;
  font-weight:650;
}

.dmr-create-field span b{
  margin-left:3px;
  color:#D94B4B;
}

.dmr-create-field input,
.dmr-create-field select,
.dmr-create-field textarea{
  width:100%;
  box-sizing:border-box;
  min-height:40px;
  padding:9px 10px;
  border:1px solid #E1E5E9;
  border-radius:8px;
  outline:none;
  background:#FFFFFF;
  color:#24313C;
  font-size:12px;
}

.dmr-create-field textarea{
  resize:vertical;
}

.dmr-create-field input:focus,
.dmr-create-field select:focus,
.dmr-create-field textarea:focus{
  border-color:#9CB6D9;
  box-shadow:0 0 0 3px rgba(50,118,232,.10);
}

.dmr-create-footer{
  min-height:70px;
  padding:14px 22px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  border-top:1px solid var(--line);
}

.dmr-create-footer>span{
  color:#8A949D;
  font-size:10px;
}

.dmr-create-footer>div{
  display:flex;
  gap:9px;
}

.dmr-create-error{
  position:fixed;
  right:34px;
  bottom:28px;
  z-index:10001;
  max-width:420px;
  padding:12px 14px;
  display:flex;
  align-items:center;
  gap:8px;
  border:1px solid #F1CCCC;
  border-radius:10px;
  background:#FFF1F1;
  color:#B64242;
  font-size:11px;
  box-shadow:0 12px 32px rgba(15,23,42,.14);
}

.dmr-modal-layer{
  position:fixed;
  inset:0;
  z-index:9999;
  padding:28px;
  display:flex;
  justify-content:flex-end;
  background:rgba(17,24,39,.30);
  backdrop-filter:blur(2px);
}

.dmr-cargo-detail-layer{
  position:fixed;
  inset:0;
  z-index:10000;
  padding:20px;
  display:flex;
  justify-content:flex-end;
  background:rgba(17,24,39,.30);
  backdrop-filter:blur(2px);
}

.dmr-cargo-detail-drawer{
  width:min(620px,100%);
  height:100%;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  border:1px solid rgba(226,231,235,.9);
  border-radius:18px;
  background:#FFFFFF;
  box-shadow:0 25px 80px rgba(15,23,42,.22);
  animation:dmrModalIn .18s ease-out;
}

.dmr-cargo-detail-header{
  flex:0 0 auto;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  padding:20px 22px;
  border-bottom:1px solid #E9EDF1;
  background:#FFFFFF;
}

.dmr-cargo-detail-heading{
  min-width:0;
  display:flex;
  align-items:center;
  gap:13px;
}

.dmr-cargo-detail-icon{
  width:42px;
  height:42px;
  flex:0 0 auto;
  display:grid;
  place-items:center;
  border-radius:13px;
  background:#FFF2E7;
  color:#E77B27;
}

.dmr-cargo-detail-heading>div{min-width:0}
.dmr-cargo-detail-heading small{display:block;color:#89939D;font-size:10px;font-weight:750}
.dmr-cargo-detail-heading h2{overflow:hidden;margin:3px 0 2px;color:#263541;font-size:20px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
.dmr-cargo-detail-heading p{margin:0;color:#77838D;font-size:11px}

.dmr-cargo-detail-body{
  min-height:0;
  flex:1 1 auto;
  overflow:auto;
  overscroll-behavior:contain;
  padding:18px 22px 28px;
  background:#FFFFFF;
}

.dmr-cargo-route-summary{
  margin-bottom:14px;
  padding:16px;
  border:1px solid #E8EDF1;
  border-radius:13px;
  background:#FFFFFF;
}
.dmr-cargo-route-summary-head{display:flex;align-items:center;justify-content:space-between;gap:12px}
.dmr-cargo-route-summary-head strong{min-width:0;overflow:hidden;color:#263541;font-size:15px;font-weight:850;text-overflow:ellipsis;white-space:nowrap}
.dmr-cargo-route-export{flex:0 0 auto;min-height:30px;padding:0 11px;border:1px solid #F2C49F;border-radius:8px;background:#FFF8F2;color:#C7651D;font-size:11px;font-weight:850;cursor:pointer;transition:background .15s,border-color .15s}
.dmr-cargo-route-export:hover{border-color:#E77B27;background:#FFF0E3}
.dmr-cargo-route-summary-head span{color:#6D7B84;font-size:11px;font-weight:750;white-space:nowrap}
.dmr-cargo-route-summary>p{margin:7px 0 0;color:#6F7D86;font-size:12px;line-height:1.5}
.dmr-cargo-route-times{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:9px;margin-top:14px;padding-top:13px;border-top:1px solid #EEF1F4}
.dmr-cargo-route-times span{display:grid;gap:5px;min-width:0}
.dmr-cargo-route-times small{color:#8A969E;font-size:10px;font-weight:700}
.dmr-cargo-route-times strong{color:#344955;font-size:11px;line-height:1.4;overflow-wrap:anywhere}
.dmr-cargo-route-map-panel{width:100%;min-width:0}
.dmr-cargo-route-map{width:100%;height:min(58vh,560px);min-height:360px;overflow:hidden;border:1px solid #E5EAEE;border-radius:14px;background:#F4F7F8}
.dmr-cargo-route-map .leaflet-container{width:100%;height:100%;z-index:0;background:#F4F7F8}
.dmr-cargo-route-layer{padding:12px;overflow:hidden}
.dmr-cargo-route-drawer{min-height:0}
.dmr-cargo-route-drawer .dmr-cargo-detail-header{padding:12px 18px}
.dmr-cargo-route-body{display:flex;min-height:0;flex:1 1 auto;flex-direction:column;gap:8px;overflow:hidden!important;padding:12px 14px;background:#FFFFFF}
.dmr-cargo-route-body .dmr-cargo-route-summary{flex:0 0 auto;margin:0;padding:11px 14px}
.dmr-cargo-route-body .dmr-cargo-route-summary>p{margin-top:5px}
.dmr-cargo-route-body .dmr-cargo-route-times{margin-top:9px;padding-top:9px}
.dmr-cargo-route-body .dmr-cargo-route-map-panel{display:flex;min-height:0;flex:1 1 0;flex-direction:column}
.dmr-cargo-route-body .dmr-cargo-route-map{height:auto;min-height:180px;flex:1 1 0}
.dmr-cargo-route-body .dmr-cargo-route-playback{flex:0 0 auto;margin-top:7px;padding:8px 11px 6px}
.dmr-cargo-route-body .dmr-cargo-route-note{flex:0 0 auto;overflow:hidden;margin:0;line-height:14px;text-overflow:ellipsis;white-space:nowrap}
.dmr-cargo-route-map .leaflet-top.leaflet-left{top:12px;left:12px;z-index:800}
.dmr-cargo-route-map .leaflet-control-zoom{margin:0;overflow:hidden;border:1px solid rgba(255,255,255,.95);border-radius:11px;background:rgba(255,255,255,.94);box-shadow:0 5px 16px rgba(20,35,50,.2);backdrop-filter:blur(7px)}
.dmr-cargo-route-map .leaflet-control-zoom a{width:34px;height:34px;border:0!important;border-bottom:1px solid #E8EDF2!important;background:transparent;color:#34495B;font-size:20px;font-weight:700;line-height:33px;transition:background .15s,color .15s}
.dmr-cargo-route-map .leaflet-control-zoom a:last-child{border-bottom:0!important}
.dmr-cargo-route-map .leaflet-control-zoom a:hover{background:#FFF2E7;color:#D86D1E}
.dmr-cargo-route-playback{margin-top:10px;padding:11px 13px 8px;border:1px solid #E5EAEE;border-radius:13px;background:#FFFFFF;box-shadow:0 4px 12px rgba(25,45,65,.05)}
.dmr-cargo-route-playback-controls{display:flex;align-items:center;gap:7px;flex-wrap:wrap}
.dmr-cargo-route-playback-controls button{height:32px;padding:0 10px;border:1px solid #DCE4EC;border-radius:8px;background:#FFFFFF;color:#536575;font-size:11px;font-weight:800;cursor:pointer}
.dmr-cargo-route-playback-controls button:disabled{cursor:not-allowed;opacity:.45}
.dmr-cargo-route-playback-controls .dmr-cargo-route-play{min-width:56px;border-color:#E77B27;background:#E77B27;color:#FFFFFF}
.dmr-cargo-route-playback-controls .dmr-cargo-route-seek{color:#3973CE}
.dmr-cargo-route-speed{display:flex;align-items:center;gap:6px;margin-left:auto;color:#83909A;font-size:10px;font-weight:700}
.dmr-cargo-route-speed select{height:32px;padding:0 8px;border:1px solid #DCE4EC;border-radius:8px;background:#FFFFFF;color:#425466;font-size:11px;font-weight:800}
.dmr-cargo-route-playback-times{display:grid;grid-template-columns:minmax(0,1fr) auto minmax(0,1fr);align-items:center;gap:8px;margin-top:11px;color:#8A969E;font-size:10px;font-variant-numeric:tabular-nums}
.dmr-cargo-route-playback-times span{display:flex;align-items:center;gap:5px;min-width:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.dmr-cargo-route-playback-times span:last-child{justify-self:end}
.dmr-cargo-route-playback-times strong{color:#344955;font-size:11px;font-weight:900;white-space:nowrap}
.dmr-cargo-route-playback-times i{flex:0 0 7px;width:7px;height:7px;border-radius:50%;background:#2563EB}
.dmr-cargo-route-playback-times i.is-end{background:#EF4444}
.dmr-cargo-route-range{display:block;width:100%;height:5px;margin:9px 0 2px;border:0;border-radius:99px;appearance:none;background:linear-gradient(90deg,#2563EB 0%,#EF4444 100%);cursor:pointer}
.dmr-cargo-route-range::-webkit-slider-thumb{width:14px;height:14px;border:2px solid #FFFFFF;border-radius:50%;appearance:none;background:#263B58;box-shadow:0 1px 5px rgba(15,23,42,.3)}
.dmr-cargo-route-range::-moz-range-thumb{width:11px;height:11px;border:2px solid #FFFFFF;border-radius:50%;background:#263B58;box-shadow:0 1px 5px rgba(15,23,42,.3)}
.dmr-cargo-route-range:disabled{cursor:not-allowed;opacity:.45}
.dmr-cargo-route-note{margin:9px 2px 0;color:#8A969E;font-size:10px;line-height:1.6}
.dmr-cargo-route-empty{min-height:190px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:9px;padding:24px;border:1px dashed #DCE4E8;border-radius:14px;background:#F8FAFB;color:#85939B;text-align:center}
.dmr-cargo-route-empty strong{color:#50636F;font-size:13px}
.dmr-cargo-route-empty span{max-width:340px;font-size:11px;line-height:1.6}

.dmr-cargo-detail-loading{
  min-height:220px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:12px;
  color:#87919B;
  font-size:12px;
}

.dmr-cargo-detail-loading p{margin:0}
.dmr-cargo-detail-error,.dmr-cargo-detail-warning{display:flex;align-items:flex-start;gap:9px;padding:13px 15px;border:1px solid #F1CCCC;border-radius:12px;background:#FFF1F1;color:#B64242;font-size:12px;line-height:1.6}
.dmr-cargo-detail-warning{margin:12px 0;border-color:#F1D7B9;background:#FFF8EF;color:#A5672A}

.dmr-cargo-state-card{
  position:relative;
  margin-bottom:14px;
  padding:17px 18px;
  border:1px solid #E6EBF0;
  border-radius:15px;
  background:#FFFFFF;
  box-shadow:0 3px 12px rgba(34,49,63,.035);
}

.dmr-cargo-state-overview{display:flex;align-items:center;justify-content:space-between;gap:12px}
.dmr-cargo-state-overview>div{display:grid;gap:5px}
.dmr-cargo-state-card small{color:#84909A;font-size:10px;font-weight:700}
.dmr-cargo-state-text{font-size:21px;font-weight:850}
.dmr-cargo-state-blue{color:#3276E8}
.dmr-cargo-state-green{color:#248B57}
.dmr-cargo-state-red{color:#C94B4B}
.dmr-cargo-state-gray{color:#78838D}
.dmr-cargo-exception-state-pill{padding:6px 11px;border-radius:999px;background:#FFF0F0;color:#C94B4B;font-size:10px;font-weight:800}
.dmr-cargo-flow{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));margin:20px 0 4px}
.dmr-cargo-flow-step{position:relative;z-index:0;min-width:0;display:flex;flex-direction:column;align-items:center;gap:8px;text-align:center}
.dmr-cargo-flow-step:not(:first-child)::before{position:absolute;z-index:-1;top:13px;right:50%;width:100%;height:3px;border-radius:99px;background:#E3E8ED;content:""}
.dmr-cargo-flow-step.is-line-complete:not(:first-child)::before{background:#62B989}
.dmr-cargo-flow-node{width:28px;height:28px;display:grid;place-items:center;border:2px solid #D6DDE4;border-radius:50%;background:#FFFFFF;color:#8D98A2;font-size:10px;font-weight:800}
.dmr-cargo-flow-label{max-width:100%;color:#929CA5;font-size:10px;font-weight:700;line-height:1.3;white-space:nowrap}
.dmr-cargo-flow-step.is-done .dmr-cargo-flow-node{border-color:#39A66B;background:#39A66B;color:#FFFFFF}
.dmr-cargo-flow-step.is-done .dmr-cargo-flow-label{color:#328B5B}
.dmr-cargo-flow-step.is-current .dmr-cargo-flow-node{border-color:#4381E8;background:#EDF4FF;color:#3276E8;box-shadow:0 0 0 4px rgba(50,118,232,.09)}
.dmr-cargo-flow-step.is-current .dmr-cargo-flow-label{color:#3276E8}
.dmr-cargo-flow-step.is-current.is-exception .dmr-cargo-flow-node{border-color:#D25454;background:#FFF0F0;color:#C94B4B;box-shadow:0 0 0 4px rgba(201,75,75,.08)}
.dmr-cargo-flow-step.is-current.is-exception .dmr-cargo-flow-label{color:#C94B4B}
.dmr-cargo-flow-step.is-skipped .dmr-cargo-flow-node{border-style:dashed;background:#FAFBFC;color:#A1AAB2}
.dmr-cargo-flow-step.is-skipped .dmr-cargo-flow-label{color:#A1AAB2}
.dmr-cargo-state-times{display:flex;flex-wrap:wrap;gap:8px 16px;margin-top:17px;padding-top:11px;border-top:1px solid #EEF1F4;color:#6F7B85;font-size:10px}

.dmr-cargo-detail-section{
  margin-bottom:13px;
  padding:16px 17px;
  border:1px solid #E6EBF0;
  border-radius:15px;
  background:#FFFFFF;
}

.dmr-cargo-section-title{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px;color:#35424D;font-size:12px;font-weight:850}
.dmr-cargo-section-title small{color:#919BA4;font-size:9px;font-weight:650}
.dmr-cargo-job-title,.dmr-cargo-contact-name{display:block;color:#293742;font-size:14px;font-weight:800}
.dmr-cargo-context-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:13px}
.dmr-cargo-context-grid>div{min-width:0;display:grid;gap:4px}
.dmr-cargo-context-grid small{color:#929CA5;font-size:9px}
.dmr-cargo-context-grid strong{overflow-wrap:anywhere;color:#4D5B66;font-size:11px;font-weight:700;line-height:1.5}
.dmr-cargo-context-address{grid-column:1/-1}
.dmr-cargo-contact-company,.dmr-cargo-contact-phone{display:block;margin-top:5px;color:#74808A;font-size:11px}
.dmr-cargo-contact-phone{width:max-content;color:#3276E8;text-decoration:none}
.dmr-cargo-contact-phone:hover{text-decoration:underline}
.dmr-cargo-signature-section>.dmr-detail-section{margin:0;padding:0;border:0;background:transparent}
.dmr-cargo-signature-section>.dmr-cargo-section-title{flex-direction:column;justify-content:center;gap:4px;margin-bottom:11px;text-align:center}
.dmr-cargo-signature-section>.dmr-cargo-section-title>span{font-size:14px}
.dmr-cargo-signature-section>.dmr-cargo-section-title>small{text-align:center}
.dmr-cargo-signature-section>.dmr-cargo-signature-media .dmr-photo-grid{display:flex;justify-content:center;width:100%}
.dmr-cargo-detail-body .dmr-photo-grid>button{background:#FFFFFF}
.dmr-cargo-detail-body .dmr-photo-grid img{background:#FFFFFF}
.dmr-cargo-detail-body .dmr-media-placeholder{background:#FFFFFF}
.dmr-cargo-signature-section>.dmr-cargo-signature-media .dmr-photo-grid>button{width:min(480px,100%);max-width:100%;aspect-ratio:3/1;border:0;border-radius:14px;background:#FFFFFF}
.dmr-cargo-signature-section>.dmr-cargo-signature-media .dmr-photo-grid img{object-fit:contain;padding:10px 18px;background:#FFFFFF}
.dmr-cargo-signature-section>.dmr-cargo-proof-empty{width:100%}
.dmr-cargo-signature-time{display:block;margin-top:9px;color:#87919B;font-size:10px;text-align:center}
.dmr-cargo-proof-empty{padding:13px;border:1px dashed #DDE3E9;border-radius:10px;background:#FAFBFC;color:#929CA5;font-size:11px;text-align:center}
.dmr-cargo-exception{margin-top:10px;padding:12px;border:1px solid #F0D5D5;border-radius:11px;background:#FFFAFA}
.dmr-cargo-exception-meta{display:flex;align-items:center;justify-content:space-between;gap:10px;color:#87919B;font-size:10px}
.dmr-cargo-exception-meta strong{color:#64717B;font-size:10px}
.dmr-cargo-exception-meta span{padding:3px 7px;border-radius:999px;background:#FFF0F0;color:#C94B4B;font-size:9px;font-weight:800}
.dmr-cargo-exception p{margin:9px 0 0;color:#495761;font-size:11px;line-height:1.65;white-space:pre-wrap}
.dmr-cargo-number-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:9px 2px;color:#8C969F;font-size:9px}
.dmr-cargo-number-row strong{color:#697680;font-size:10px;font-weight:700;overflow-wrap:anywhere;text-align:right}
.dmr-cargo-detail-section>.dmr-cargo-number-row{margin-top:12px;padding-top:12px;border-top:1px solid #EEF1F4}

.dmr-modal{
  width:min(660px,100%);
  height:100%;
  display:flex;
  flex-direction:column;
  overflow:hidden;
  border-radius:17px;
  background:#FFFFFF;
  box-shadow:0 25px 80px rgba(15,23,42,.22);
  animation:dmrModalIn .18s ease-out;
}

.dmr-import-modal{
  width:min(560px,100%);
  height:auto;
  max-height:min(610px,100%);
}

.dmr-export-modal{
  width:min(560px,100%);
  height:auto;
  max-height:min(610px,100%);
}

.dmr-action-modal-layer{
  align-items:center;
  justify-content:center;
  padding:20px;
}

.dmr-action-modal-layer .dmr-modal{
  width:min(620px,100%);
  border:1px solid rgba(226,231,235,.9);
  box-shadow:0 22px 70px rgba(15,23,42,.18);
}

.dmr-action-modal-layer .dmr-modal.dmr-cargo-qr-modal{
  width:min(440px,100%);
  height:auto;
  max-height:calc(100vh - 40px);
  border-radius:17px;
}

.dmr-cargo-qr-body{
  flex:0 1 auto;
  display:grid;
  justify-items:center;
  gap:13px;
  padding:18px 22px 20px;
}

.dmr-cargo-qr-heading{
  display:grid;
  justify-items:center;
  gap:4px;
  text-align:center;
}

.dmr-cargo-qr-heading strong{
  color:#25313D;
  font-size:15px;
  font-weight:800;
}

.dmr-cargo-qr-heading span{
  color:#87919B;
  font-size:11px;
}

.dmr-cargo-qr-preview{
  width:min(280px,68vw);
  aspect-ratio:1;
  display:grid;
  place-items:center;
  overflow:hidden;
  padding:12px;
  border:1px solid #E9EDF1;
  border-radius:14px;
  background:#FFFFFF;
}

.dmr-cargo-qr-preview img{
  width:100%;
  height:100%;
  object-fit:contain;
  image-rendering:pixelated;
}

.dmr-cargo-qr-error{
  max-width:220px;
  color:#8A949E;
  font-size:12px;
  line-height:1.6;
  text-align:center;
}

.dmr-cargo-qr-code{
  width:100%;
  display:grid;
  gap:6px;
  padding:12px 14px;
  border:1px solid #E9EDF1;
  border-radius:10px;
  background:#F8FAFC;
}

.dmr-cargo-qr-code span{
  color:#87919B;
  font-size:10px;
  font-weight:700;
}

.dmr-cargo-qr-code code{
  color:#25313D;
  font-size:13px;
  font-weight:800;
  overflow-wrap:anywhere;
  user-select:all;
}

.dmr-cargo-qr-hint{
  margin:0;
  color:#87919B;
  font-size:10px;
  line-height:1.5;
  text-align:center;
}

.dmr-cargo-qr-footer{
  min-height:64px;
  justify-content:flex-end;
  padding:10px 18px;
}

.dmr-cargo-qr-footer>div{
  display:flex;
  justify-content:flex-end;
}

.dmr-import-modal .dmr-modal-body,
.dmr-edit-modal .dmr-modal-body,
.dmr-export-modal .dmr-modal-body{
  flex:0 1 auto;
  padding:20px 22px;
}

.dmr-import-modal .dmr-modal-header,
.dmr-edit-modal .dmr-modal-header,
.dmr-export-modal .dmr-modal-header{
  min-height:72px;
}

.dmr-import-grid{
  display:grid;
  grid-template-columns:repeat(2,minmax(0,1fr));
  gap:12px 14px;
}

.dmr-action-modal-layer .dmr-modal-footer{
  min-height:70px;
  padding:12px 22px;
  justify-content:flex-end;
}

.dmr-action-modal-layer .dmr-modal-footer>span{
  min-width:0;
  flex:1;
  color:#7A8690;
  font-size:10px;
  line-height:1.5;
}

.dmr-action-modal-layer .dmr-modal-footer>div{
  flex:0 0 auto;
  display:flex;
  gap:8px;
}

.dmr-import-file-name{
  grid-column:1 / -1;
  margin:0;
  padding:10px 12px;
  border-radius:8px;
  background:#F7F9FB;
  color:#66727D;
  font-size:11px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.dmr-import-error{
  grid-column:1 / -1;
  display:flex;
  align-items:center;
  gap:8px;
  padding:10px 12px;
  border:1px solid #F1CCCC;
  border-radius:8px;
  background:#FFF1F1;
  color:#B64242;
  font-size:11px;
}

.dmr-export-note{
  margin-bottom:16px;
  padding:11px 12px;
  border:1px solid #F1E1D3;
  border-radius:9px;
  background:#FFF9F4;
  color:#8B6F5B;
  font-size:11px;
  line-height:1.6;
}

.dmr-import-note{
  margin-bottom:16px;
  padding:11px 12px;
  border:1px solid #D9E7F7;
  border-radius:9px;
  background:#F5F9FF;
  color:#5D718A;
  font-size:11px;
  line-height:1.6;
}

.dmr-import-file-picker{
  min-height:52px;
  padding:7px;
  display:flex;
  align-items:center;
  gap:10px;
  border:1px dashed #C9D7E6;
  border-radius:10px;
  background:#F8FBFF;
  cursor:pointer;
}

.dmr-import-file-picker input{
  position:absolute;
  width:1px;
  height:1px;
  opacity:0;
  pointer-events:none;
}

.dmr-import-file-trigger{
  flex:0 0 auto;
  min-height:34px;
  padding:0 12px;
  display:inline-flex;
  align-items:center;
  border-radius:7px;
  background:#EAF2FF;
  color:#3475D8;
  font-size:11px;
  font-weight:700;
}

.dmr-import-file-label{
  min-width:0;
  overflow:hidden;
  color:#7C8792;
  font-size:11px;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.dmr-edit-summary{
  display:grid;
  gap:5px;
  margin-bottom:16px;
  padding:13px 14px;
  border:1px solid #F1E1D3;
  border-radius:10px;
  background:#FFF9F4;
}

.dmr-edit-summary span{
  color:#C2763B;
  font-size:10px;
}

.dmr-edit-summary strong{
  color:#4A5661;
  font-size:13px;
}

@keyframes dmrModalIn{
  from{
    transform:translateX(20px);
    opacity:.5;
  }

  to{
    transform:translateX(0);
    opacity:1;
  }
}

.dmr-modal-header{
  min-height:82px;
  padding:18px 20px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:15px;
  border-bottom:1px solid var(--line);
}

.dmr-modal-heading{
  min-width:0;
  display:flex;
  align-items:center;
  gap:12px;
}

.dmr-modal-icon{
  width:42px;
  height:42px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  border-radius:11px;
}

.dmr-modal-heading small{
  display:block;
  margin-bottom:5px;
  color:#929BA5;
  font-size:9px;
}

.dmr-modal-heading h2{
  color:#1F2A34;
  font-size:17px;
  font-weight:680;
}

.dmr-icon-button{
  width:37px;
  height:37px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  border:1px solid #E8EBEE;
  border-radius:9px;
  background:#FFFFFF;
  color:#7B858E;
}

.dmr-modal-body{
  flex:1;
  overflow:auto;
  padding:20px;
}

.dmr-report-meta-card{
  display:grid;
  grid-template-columns:
    repeat(4,minmax(0,1fr));
  border:1px solid #E9ECEF;
  border-radius:12px;
  background:#FAFBFC;
  overflow:hidden;
}

.dmr-report-meta-card>div{
  min-width:0;
  padding:14px;
  border-right:1px solid #E9ECEF;
}

.dmr-report-meta-card>div:last-child{
  border-right:0;
}

.dmr-report-meta-card>div>span{
  display:block;
  color:#969FA8;
  font-size:9px;
}

.dmr-report-meta-card>div>strong{
  display:block;
  margin-top:6px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#4B5661;
  font-size:10px;
  font-weight:650;
}

.dmr-detail-section{
  margin-top:24px;
}

.dmr-detail-section h3{
  margin-bottom:11px;
  display:flex;
  align-items:center;
  gap:8px;
  color:#3D4853;
  font-size:12px;
  font-weight:680;
}

.dmr-detail-section h3>span{
  color:#A0A7AE;
  font-size:9px;
  font-weight:500;
}

.dmr-detail-grid{
  display:grid;
  grid-template-columns:
    repeat(2,minmax(0,1fr));
  gap:9px;
}

.dmr-detail-item{
  padding:12px;
  border:1px solid #ECEFF2;
  border-radius:9px;
  background:#FBFCFD;
}

.dmr-detail-full{
  grid-column:1/-1;
}

.dmr-detail-item span{
  display:block;
  color:#969FA8;
  font-size:9px;
}

.dmr-detail-item strong{
  display:block;
  margin-top:6px;
  color:#4B5661;
  font-size:10px;
  line-height:1.6;
  font-weight:620;
  overflow-wrap:anywhere;
}

.dmr-note-box{
  padding:14px 15px;
  border:1px solid #ECEFF2;
  border-radius:10px;
  background:#FAFBFC;
  color:#626D77;
  font-size:11px;
  line-height:1.8;
  white-space:pre-wrap;
}

.dmr-incident-note{
  border-color:#F1D9D9;
  background:#FFF8F8;
}

.dmr-inspection-list{
  display:grid;
  gap:7px;
}

.dmr-inspection-row{
  min-height:53px;
  padding:10px 12px;
  display:flex;
  align-items:center;
  gap:10px;
  border:1px solid #E9EDF0;
  border-radius:9px;
  background:#FCFDFD;
}

.dmr-inspection-row.is-abnormal{
  border-color:#F0CECE;
  background:#FFF7F7;
}

.dmr-inspection-check{
  width:29px;
  height:29px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  border-radius:8px;
  background:#EAF6EF;
  color:#3B8362;
}

.dmr-inspection-row.is-abnormal .dmr-inspection-check{
  background:#FDEAEA;
  color:#D24B4B;
}

.dmr-inspection-row>div{
  flex:1;
  min-width:0;
}

.dmr-inspection-row>div>strong{
  display:block;
  color:#4A5661;
  font-size:10px;
  font-weight:650;
}

.dmr-inspection-row>div>small{
  display:block;
  margin-top:4px;
  color:#929BA4;
  font-size:9px;
}

.dmr-inspection-row>b{
  color:#468264;
  font-size:9px;
}

.dmr-inspection-row.is-abnormal>b{
  color:#C94949;
}

.dmr-emergency-banner{
  padding:16px;
  display:flex;
  gap:12px;
  border-radius:12px;
  background:linear-gradient(135deg,#3B2527,#261D20);
  color:#FFFFFF;
}

.dmr-emergency-banner>div{
  width:42px;
  height:42px;
  flex-shrink:0;
  display:grid;
  place-items:center;
  border-radius:11px;
  background:#D84949;
}

.dmr-emergency-banner section>span{
  display:block;
  color:#FFB6B6;
  font-size:9px;
  letter-spacing:.6px;
}

.dmr-emergency-banner section>strong{
  display:block;
  margin-top:5px;
  font-size:15px;
  font-weight:680;
}

.dmr-emergency-banner section>p{
  margin-top:5px;
  color:#D7C8CA;
  font-size:9px;
}

.dmr-photo-grid{
  display:grid;
  grid-template-columns:repeat(4,1fr);
  gap:8px;
}

.dmr-photo-grid>button{
  aspect-ratio:1;
  padding:0;
  position:relative;
  overflow:hidden;
  border:0;
  border-radius:10px;
  background:#E9ECEF;
}

.dmr-photo-grid img{
  width:100%;
  height:100%;
  display:block;
  object-fit:cover;
}

.dmr-photo-grid>button>span{
  position:absolute;
  right:6px;
  bottom:6px;
  width:26px;
  height:26px;
  display:grid;
  place-items:center;
  border-radius:8px;
  background:rgba(16,24,32,.72);
  color:#FFFFFF;
}

.dmr-media-viewer{position:fixed;inset:0;z-index:1200;display:grid;place-items:center;padding:24px;background:rgba(18,34,47,.74)}.dmr-media-viewer-card{width:min(900px,96vw);height:min(88vh,900px);display:flex;flex-direction:column;overflow:hidden;border-radius:14px;background:#fff;box-shadow:0 20px 70px rgba(0,0,0,.35)}.dmr-media-viewer-head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:1px solid #e6edf2;color:#294257}.dmr-media-viewer-head strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.dmr-media-viewer-body{flex:1;display:grid;place-items:center;min-height:0;padding:14px;background:#f5f7f8}.dmr-media-viewer-body img{max-width:100%;max-height:100%;object-fit:contain}.dmr-media-viewer-body iframe{width:100%;height:100%;border:0;background:#fff}.dmr-media-viewer-foot{display:flex;align-items:center;gap:8px;padding:9px 16px;color:#6f818c;font-size:10px;border-top:1px solid #e6edf2}.dmr-media-viewer-foot span{display:inline-flex;padding:2px 5px;border-radius:4px;background:#eaf8ef;color:#18845a;font-size:8px;font-weight:900;letter-spacing:.35px}
.dmr-media-loading{margin:0 0 10px;color:var(--muted);font-size:12px}.dmr-media-placeholder{display:grid;place-items:center;width:100%;height:100%;min-height:120px;background:#f4f7f9;color:var(--muted);font-size:12px}.dmr-photo-grid button:disabled,.dmr-evidence-file-row:disabled{cursor:wait;opacity:.72}
.dmr-evidence-file-list{
  display:grid;
  gap:8px;
}

.dmr-evidence-file-row{
  width:100%;
  min-height:58px;
  padding:9px 10px;
  display:flex;
  align-items:center;
  gap:10px;
  border:1px solid var(--line);
  border-radius:10px;
  background:#FBFCFD;
  color:var(--ink);
  text-align:left;
  cursor:pointer;
}

.dmr-evidence-file-row:hover{
  border-color:#F97316;
  background:#FFF9F5;
}

.dmr-evidence-file-icon{
  width:34px;
  height:34px;
  display:grid;
  place-items:center;
  flex:0 0 auto;
  border-radius:9px;
  background:#FFF0F0;
  color:#DC2626;
}

.dmr-evidence-file-copy{
  min-width:0;
  flex:1;
}

.dmr-evidence-file-copy strong,
.dmr-evidence-file-copy small{
  display:block;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}

.dmr-evidence-file-copy strong{font-size:12px;font-weight:750;}
.dmr-evidence-file-copy small{margin-top:3px;color:var(--muted);font-size:10px;}

.dmr-modal-footer{
  min-height:77px;
  padding:14px 18px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
  border-top:1px solid var(--line);
  background:#FFFFFF;
}

.dmr-modal-footer>div:first-child{
  min-width:0;
}

.dmr-modal-footer>div:first-child small{
  display:block;
  color:#9CA4AC;
  font-size:8px;
}

.dmr-modal-footer>div:first-child strong{
  display:block;
  max-width:190px;
  margin-top:4px;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  color:#6C7781;
  font-size:9px;
  font-weight:550;
}

.dmr-modal-actions{
  display:flex;
  align-items:center;
  gap:8px;
}

.dmr-secondary-action,
.dmr-primary-action{
  min-height:40px;
  padding:0 14px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  border-radius:8px;
  font-size:10px;
  font-weight:650;
}

.dmr-secondary-action{
  border:1px solid #DFE4E8;
  background:#FFFFFF;
  color:#66727C;
}

.dmr-primary-action{
  border:0;
  background:#18232D;
  color:#FFFFFF;
}

.dmr-primary-action:hover{
  background:#26333E;
}

@media(max-width:1300px){
  .dmr-stats{
    grid-template-columns:
      repeat(3,minmax(0,1fr));
  }
}

@media(max-width:980px){
  .dmr-root{
    padding:20px;
  }

  .dmr-main-card{
    display:block;
  }

  .dmr-page-header{
    align-items:flex-start;
  }

  .dmr-stats{
    grid-template-columns:
      repeat(2,minmax(0,1fr));
  }

  .dmr-report-catalog-grid{
    grid-template-columns:repeat(3,minmax(0,1fr));
    overflow-x:hidden;
  }

  .dmr-report-catalog-heading{
    align-items:flex-start;
    flex-direction:column;
  }

  .dmr-toolbar{
    align-items:stretch;
    flex-direction:column;
    border-left:0;
  }

  .dmr-search{
    width:100%;
  }

  .dmr-filters{
    width:100%;
    flex-wrap:wrap;
  }

  .dmr-filters label{
    flex:1;
  }

  .dmr-filters select{
    width:100%;
  }

  .dmr-expense-summary{
    grid-template-columns:1fr;
    gap:16px;
  }

  .dmr-report-meta-card{
    grid-template-columns:
      repeat(2,minmax(0,1fr));
  }

  .dmr-report-meta-card>div:nth-child(2){
    border-right:0;
  }

  .dmr-report-meta-card>div:nth-child(-n+2){
    border-bottom:1px solid #E9ECEF;
  }
}

.dmr-media-viewer-card{width:min(960px,calc(100vw - 48px));height:min(86vh,calc(100vh - 48px));min-height:360px}.dmr-media-viewer-body{overflow:hidden}.dmr-media-viewer-body img{display:block;width:auto;height:auto;max-width:100%;max-height:100%;min-width:0;min-height:0}.dmr-media-viewer-body iframe{display:block;min-width:0;min-height:0}@media(max-width:680px){.dmr-media-viewer{padding:12px}.dmr-media-viewer-card{width:calc(100vw - 24px);height:calc(100vh - 24px);min-height:0;border-radius:12px}.dmr-media-viewer-head{padding:11px 13px}.dmr-media-viewer-body{padding:10px}.dmr-media-viewer-foot{padding:8px 13px}}
@media(max-width:680px){
  .dmr-root{
    padding:15px;
  }

  .dmr-page-header{
    flex-direction:column;
  }

  .dmr-page-header h1{
    font-size:25px;
  }

  .dmr-header-actions,
  .dmr-export-button{
    width:100%;
  }

  .dmr-create-button{
    flex:1;
  }

  .dmr-export-button{
    justify-content:center;
  }

  .dmr-date-navigator{
    flex-wrap:wrap;
  }

  .dmr-date-actions{
    width:100%;
    margin-left:0;
  }

  .dmr-date-actions .dmr-export-button,
  .dmr-date-actions .dmr-create-button{
    width:auto;
    flex:1;
  }

  .dmr-stats{
    grid-template-columns:1fr 1fr;
    gap:8px;
  }

  .dmr-report-catalog{
    padding:0 0 10px;
  }

  .dmr-report-catalog-grid{
    grid-template-columns:repeat(3,minmax(0,1fr));
    overflow-x:hidden;
  }

  .dmr-stat-card{
    min-height:118px;
    padding:14px;
    flex-direction:column;
    gap:10px;
  }

  .dmr-stat-copy strong{
    font-size:20px;
  }

  .dmr-filters{
    flex-direction:column;
  }

  .dmr-expense-summary{
    margin:12px;
    padding:15px;
  }

  .dmr-expense-bar-row{
    grid-template-columns:
      62px minmax(60px,1fr) 82px;
  }

  .dmr-modal-layer{
    padding:0;
  }

  .dmr-create-layer{
    padding:0;
  }

  .dmr-create-modal{
    width:100%;
    height:100%;
    border-radius:0;
  }

  .dmr-create-grid{
    grid-template-columns:1fr;
  }

  .dmr-import-grid{
    grid-template-columns:1fr;
  }

  .dmr-create-field-wide{
    grid-column:auto;
  }

  .dmr-create-footer{
    align-items:stretch;
    flex-direction:column;
  }

  .dmr-create-footer>div{
    width:100%;
  }

  .dmr-create-footer button{
    flex:1;
  }

  .dmr-modal{
    width:100%;
    height:100%;
    border-radius:0;
  }

  .dmr-action-modal-layer{
    padding:12px;
  }

  .dmr-action-modal-layer .dmr-modal{
    width:100%;
    height:auto;
    max-height:calc(100vh - 24px);
    border-radius:16px;
  }

  .dmr-modal-body{
    padding:16px;
  }

  .dmr-detail-grid{
    grid-template-columns:1fr;
  }

  .dmr-detail-full{
    grid-column:auto;
  }

  .dmr-photo-grid{
    grid-template-columns:
      repeat(3,1fr);
  }

  .dmr-modal-footer{
    align-items:stretch;
    flex-direction:column;
  }

  .dmr-modal-footer>div:first-child{
    display:none;
  }

  .dmr-modal-actions{
    width:100%;
  }

  .dmr-modal-actions button{
    flex:1;
  }

  .dmr-action-modal-layer .dmr-modal-footer{
    align-items:stretch;
    flex-direction:column;
  }

  .dmr-action-modal-layer .dmr-modal-footer>span{
    display:block;
  }

  .dmr-action-modal-layer .dmr-modal-footer>div{
    width:100%;
  }

  .dmr-action-modal-layer .dmr-modal-footer button{
    flex:1;
  }
}

@media(prefers-reduced-motion:reduce){
  .dmr-spinner{
    animation:none;
  }

  .dmr-update-button{
    animation:none;
  }

  .dmr-modal{
    animation:none;
  }
}


/* Desktop workspace boundary: keep the page shell fixed and give the report table the scroll owner. */
@media(min-width:981px){
  .dmr-root{
    width:calc(100% + 44px);
    height:100vh;
    max-height:none;
    min-height:0;
    margin:-22px;
    padding:0;
    display:flex;
    flex-direction:column;
    overflow:hidden;
  }
  .dmr-date-navigator,
  .dmr-stats,
  .dmr-report-catalog{
    flex:0 0 auto;
  }
  .dmr-main-card{
    width:100%;
    height:100%;
    min-height:0;
    flex:1 1 auto;
    grid-template-rows:auto minmax(0,1fr);
    overflow:hidden;
  }
  .dmr-first-table-wrap{
    min-height:0;
    height:100%;
    overflow:auto;
    overscroll-behavior:contain;
    scrollbar-width:thin;
    scrollbar-color:#AEB8C2 transparent;
  }
}


/* Report center header stays above the date toolbar and aligns with the content boundary. */
@media(min-width:981px){
  .dmr-page-header{
    width:100%;
    max-width:none;
    min-height:48px;
    margin:0 0 14px;
    padding:0 2px;
    align-items:flex-end;
    flex:0 0 auto;
  }
  .dmr-page-header h1{
    font-size:28px;
    letter-spacing:-.8px;
  }
}


/* Report center visual density: compact header, full-width controls, and readable table rows. */
@media(min-width:981px){
  .dmr-root{background:#F5F7FB}
  .dmr-page-header{padding:0 2px 2px}
  .dmr-page-header .dmr-eyebrow{display:none}
  .dmr-page-header h1{font-size:27px;font-weight:800;letter-spacing:-.9px}
  .dmr-date-navigator{min-height:68px;margin-bottom:14px;padding:12px 16px;border-radius:16px;box-shadow:0 4px 16px rgba(34,49,63,.04)}
  .dmr-date-navigator label{min-width:190px}
  .dmr-date-navigator input{font-size:16px}
  .dmr-report-catalog{margin-bottom:14px;padding-bottom:8px;border:0}
  .dmr-report-catalog-grid{gap:8px;padding:0}
  .dmr-report-catalog-card{min-height:44px;border-radius:13px;box-shadow:0 2px 8px rgba(34,49,63,.03)}
  .dmr-main-card{border-radius:16px;box-shadow:0 8px 24px rgba(34,49,63,.05)}
  .dmr-list-heading{min-height:60px;padding:0 18px;background:#FFFFFF}
  .dmr-toolbar{min-height:60px;padding:9px 16px;background:#FFFFFF}
  .dmr-first-table-wrap{border-top:1px solid #E9EDF1}
  .dmr-first-table th{height:44px;padding:0 12px;background:#F8FAFC;font-size:11px}
  .dmr-first-table td{height:46px;padding:12px;font-size:12px}
  .dmr-first-table th:first-child,.dmr-first-table td:first-child{padding-left:18px}
  .dmr-first-table th:last-child,.dmr-first-table td:last-child{padding-right:18px}
  .dmr-open-button,.dmr-row-edit-button,.dmr-row-delete-button{min-height:28px;padding:0 8px}
}


/* Header rows follow the page boundary: full width and left aligned, not centered cards. */
@media(min-width:981px){
  .dmr-root{align-items:stretch}
  .dmr-page-header,
  .dmr-date-navigator,
  .dmr-report-catalog,
  .dmr-stats,
  .dmr-main-card{
    width:100%;
    max-width:none;
    margin-left:0;
    margin-right:0;
  }
  .dmr-page-header{justify-content:flex-start}
  .dmr-date-navigator{justify-content:flex-start}
  .dmr-date-actions{margin-left:auto}
  .dmr-report-catalog-grid{width:100%;justify-content:stretch}
}
@media(max-width:680px){
  .dmr-cargo-detail-layer{padding:0}
  .dmr-cargo-detail-drawer{width:100%;border:0;border-radius:0}
  .dmr-cargo-detail-header{padding:15px 16px}
  .dmr-cargo-detail-body{padding:14px 14px 24px}
  .dmr-cargo-route-layer{padding:0}
  .dmr-cargo-route-drawer{width:100%;height:100%;border:0;border-radius:0}
  .dmr-cargo-route-drawer .dmr-cargo-detail-header{padding:11px 14px}
  .dmr-cargo-route-body{gap:6px;padding:8px 10px 9px}
  .dmr-cargo-route-body .dmr-cargo-route-summary{padding:9px 11px}
  .dmr-cargo-route-body .dmr-cargo-route-times{grid-template-columns:repeat(3,minmax(0,1fr));gap:5px;margin-top:7px;padding-top:7px}
  .dmr-cargo-route-body .dmr-cargo-route-times strong{font-size:10px}
  .dmr-cargo-route-body .dmr-cargo-route-map{min-height:140px}
  .dmr-cargo-route-body .dmr-cargo-route-playback{margin-top:5px;padding:7px 8px 5px}
  .dmr-cargo-route-body .dmr-cargo-route-playback-times{gap:4px;margin-top:7px;font-size:9px}
  .dmr-cargo-route-body .dmr-cargo-route-playback-times strong{font-size:10px}
  .dmr-cargo-route-body .dmr-cargo-route-playback-controls{gap:5px}
  .dmr-cargo-route-body .dmr-cargo-route-playback-controls button{height:29px;padding:0 7px;font-size:10px}
  .dmr-cargo-route-body .dmr-cargo-route-speed{gap:4px;font-size:9px}
  .dmr-cargo-route-body .dmr-cargo-route-speed select{height:29px;padding:0 5px;font-size:10px}
  .dmr-cargo-route-body .dmr-cargo-route-note{font-size:9px}
  .dmr-cargo-detail-heading h2{font-size:18px}
  .dmr-cargo-context-grid{gap:10px}
  .dmr-cargo-state-card{padding:15px 13px}
  .dmr-cargo-state-text{font-size:19px}
  .dmr-cargo-flow{margin:18px -3px 3px}
  .dmr-cargo-flow-node{width:26px;height:26px}
  .dmr-cargo-flow-step:not(:first-child)::before{top:12px}
  .dmr-cargo-flow-label{font-size:9px}
  .dmr-cargo-detail-button{min-height:28px;padding:0 6px;font-size:8px}
  .dmr-route-action-button{min-height:28px;padding:0 6px;font-size:8px}
  .dmr-cargo-route-map{height:58vh;min-height:280px}
  .dmr-cargo-route-times{grid-template-columns:1fr}
}
`;
